/**
 * Onboarding page - tier-aware self-serve onboarding funnel
 *
 * Phase 1 implementation:
 * - Persists onboarding session state in Firestore under users/{uid}/onboarding/session
 * - Hydrates from subscription + tenant membership
 * - Resumable state machine
 * - Clear next actions for Slack connect, config, docs, and live test
 *
 * This page intentionally ships before full Slack OAuth / upload tooling so the
 * funnel and state persistence exist first. Later phases plug into these steps.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SeoHead from "../components/SeoHead";
import styled, { keyframes } from 'styled-components';
import KeepCase from '../components/common/KeepCase';
import { PrimaryButton as SharedPrimaryButton, SecondaryButton as SharedSecondaryButton } from '../styles/CommonStyles';
import { getAuth, updateProfile } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp, onSnapshot, arrayUnion } from 'firebase/firestore';
import BrandWordmark from '../components/BrandWordmark';
import BrandedTransition from '../components/common/BrandedTransition';
import Spinner from '../components/common/Spinner';
import {
  DEFAULT_PRACTICE_OPTIONS,
  CAPABILITIES_BY_PRACTICE,
  UNIVERSAL_CAPABILITIES,
  getCapabilitiesForAreas,
  buildCapabilityUniverse,
  groupCapabilities,
  INDUSTRY_AREA_KEYWORDS,
} from '../data/onboardingOptions';
import { resolveEffectiveConfig, updateTenantConfig, updateTenantProfile, addKnowledgeDoc } from '../services/tenantService';
import { createInvitation, listRoles } from '../services/rbacService';
import { clearWorkspaceOptionsCache } from '../services/workspaceOptionsCache';
import { beginWorkspaceTransition, subscribeWorkspaceTransition } from '../services/workspaceTransition';
import { reserveAndWarmDraftOrgVm, getReservedDraftOrgId, clearReservedDraftOrgId } from '../lib/warmWorkspace';
import { IntegrationsPanel } from '../services/composio';
import {
  getConnectedAccounts, TOOLKIT_CATEGORIES, listToolkits,
  listCustomConnections, updateCustomConnectionIdentity,
  familyForToolkit, sortByLikelihood, MORE_APPS_FAMILY,
  // Storage scope sheet (2026-08-11, PLAN-storage-folder-permissions §7.1).
  isStorageToolkit, setStorageScope, browseStorageFolders,
  // Access tier (2026-08-12 owner directive): the post-connect sheet covers
  // EVERY app — full access (default) / read + write / read-only.
  // 2026-08-19: the sheet is now the SHARED AppAccessSheet (governance plan
  // §6, fourth surface) — presets + fine-tune profile + share-with-org row.
  setAccessTier, setAccessProfile, shareConnection,
  profileFromTier, tierFromProfile,
  CAPABILITY_CLASS_SUMMARIES,
  // OAuth popup handshake (task #52): the callback page renders a minimal
  // "connected" landing instead of the full Integrations page in the popup.
  markConnectPopupLaunch,
  type ComposioToolkit, type StorageScopeFolder, type AccessTier, type AccessProfile,
} from '../services/composio/ComposioClient';
import AppAccessSheet from '../services/composio/AppAccessSheet';
import IntegrationIcon from '../services/composio/IntegrationIcon';
import FolderBrowser from '../services/composio/FolderBrowser';
import RoleDetailPanel from '../components/TeamRoles/RoleDetailPanel';
// Shared connect-popup engine (2026-08-18): sync-open + mint + navigate —
// the same mechanics the Integrations page and the in-chat card use.
import { launchConnectPopup, watchPopupClosed } from '../services/composio/connectPopupFlow';
import { peekConnectLane, laneIsDirect, laneCtaLabel, laneExplainer, routeToBrowserLane } from '../services/composio/connectLane';
// THE match key for "is this the same app?" (connectApp.ts) — a catalog row is
// `ouraring`, its browser-lane connection is `custom-oura_ring`, and the user
// typed "Oura Ring". One normalizer, reused rather than re-derived.
import {
  buildBrowserLaneRoster,
  isAppConnectedInEitherLane,
  browserLaneEntryFor,
  type BrowserLaneRoster,
} from '../services/composio/browserLaneRoster';
import IdentityPrompt from '../services/composio/panel/IdentityPrompt';
import { prettyIntegrationName } from '../services/composio/integrationMeta';
import { rankedIndustryApps } from '../data/industryRecommendedApps';
import Modal from '../components/common/Modal';
import ComposioConnectCoach from '../components/onboarding/ComposioConnectCoach';
import { storage } from '../utils/firebaseStorage';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { JourneyStepper, NEW_ACCOUNT_JOURNEY, WORKSTATION_BOOT_ESTIMATE } from '../components/onboarding/JourneyStepper';
import WorkstationReadinessHint from '../components/onboarding/WorkstationReadinessHint';
import { isTrialSubscription } from '../hooks/trialStatus';
import { needsTrialOnboarding } from '../hooks/trialOnboardingGate';
import { writeThroughProfile } from '../services/profileWriteThrough';
import { ensureOrganization, renameWorkspaceOrganization } from '../services/organizations';
import { isProtectedOrgName, PROTECTED_ORG_NAME_MESSAGE, safePrefillOrgName } from '../lib/orgNameRules';
import { fetchWorkspaceOptionsCached } from '../services/workspaceOptionsCache';
import { searchToolkits } from '../services/composio/appSearch';
// lazyWithRetry, not bare React.lazy (returning-tab reliability, 2026-09-01):
// these were the last two bare lazy calls in src/ — a stale tab opening the
// role editor or the terms link hit an unretried, unrecovered ChunkLoadError.
import { lazyWithRetry } from '../utils/lazyWithRetry';

// The EXACT role editor from members & roles, mounted in-flow (task #55) —
// lazy so the 800-line editor never weighs down the onboarding bundle.
const RoleEditorModalLazy = lazyWithRetry(() => import('../components/TeamRoles/RoleEditorModal'));
// Terms body is ~370 lines of legal JSX — loaded only when the confirm
// card's terms link is actually opened.
const TermsContentInner = lazyWithRetry(() => import('../components/legal/TermsContent'));
const TermsContentLazy: React.FC = () => (
  <React.Suspense fallback={<div style={{ padding: '1rem', color: '#9A948A', fontSize: '0.85rem' }}>loading the terms…</div>}>
    <TermsContentInner />
  </React.Suspense>
);

// ============================================================================
// Types
// ============================================================================

type PlanTier = 'starter' | 'professional' | 'enterprise' | 'trial';
type OnboardingState =
  | 'billing_pending'
  | 'billing_complete'
  | 'slack_connect_pending'
  | 'slack_connected'
  | 'config_pending'
  | 'docs_pending'
  | 'test_run_pending'
  | 'complete'
  | 'error_recoverable';

interface OnboardingSession {
  state: OnboardingState;
  tenantId: string | null;
  tier: PlanTier;
  steps: {
    billingComplete: boolean;
    slackConnected: boolean;
    configCompleted: boolean;
    docsCompleted: boolean;
    testRunCompleted: boolean;
  };
  profile?: {
    firmName?: string;
    practiceAreas?: string[];
    defaultLanguage?: string;
    brandTone?: string;
  };
  notes?: {
    lastCompletedStep?: string;
    lastError?: string | null;
  };
  /** Trial-flow resume point (2026-08-04 staged flow): which announced phase
   *  the user is in, persisted on every transition so a refresh mid-flow lands
   *  exactly where they left off — the flow is unbreakable by design. */
  trialPhase?: 'profile' | 'apps' | 'team' | 'orientation' | 'done';
  createdAt?: unknown;
  updatedAt?: unknown;
}

interface SubscriptionInfo {
  status: string;
  plan: PlanTier | 'trial';
  planAmountUsd: number;
  aiEnabled: boolean;
  tenantId: string | null;
  is_trial?: boolean;
  trial_tier?: string;
  trial_credit_cents?: number;
  trial_expires_at?: any;
}

// Work areas — general-purpose (any industry), legal included as one of many.
// Work-area / capability catalogs live in src/data/onboardingOptions.ts —
// SHARED with the recommendation prefetch (src/services/onboardingRecsPrefetch)
// so both callers send byte-identical option lists and the server cache key
// (profileHash) matches across surfaces.

// ── Firm-aware onboarding recommendations (2026-08-03) ──
// Server: functions/src/onboardingRecommendations.ts. Codes minted for a firm
// carry a firmProfile that redemption copies onto users/{uid}.trial; the
// endpoint turns it into pre-selections and tailored starter tasks. Codes
// without one get { mode: 'general' } instantly with no model call.
interface OnboardingRecs {
  mode: 'firm' | 'general';
  firmName?: string;
  /** Raw team-size text from the invite's firm profile (e.g. "6-10"). */
  firmTeamSize?: string;
  recommendedIntegrations: string[];
  /** Wider ranked set (≤10) for the catalog modal's "recommended for you"
   *  group (2026-08-19) — superset of recommendedIntegrations. Optional:
   *  pre-v8 caches don't carry it; the per-industry fallback covers those. */
  catalogRecommendations?: string[];
  recommendedWorkAreas: string[];
  recommendedCapabilities: string[];
  starterTasks: Array<{ title: string; prompt: string }>;
  welcomeNote?: string;
}

// Friendly names live in the SHARED map (src/services/composio/integrationMeta.ts,
// 2026-08-19) so onboarding, the chat connect tool, and future surfaces never
// drift on how an app is named in copy.
const prettyIntegration = prettyIntegrationName;

// Full one-click catalog for the "more apps" modal (task #43) — the SAME
// grouped catalog the integrations page renders, shown inside the guided
// flow so connecting extra accounts never means leaving onboarding.
// Category keys are title-case in the source module; lowercased at render
// to match the funnel voice.


// Industry-aware catalog ranking moved to src/data/industryRecommendedApps.ts
// (2026-08-19): one deterministic list per work-area chip (all 18), free-text
// rules for research-derived industries, never slack/teams. The model-ranked
// recs.catalogRecommendations wins over these when present.

/** Team-size chips — single source for the render AND the firm-profile prefill. */
const TEAM_SIZE_OPTIONS = ['Just me', '2–10 people', '11–50 people', '51–200 people', '201–500 people', '500+ people'];

/** Map a free-text firm-profile team size ("6-10", "solo", "about 40") onto a chip. */
function mapTeamSizeToOption(raw?: string): string | null {
  if (!raw) return null;
  const s = raw.toLowerCase();
  if (/just me|only me|solo/.test(s)) return TEAM_SIZE_OPTIONS[0];
  const nums = (s.match(/\d+/g) || []).map(Number).filter(Number.isFinite);
  if (nums.length === 0) return null;
  const max = Math.max(...nums);
  if (max <= 1) return TEAM_SIZE_OPTIONS[0];
  if (max <= 10) return TEAM_SIZE_OPTIONS[1];
  if (max <= 50) return TEAM_SIZE_OPTIONS[2];
  if (max <= 200) return TEAM_SIZE_OPTIONS[3];
  if (max <= 500) return TEAM_SIZE_OPTIONS[4];
  return TEAM_SIZE_OPTIONS[5];
}

/** The user's first-page starter prompts (chat chips + welcome bullets).
 *  Firm-aware codes get the model's four tailored starter tasks; otherwise
 *  derive from the capabilities chosen during onboarding so even the general
 *  flow lands on suggestions that match the user's stated work. */
/** Per-app starter prompts — chips that exercise what the account ACTUALLY
 *  connected during setup (2026-08-04 owner directive). Keyed by Composio
 *  toolkit slug. */
const APP_STARTERS: Array<[string, string]> = [
  ['gmail', 'Summarize my unread emails and draft replies to the important ones'],
  ['outlook', 'Summarize my unread emails and draft replies to the important ones'],
  ['googlecalendar', 'Look at my calendar this week and flag conflicts or prep I should do'],
  ['googledrive', 'Read the newest documents in my Drive and summarize what changed'],
  ['googlesheets', 'Turn my latest spreadsheet into a clean weekly report'],
  ['slack', 'Catch me up on my Slack channels and draft any replies I owe'],
  ['notion', 'Summarize what changed in my Notion workspace this week'],
];

/** "Connect my X" starter → the toolkit slug it pitches. Used to drop
 *  connect-suggestions for apps the user ALREADY connected during this very
 *  onboarding (2026-08-14 owner report: the welcome told a user with a green
 *  Gmail tile to "Connect my Gmail" — Preston must know what he has). */
const CONNECT_SUGGESTION_APP_HINTS: Array<[string, RegExp]> = [
  ['gmail', /\bgmail\b/i],
  ['outlook', /\boutlook\b/i],
  ['googledrive', /\bgoogle\s*drive\b/i],
  ['googlecalendar', /\bcalendar\b/i],
  ['googlesheets', /\bsheets\b/i],
  ['quickbooks', /\bquickbooks\b/i],
  ['slack', /\bslack\b/i],
  ['notion', /\bnotion\b/i],
  ['hubspot', /\bhubspot\b/i],
];

function isStaleConnectSuggestion(prompt: string, connectedSlugs: string[]): boolean {
  if (!/^\s*connect\b/i.test(prompt)) return false;
  return CONNECT_SUGGESTION_APP_HINTS.some(
    ([slug, re]) => connectedSlugs.includes(slug) && re.test(prompt),
  );
}

function deriveStarterPrompts(recs: OnboardingRecs | null, capabilities: string[]): string[] {
  // Up to six (2026-08-04 owner directive: more custom options at first
  // start). Model recs first, capability-derived prompts fill the rest.
  const fromRecs = (recs?.starterTasks || []).map(t => t.prompt).filter(Boolean).slice(0, 6);
  if (fromRecs.length >= 6) return fromRecs;
  const CAP_PROMPTS: Array<[RegExp, string]> = [
    [/deep research/i, 'Run deep research on the biggest recent development in my field'],
    [/document reading|multi-file/i, 'Read these files and summarize the key points and action items'],
    [/drafting/i, 'Draft a first version of the document I send most often'],
    [/bilingual|spanish/i, 'Translate this client message to Spanish'],
    [/email & calendar/i, 'Draft a follow-up email for my last meeting'],
    [/meeting notes/i, 'Turn these meeting notes into a summary with action items'],
    [/data organization|reporting/i, 'Organize this data into a clean report'],
  ];
  const out: string[] = [...fromRecs];
  for (const [re, prompt] of CAP_PROMPTS) {
    if (out.length >= 6) break;
    if (capabilities.some(c => re.test(c)) && !out.includes(prompt)) out.push(prompt);
  }
  return out;
}

/** First-message welcome for the workspace chat. Deliberately SHORT (2026-08-04
 *  owner copy-diet directive) and bullet-for-bullet identical to the starter
 *  chips (`starters`) so the bubble and the one-click chips can never drift. */
function buildPersonalizedWelcome(firmName: string, practiceAreas: string[], starters: string[], creditCents: number): string {
  const org = firmName || 'your team';
  const areas = practiceAreas.slice(0, 3).join(', ') || 'your work';
  const credit = creditCents ? ` Your $${(creditCents / 100).toFixed(0)} trial credit is loaded.` : '';
  return [
    `${org} — your workspace is ready. I'm Prest0n, set up for ${areas}.${credit}`,
    ``,
    ...starters.slice(0, 4).map(s => `• "${s}"`),
    ``,
    `Pick one, or just tell me what you need.`,
  ].join('\n');
}

// (The legacy STEP_ORDER table died with its funnel — 2026-08-19 uniformity
// directive. deriveState stays: session docs still persist the state field.)

// ============================================================================
// Helpers
// ============================================================================

function getProjectId(): string {
  return process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
}

function fnUrl(name: string): string {
  return `https://us-central1-${getProjectId()}.cloudfunctions.net/${name}`;
}

async function getAuthToken(): Promise<string | null> {
  const user = getAuth().currentUser;
  if (!user) return null;
  return user.getIdToken();
}

function emptySession(tier: PlanTier = 'starter', tenantId: string | null = null, billingComplete: boolean = false): OnboardingSession {
  return {
    state: billingComplete ? 'billing_complete' : 'billing_pending',
    tier,
    tenantId,
    steps: {
      billingComplete,
      slackConnected: false,
      configCompleted: false,
      docsCompleted: false,
      testRunCompleted: false,
    },
    profile: {
      firmName: '',
      practiceAreas: [],
      defaultLanguage: 'English',
      brandTone: '',
    },
    notes: {
      lastCompletedStep: 'billing_complete',
      lastError: null,
    },
  };
}

function deriveState(session: OnboardingSession): OnboardingState {
  if (!session.steps.billingComplete) return 'billing_pending';
  if (!session.steps.slackConnected) return 'slack_connect_pending';
  if (!session.steps.configCompleted) return 'config_pending';
  if (!session.steps.docsCompleted) return 'docs_pending';
  if (!session.steps.testRunCompleted) return 'test_run_pending';
  return 'complete';
}

function stateDescription(state: OnboardingState, tier: PlanTier): string {
  switch (state) {
    case 'billing_pending':
      return 'Choose a plan to get started. Once billing is confirmed you\'ll continue setup here.';
    case 'billing_complete':
      return 'Your subscription is confirmed. Next we\'ll connect your workspace and configure your team.';
    case 'slack_connect_pending':
      return 'Connect the tools your team already uses — we\'ll recommend the best ones for your work.';
    case 'slack_connected':
      return 'Tools connected. Next we\'ll set up your team profile.';
    case 'config_pending':
      return 'We have your tenant. Now we need your work areas, tone, and preferences.';
    case 'docs_pending':
      return 'Next we\'ll load your team context and reference documents.';
    case 'test_run_pending':
      return 'One final step: run a live tenant-scoped test to verify the whole system.';
    case 'complete':
      return 'Onboarding is complete. Your workspace is ready.';
    case 'error_recoverable':
      return 'Something interrupted setup, but this flow is resumable. Continue from the last saved step.';
    default:
      return 'Continue onboarding.';
  }
}

// ============================================================================
// Styled Components
// ============================================================================

const Page = styled.main`
  background: var(--brand-canvas);
  min-height: 100vh;
  padding: 2rem 1rem 4rem;

  @media (min-width: 768px) {
    padding: 2.5rem 1.5rem 5rem;
  }
`;

const Wrap = styled.div`
  max-width: 980px;
  margin: 0 auto;
  display: grid;
  gap: 1.5rem;

  @media (min-width: 1200px) {
    max-width: 1220px;
  }

  /* Use the desktop, don't center a phone column in it (2026-08-04). */
  @media (min-width: 1440px) {
    max-width: min(1440px, 92vw);
  }
`;

const Hero = styled.section`
  background: white;
  border: 1px solid #ECEAE5;
  border-radius: 1.25rem;
  padding: 1.5rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.04);
`;

const Eyebrow = styled.div`
  font-size: 0.72rem;
  text-transform: lowercase;
  letter-spacing: 0.08em;
  color: #9A948A;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: clamp(2rem, 3vw, 2.5rem);
  font-weight: 500;
  letter-spacing: -0.03em;
  color: var(--brand-text);
  margin-bottom: 0.75rem;
  /* Progressive enhancement — avoids one-word orphan lines. */
  text-wrap: balance;
`;

/* Rail-sized headline (2026-08-09 owner wrap pass): the hero-sized Title
   rendered a ~10–14ch measure inside the 320px stepper rail, orphaning
   single words onto their own line. */
const RailTitle = styled(Title)`
  font-size: clamp(1.35rem, 2vw, 1.6rem);
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  line-height: 1.65;
  color: #666;
  max-width: 760px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 960px) {
    grid-template-columns: 320px 1fr;
    align-items: start;
  }
`;

const Card = styled.section`
  background: white;
  border: 1px solid #ECEAE5;
  border-radius: 1.25rem;
  padding: 1.25rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.04);

  @media (min-width: 768px) {
    padding: 1.75rem;
  }
`;

/* Trial-branch desktop split (2026-08-03 desktop-polish audit): journey
   stepper + hero context in a sticky left rail, the action card right. The
   single 980px column read as a stretched phone layout on wide screens. */
const TrialGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 340px) minmax(0, 1fr);
    align-items: start;
  }

  @media (min-width: 1440px) {
    grid-template-columns: minmax(0, 380px) minmax(0, 1fr);
    gap: 2rem;
  }
`;

const TrialRail = styled.div`
  display: grid;
  gap: 1.5rem;
  align-content: start;

  @media (min-width: 1024px) {
    position: sticky;
    top: 1.5rem;
  }
`;

const CardTitle = styled.h2`
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--brand-text);
  margin-bottom: 1rem;
`;

const StepList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const StepRow = styled.div<{ $done?: boolean; $active?: boolean }>`
  border: 1px solid ${p => p.$active ? 'var(--brand-text)' : '#ECEAE5'};
  background: ${p => p.$done ? '#F7FFF8' : '#fff'};
  border-radius: 0.9rem;
  padding: 0.8rem 0.9rem;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const StepDot = styled.div<{ $done?: boolean; $active?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 999px;
  margin-top: 2px;
  background: ${p => p.$done ? '#27AE60' : p.$active ? 'var(--brand-text)' : '#E1DED8'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const StepLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--brand-text);
`;

const StepSub = styled.div`
  font-size: 0.78rem;
  color: #666;
  line-height: 1.5;
  margin-top: 0.2rem;
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  /* Wrap pass (2026-08-09): the credit + dates spans total wider than the
     rail — break cleanly at the separators instead of mid-phrase inside a
     999px lozenge (radius relaxes so a wrapped pill doesn't look broken). */
  flex-wrap: wrap;
  row-gap: 0.2rem;
  gap: 0.45rem;
  background: #F3F1EC;
  color: #444;
  border-radius: 16px;
  padding: 0.35rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 1rem;

  & > span {
    white-space: nowrap;
  }
`;

const SectionText = styled.p`
  font-size: 0.88rem;
  color: #666;
  line-height: 1.65;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 0.9rem;
  margin-top: 1rem;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.1rem 1.25rem;
  }
`;

/* Escape hatch for FormGrid children that genuinely need the full row at
   desktop (the chip clouds); the two short inputs pair up instead. */
const FullWidth = styled.div`
  @media (min-width: 1024px) {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  display: grid;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  border: 1px solid #DDD8CF;
  border-radius: 0.75rem;
  padding: 0.8rem 0.9rem;
  font-size: 0.9rem;
  font-family: inherit;
  background: #fff;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadList = styled.div`
  display: grid;
  gap: 0.6rem;
  margin-top: 1rem;
`;

const UploadItem = styled.div`
  border: 1px solid #ECEAE5;
  border-radius: 0.85rem;
  padding: 0.75rem 0.9rem;
  background: #FCFBF9;
  font-size: 0.82rem;
  color: #444;
  line-height: 1.5;
  /* Long unbroken filenames must break instead of overflowing the card. */
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const TestList = styled.div`
  display: grid;
  gap: 0.65rem;
  margin-top: 1rem;
`;

const TestItem = styled.div<{ $pass: boolean }>`
  border: 1px solid ${p => p.$pass ? '#B7E4C7' : '#F5C2C7'};
  background: ${p => p.$pass ? '#F1FFF5' : '#FFF6F6'};
  border-radius: 0.85rem;
  padding: 0.75rem 0.9rem;
`;

const Select = styled.select`
  border: 1px solid #DDD8CF;
  border-radius: 0.75rem;
  padding: 0.8rem 0.9rem;
  font-size: 0.9rem;
  font-family: inherit;
  background: #fff;
`;

const PracticeAreaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

/* Selection restyle (task #53, owner report 2026-08-12): the old selected
   state was a solid BLACK fill — heavy, "scary", and at a glance hard to
   tell apart from an unselected dark-on-white chip. Selected now reads in
   the brand gold with an explicit leading ✓; unselected stays plain white.
   The double border is drawn with an inset shadow so toggling never shifts
   layout by a pixel. */
const Chip = styled.button<{ $selected?: boolean }>`
  border: 1px solid ${p => p.$selected ? 'var(--brand-gold)' : '#DDD8CF'};
  box-shadow: ${p => p.$selected ? 'inset 0 0 0 1px var(--brand-gold)' : 'none'};
  background: ${p => p.$selected ? 'rgba(198, 167, 94, 0.14)' : '#fff'};
  color: ${p => p.$selected ? 'var(--brand-text)' : '#555'};
  font-weight: ${p => p.$selected ? 700 : 500};
  /* Wrap pass (2026-08-09): labels run up to ~55 chars (server contract —
     never shorten the copy) and sometimes wrap; a wrapped 999px lozenge
     looks broken, so relax the radius and keep wrapped lines tidy. */
  border-radius: 16px;
  padding: 0.45rem 0.75rem;
  font-size: 0.78rem;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  line-height: 1.3;
  text-wrap: balance;
  /* Official lowercase style — display-only; stored values stay canonical. */
  text-transform: lowercase;

  &::before {
    content: ${p => (p.$selected ? "'✓'" : 'none')};
    margin-right: 0.35rem;
    color: var(--brand-gold-hover);
    font-weight: 800;
  }
`;

/* Capability GROUP bubble (task #53): one decision per THEME instead of one
   per capability. The member capabilities render inside as muted examples —
   the idea-generation intent survives, the decision count drops. */
const GroupBubble = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  width: 100%;
  text-align: left;
  border: 1px solid ${p => p.$selected ? 'var(--brand-gold)' : '#DDD8CF'};
  box-shadow: ${p => p.$selected ? 'inset 0 0 0 1px var(--brand-gold)' : 'none'};
  background: ${p => p.$selected ? 'rgba(198, 167, 94, 0.12)' : '#fff'};
  border-radius: 0.9rem;
  padding: 0.7rem 0.85rem;
  font-family: inherit;
  cursor: pointer;
  text-transform: lowercase;
`;

const GroupCheck = styled.span<{ $selected?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  flex-shrink: 0;
  margin-top: 0.05rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 800;
  border: ${p => p.$selected ? 'none' : '1.5px solid #D5CFC4'};
  background: ${p => p.$selected ? 'var(--brand-gold)' : '#fff'};
  color: #fff;
`;

const GroupLabel = styled.span<{ $selected?: boolean }>`
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${p => p.$selected ? 'var(--brand-text)' : '#444'};
  line-height: 1.3;
`;

const GroupExamples = styled.span`
  display: block;
  font-size: 0.72rem;
  color: #9A948A;
  line-height: 1.45;
  margin-top: 0.15rem;
`;

/* Storage scope sheet (2026-08-11, PLAN-storage-folder-permissions §7.1):
   the in-flow "what should prest0n use?" card that slides in under the
   connect rows when a storage app lands. Same visual family as the "up
   next" ask card — a card within the phase, never an overlay or a route. */
const scopeSheetSlideIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ScopeSheetCard = styled.div`
  border: 1px solid #E6E0D6;
  border-radius: 0.9rem;
  background: #FBFAF7;
  padding: 0.85rem 1rem;
  margin: 0 0 1.1rem;
  display: grid;
  gap: 0.7rem;
  animation: ${scopeSheetSlideIn} 0.25s ease-out;
`;

/* Minimal focused-flow top bar: the global header (and all its escape
   hatches) is hidden on /onboarding, so the page carries its own wordmark
   and a single quiet exit. */
const TopBar = styled.div`
  max-width: 980px;
  margin: 0 auto 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0.25rem 0;

  /* Track Wrap's desktop width so the wordmark stays flush with content. */
  @media (min-width: 1200px) {
    max-width: 1120px;
  }
`;


const TopBarSignOut = styled.button`
  background: none;
  border: none;
  font-family: inherit;
  font-size: 0.78rem;
  color: #9A948A;
  cursor: pointer;
  text-transform: lowercase;
  padding: 0.35rem 0.4rem;

  &:hover { color: #555; text-decoration: underline; }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1.1rem;
`;

const PrimaryButton = styled(SharedPrimaryButton)`
  border-radius: 0.85rem;
  padding: 0.85rem 1.5rem;
  font-size: 0.88rem;
  font-weight: 600;
  width: auto;
  /* Wrap pass (2026-08-09): "save & continue →" must never orphan the
     trailing arrow onto its own line (StartTrial BigCodeButton precedent).
     Scoped here — the shared base Button stays wrap-friendly app-wide. */
  white-space: nowrap;
  /* Task #53 (owner report 2026-08-12): the black pill CTAs read as
     intimidating to first-time users. Onboarding's primary actions use the
     brand gold — same palette as the wordmark, coach panel, and email CTA.
     Scoped here; the shared base button is unchanged app-wide. */
  background: var(--brand-gold);
  border-color: var(--brand-gold);
  color: #fff;

  &:hover:not(:disabled) {
    background: var(--brand-gold-hover);
    border-color: var(--brand-gold-hover);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(SharedSecondaryButton)`
  border-radius: 0.85rem;
  padding: 0.85rem 1.5rem;
  font-size: 0.88rem;
  font-weight: 600;
  width: auto;
  white-space: nowrap;
`;

const Callout = styled.div`
  background: #F6F4EF;
  border: 1px solid #E6E0D6;
  border-radius: 0.95rem;
  padding: 0.9rem 1rem;
  font-size: 0.82rem;
  color: #5E584E;
  line-height: 1.6;
  margin-top: 1rem;
`;

const ErrorText = styled.div`
  margin-top: 0.85rem;
  color: #B42318;
  font-size: 0.82rem;
`;

const SuccessText = styled.div`
  margin-top: 0.85rem;
  color: #067647;
  font-size: 0.82rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
`;

const ModalBox = styled.div`
  background: var(--brand-canvas);
  border-radius: 1.5rem;
  padding: 2rem;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18);

  @media (min-width: 900px) {
    max-width: 880px;
    padding: 2.5rem;
  }
`;

const ModalEyebrow = styled.div`
  font-size: 0.7rem;
  text-transform: lowercase;
  letter-spacing: 0.09em;
  font-weight: 600;
  color: #AAA;
  margin-bottom: 0.6rem;
`;

const ModalTitle = styled.h2`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: -0.025em;
  color: var(--brand-text);
  margin-bottom: 0.5rem;
`;

const ModalSub = styled.p`
  font-size: 0.85rem;
  color: #888;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const InlineCode = styled.code`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  background: #F3F1EC;
  padding: 0.1rem 0.35rem;
  border-radius: 6px;
  font-size: 0.78rem;
  /* Unbounded tokens (tenant ids) must break instead of overflowing. */
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const RecommendedApps = styled.div`
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  background: #F9F8F5;
  border: 1px solid #ECEAE5;
  border-radius: 0.95rem;
`;

const RecLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: lowercase;
  letter-spacing: 0.06em;
  color: #9A948A;
  margin-bottom: 0.6rem;
`;

const RecGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const RecChip = styled.span<{ $connected?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid ${p => p.$connected ? '#27AE60' : '#DDD8CF'};
  background: ${p => p.$connected ? '#F1FFF5' : '#fff'};
  color: ${p => p.$connected ? '#1B7A3D' : '#444'};
  border-radius: 999px;
  padding: 0.4rem 0.7rem;
  font-size: 0.78rem;
  font-weight: 500;
`;

// ============================================================================
// Component
// ============================================================================

const Onboarding: React.FC = () => {
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [session, setSession] = useState<OnboardingSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [effectiveTenantId, setEffectiveTenantId] = useState<string | null>(null);
  const [connectedApps, setConnectedApps] = useState<string[]>([]);

  const [firmName, setFirmName] = useState('');
  // Unified onboarding captures the user's REAL full name (owner directive
  // 2026-08-25): the standard signup has no name field and fabricates one
  // from the email prefix — so this field is the identity source of record.
  // Prefilled only from Firebase auth displayName (real for Google sign-ins,
  // empty for email/password); persisted to auth + users/{uid} at save. The
  // first name also names the personal workspace ("[first name]'s Workspace").
  const [fullName, setFullName] = useState('');
  const fullNamePrefilledRef = useRef(false);
  useEffect(() => {
    if (fullNamePrefilledRef.current) return;
    const unsub = getAuth().onAuthStateChanged((u) => {
      if (fullNamePrefilledRef.current || !u || u.isAnonymous) return;
      const dn = (u.displayName || '').trim();
      if (dn) {
        fullNamePrefilledRef.current = true;
        setFullName((prev) => prev || dn);
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Same-owner duplicate-name guard (owner report 2026-08-25: two identical
  // "Acme Inc." rows in the switcher): the names of workspaces this user
  // already owns, normalized, so a new draft can't take one of them.
  const [ownedWorkspaceNames, setOwnedWorkspaceNames] = useState<Set<string>>(new Set());
  useEffect(() => {
    let alive = true;
    const u = getAuth().currentUser;
    void fetchWorkspaceOptionsCached(u?.uid ?? null).then((options) => {
      if (!alive) return;
      const names = new Set<string>();
      for (const o of options) {
        if (o.isOwner && o.displayName) names.add(o.displayName.trim().replace(/\s+/g, ' ').toLowerCase());
      }
      setOwnedWorkspaceNames(names);
    }).catch(() => { /* guard degrades to server-side wall */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isDuplicateWorkspaceName = useCallback((name: string) => {
    const key = name.trim().replace(/\s+/g, ' ').toLowerCase();
    return Boolean(key) && ownedWorkspaceNames.has(key);
  }, [ownedWorkspaceNames]);
  const [brandTone, setBrandTone] = useState('Professional and direct');
  const [defaultLanguage, setDefaultLanguage] = useState('English');
  const [practiceAreas, setPracticeAreas] = useState<string[]>([]);

  // Docs upload state
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; url: string; docId?: string }>>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Live test state
  const [testRunning, setTestRunning] = useState(false);
  const [testResults, setTestResults] = useState<Array<{ label: string; pass: boolean; detail?: string }>>([]);

  // Trial-specific state
  const [firmSize, setFirmSize] = useState<string>('');
  // Contact phone (Phase 0, 2026-08-15): optional; write-throughs to the
  // account profile. NOT the provisioned Prest0n number.
  const [contactPhone, setContactPhone] = useState('');
  const [selectedCapabilities, setSelectedCapabilities] = useState<string[]>([...UNIVERSAL_CAPABILITIES]);
  const dynamicCapabilities = useMemo(() => getCapabilitiesForAreas(practiceAreas), [practiceAreas]);
  const capabilitiesRef = useRef<HTMLDivElement>(null);

  // Capability GROUPS (task #53): the page toggles whole themes; the stored
  // model stays the individual capability strings (recs pre-select those, so
  // a group lights up when ANY member is selected; toggling makes it
  // all-or-none from then on).
  const capabilityGroups = useMemo(() => groupCapabilities(dynamicCapabilities), [dynamicCapabilities]);
  const isGroupSelected = (members: string[]) =>
    members.some((m) => selectedCapabilities.includes(m));
  const toggleCapabilityGroup = (members: string[]) => {
    areasFromInstantRef.current = false; // user took ownership of the chips
    setSelectedCapabilities(prev => {
      if (members.some((m) => prev.includes(m))) {
        return prev.filter((c) => !members.includes(c));
      }
      const merged = new Set(prev);
      members.forEach((m) => merged.add(m));
      return Array.from(merged);
    });
  };

  // Auto-add practice-specific capabilities when a new area is selected,
  // and gently scroll the capabilities section into view on first selection.
  const togglePracticeAreaWithCaps = (area: string) => {
    areasFromInstantRef.current = false; // user took ownership of the chips
    setPracticeAreas(prev => {
      const next = prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area];
      const isFirstSelection = prev.length === 0 && !prev.includes(area);
      // Auto-add the new area's capabilities to selection
      if (!prev.includes(area)) {
        const areaCaps = CAPABILITIES_BY_PRACTICE[area] || [];
        setSelectedCapabilities(caps => {
          const capSet = new Set(caps);
          areaCaps.forEach(c => capSet.add(c));
          return Array.from(capSet);
        });
        // Scroll capabilities section into view on first practice area selection
        if (isFirstSelection) {
          setTimeout(() => {
            capabilitiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }, 100);
        }
      }
      return next;
    });
  };
  const [trialSaving, setTrialSaving] = useState(false);

  // Firm-aware recommendations (fetched once per mount; server caches per
  // firm-profile hash, so this is instant on resume).
  const [recs, setRecs] = useState<OnboardingRecs | null>(null);
  const recsFetchedRef = useRef(false);
  // True once the recommendations fetch has FAILED or timed out — without
  // this, the "personalizing your setup…" hint persisted forever on a
  // failed fetch (2026-08-09 owner clarity pass).
  const [recsUnavailable, setRecsUnavailable] = useState(false);
  const recsAppliedRef = useRef(false);
  // INSTANT identity layer (2026-08-04 owner feedback: "a little delay on
  // this page to choose the recommended items"): the invite's firm profile
  // already sits on users/{uid}.trial.firmProfile (owner-readable) — one
  // Firestore read applies the firm name, team size, and header the moment
  // the page mounts, so only the chip pre-selection waits on the model (and
  // says so, instead of silently popping in).
  const [instantFirmProfile, setInstantFirmProfile] = useState<Record<string, string> | null>(null);
  const instantAppliedRef = useRef(false);
  // True while the work-area chips hold the keyword-derived INSTANT picks —
  // model recommendations may replace those; user toggles clear the flag and
  // then always win.
  const areasFromInstantRef = useRef(false);
  // True while the company-name field holds the tenant-provisioning
  // PLACEHOLDER ("<name> Law" / "<x>'s Firm" from ensureTenantProvisioned) —
  // never typed or saved by the user. The invite's real firm name may
  // overwrite a placeholder; it must never overwrite user input. (2026-08-04
  // owner repro ×2: "miles444 Law", "lovemiles Law" — both placeholders that
  // blocked the firm-profile prefill.)
  const firmNamePlaceholderRef = useRef(false);

  // ── New-organization mode (task #75, 2026-08-18 owner directive) ──
  // '/onboarding?neworg=1': the EXACT same staged flow, but it sets up a NEW
  // organization that does not exist yet. Everything persists to the DRAFT
  // doc (users/{uid}/onboarding/pending_org) — never the task-56 session the
  // hard gate reads — and completion mints the org server-side
  // (createOrganizationWorkspace). No VM warm-up, no trial provisioning, no
  // writes to org #1's identity, welcome, or memory.
  const [newOrgMode] = useState<boolean>(() => {
    try { return new URLSearchParams(window.location.search).get('neworg') === '1'; } catch { return false; }
  });
  // ── First-signup draft mode (2026-08-19 personal/org split) ──
  // Under ops/nativeWorkspace.lazyEnabled, signup mints NOTHING — the
  // startOrganicTrial callable answers 'lazy-mode' and this page becomes the
  // decision point: the SAME staged flow, persisted to the pending_org draft
  // (like ?neworg=1), with the profile card's company/just-me fork choosing
  // the workspace that actually gets minted at launch — a co_ company org
  // with the uniform trial, or the user's uid-keyed personal workspace via
  // activatePersonalWorkspace. Server-steered (no client flag reads) and
  // persisted on the draft (firstSignup:true) so gate-redirected returns
  // resume the right mode without URL params.
  const [firstSignupMode, setFirstSignupMode] = useState(false);
  // Ref mirror for [] -deps callbacks (the recs fetch marks pendingOrg so
  // first-signup company research lands where the mint's welcome reads it).
  const firstSignupModeRef = useRef(false);
  useEffect(() => { firstSignupModeRef.current = firstSignupMode; }, [firstSignupMode]);
  // What the first-signup launch mints: chosen on the profile card.
  const [draftTargetKind, setDraftTargetKind] = useState<'company' | 'personal'>('company');
  // Draft-persisted modes: everything writes to pending_org, never the
  // task-56 session; no uid-account writes until launch decides the target.
  const draftMode = newOrgMode || firstSignupMode;
  const onboardingDocId = draftMode ? 'pending_org' : 'session';
  // A creator who still owes their OWN onboarding is refused at ENTRY — never
  // after minutes of draft work (task #75 review findings 4/14/17).
  const [newOrgBlocked, setNewOrgBlocked] = useState(false);

  // ── Early mint + eager VM (2026-08-19 owner directive: one organization
  // means at least one VM, ready by the END of onboarding — for EVERY
  // posture) ──
  // The mint is idempotent + reservation-first server-side, so firing it at
  // profile save and again at completion is safe; the VM then has the whole
  // apps/team stretch to boot from the golden image. The draft is stamped
  // earlyMinted so the resume/reset gates can tell "minted mid-onboarding,
  // resume me" from "consumed by a finished creation" (launchCompleted).
  const [mintedOrgAccountId, setMintedOrgAccountId] = useState<string | null>(null);
  // Reserved-but-unminted org id (2026-08-23 warm-from-arrival): the org VM
  // provisions against this id before the legal-name confirm; the connect
  // lane targets it so browser sign-ins land on the ORG's workstation.
  const [draftReservedOrgId, setDraftReservedOrgId] = useState<string | null>(() => getReservedDraftOrgId());
  /**
   * Which workstation a browser-lane sign-in must run on (review 2026-08-23).
   * ONLY a MINTED org: the client resolves the VM doc through
   * accounts/{id}/vm/main, whose read rule requires membership — which
   * bootstrapFounderRbac writes at mint. Targeting a merely RESERVED id would
   * hit permission-denied and dead-end the sign-in ("could not reach the
   * workstation browser") in exactly the pre-mint window this feature is for.
   * It also keeps the "just me" fork honest: a personal draft never mints a
   * company org, so it correctly falls back to ambient resolution instead of
   * pointing at an abandoned reservation. The reservation still does its real
   * job — warming the VM server-side, where the Admin SDK needs no rules.
   */
  /** The org the invite-code REDEMPTION created (session mode), captured
   *  from redeemAccessCode's response / the self-heal below. The 2026-08-23
   *  minted-only rule above still holds for DRAFT reservations (no member
   *  doc yet -> vm/main reads denied) — but the code lane writes the active
   *  isOwner member doc AT redemption, so its org is client-readable from
   *  the first second and is a valid sign-in target long before the
   *  completion-time back-pointer switch (2026-08-27 dead-end RCA). */
  const [redemptionOrgAccountId, setRedemptionOrgAccountId] = useState<string | null>(null);
  const signInWorkspaceTarget = mintedOrgAccountId ?? redemptionOrgAccountId;
  // Connect-intent warmup (2026-08-31 instant-connect): the moment the org
  // sign-in target exists, verify its workstation is awake and warm the VM
  // transport — an org VM that idled back to sleep mid-onboarding used to
  // dead-end the connect modal (the gate refused to wake explicit targets);
  // now the wake starts here, minutes before any sign-in click needs it.
  useEffect(() => {
    if (!signInWorkspaceTarget) return;
    void import('../services/composio/connectIntentWarmup')
      .then(({ warmUpConnectIntent }) => warmUpConnectIntent({ workspaceAccountId: signInWorkspaceTarget }))
      .catch(() => undefined);
  }, [signInWorkspaceTarget]);
  const earlyMintInFlightRef = useRef(false);
  const mintDraftOrgEarly = useCallback(async () => {
    if (earlyMintInFlightRef.current || mintedOrgAccountId) return;
    earlyMintInFlightRef.current = true;
    try {
      const u = getAuth().currentUser;
      if (!u) return;
      await setDoc(doc(getFirestore(), 'users', u.uid, 'onboarding', 'pending_org'), {
        earlyMinted: true,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const createOrg = httpsCallable(getFunctions(undefined, 'us-central1'), 'createOrganizationWorkspace');
      const result: any = await createOrg({});
      const accountId = result?.data?.accountId;
      if (!accountId) return;
      setMintedOrgAccountId(accountId);
      // Eager VM for EVERY posture — trial, founding, hire_required alike
      // (owner decision 2026-08-19; reverses the no-VM-until-hired half of
      // hire-first). Fire-and-forget: completion re-kicks, /desktop
      // self-heals, and failures never block the funnel.
      try {
        const token = await getAuthToken();
        if (token) {
          void fetch(fnUrl('provisionUserVM'), {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ accountId }),
          }).catch(() => undefined);
        }
      } catch { /* best-effort */ }
    } catch { /* silent — completion retries the mint */ } finally {
      earlyMintInFlightRef.current = false;
    }
  }, [mintedOrgAccountId]);

  // ── Organic company confirm (task #74, 2026-08-16 owner directive) ──
  // A CODELESS signup has no seeded firm profile — the profile stage leads
  // with the company's LEGAL NAME + an authority attestation, and CONFIRM
  // itself fires the web-research + recommendations call (the SSOT
  // 'onboarding-company-research' ladder server-side). The user keeps
  // filling the form while research runs, so recommendations are warm by
  // the time the chips matter — never a blocking wait.
  const [firmProfileChecked, setFirmProfileChecked] = useState(false);
  const [companyConfirmed, setCompanyConfirmed] = useState(false);
  const [companyLegalName, setCompanyLegalName] = useState('');
  const [companyAttested, setCompanyAttested] = useState(false);
  // In-flow Terms of Service popup (2026-08-23): the confirm card's
  // agreement links here — full selectable text, never a navigation.
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsCopied, setTermsCopied] = useState(false);
  const termsBodyRef = useRef<HTMLDivElement | null>(null);
  const [companyResearching, setCompanyResearching] = useState(false);
  // "wrong company? re-run" correction path + honest research-failure state
  // (review round 2026-08-18: a typo or transient error must never be
  // permanent, and the confirm gate must only ever face ORGANIC accounts).
  const [companyEditRequested, setCompanyEditRequested] = useState(false);
  const [companyResearchFailed, setCompanyResearchFailed] = useState(false);

  // VM provisioning state (for trial users getting dedicated VMs)
  const [vmStatus, setVmStatus] = useState<'unknown' | 'PROVISIONING' | 'RUNNING' | 'done'>('unknown');
  const [vmProvisioningStarted, setVmProvisioningStarted] = useState(false);

  const currentState = session?.state || 'billing_complete';
  const currentTier = session?.tier || subscription?.plan || 'starter';

  const refreshSubscription = useCallback(async (): Promise<SubscriptionInfo | null> => {
    const token = await getAuthToken();
    if (!token) return null;
    const resp = await fetch(fnUrl('getSubscriptionStatus'), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!resp.ok) throw new Error(`Failed to load subscription (${resp.status})`);
    const data = await resp.json();
    const sub = data?.subscription || null;
    setSubscription(sub);
    return sub;
  }, []);

  // Fetch firm-aware recommendations once the subscription confirms a trial.
  // Failure or absence of a firm profile degrades silently to the general
  // defaults — this must never block or slow the flow.
  useEffect(() => {
    // Fires at MOUNT, not gated on the subscription read (2026-08-04 owner
    // feedback: "a little delay to choose the recommended items" — the old
    // gate serialized a full getSubscriptionStatus round-trip in front of
    // the model call). The endpoint reads users/{uid}.trial server-side and
    // returns cheap general defaults for non-trial accounts, so firing
    // immediately is always safe.
    // New-org drafts have no seeded profile — recs come ONLY from the
    // confirm-fired research (org #1's recs must never leak in).
    if (newOrgMode) return;
    if (recsFetchedRef.current) return;
    recsFetchedRef.current = true;
    // Honesty timeout: if recommendations haven't landed in 20s, stop
    // implying they're seconds away — the picker below works without them.
    const waitTimer = setTimeout(() => setRecsUnavailable(true), 20_000);
    (async () => {
      try {
        const token = await getAuthToken();
        if (!token) { setRecsUnavailable(true); return; }
        // Identical lists to the redemption-time prefetch — the server cache
        // key hashes them, so this call is usually a warm hit by now.
        const resp = await fetch(fnUrl('getOnboardingRecommendations'), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workAreaOptions: DEFAULT_PRACTICE_OPTIONS,
            capabilityOptions: buildCapabilityUniverse(),
          }),
        });
        if (!resp.ok) { setRecsUnavailable(true); return; }
        const data = await resp.json();
        if (data?.recommendations?.mode) {
          // Never downgrade: if organic research already applied FIRM recs,
          // a late-landing general response from this mount call must not
          // clobber them (review round 2026-08-18 — response-order race).
          const incoming = data.recommendations as OnboardingRecs;
          setRecs((prev) => (prev?.mode === 'firm' && incoming.mode !== 'firm' ? prev : incoming));
        } else {
          setRecsUnavailable(true);
        }
      } catch { setRecsUnavailable(true); /* general flow */ }
      finally { clearTimeout(waitTimer); }
    })();
  }, [subscription]);

  // ── Organic research + recommendations (task #74) ──
  // Fired by the company CONFIRM (and by resume when a stored legal name
  // exists but research never landed). Runs the server-side web research on
  // the confirmed legal name, then applies the SAME firm recommendations the
  // code flow gets. Never blocks: the user keeps working while it runs.
  const organicRecsFiredRef = useRef(false);
  const recsPresentRef = useRef(false); // mirrors Boolean(recs) — avoids a stale closure read
  const fetchOrganicRecommendations = useCallback(async (name: string) => {
    if (organicRecsFiredRef.current) return;
    organicRecsFiredRef.current = true;
    setCompanyResearching(true);
    setCompanyResearchFailed(false);
    // Honesty timeout: research budgets fit well under this, so past it the
    // spinner stops over-promising; the picker below works without it and a
    // late result still applies when it lands.
    const slowTimer = setTimeout(() => setCompanyResearching(false), 55_000);
    let success = false;
    let pendingRetry = false;
    try {
      const token = await getAuthToken();
      if (!token) { setRecsUnavailable(true); return; }
      const resp = await fetch(fnUrl('getOnboardingRecommendations'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: name,
          workAreaOptions: DEFAULT_PRACTICE_OPTIONS,
          capabilityOptions: buildCapabilityUniverse(),
          // Draft research caches separately and never touches org #1's
          // trial.firmProfile (task #75).
          ...((newOrgMode || firstSignupModeRef.current) ? { pendingOrg: true } : {}),
        }),
      });
      const data = resp.ok ? await resp.json() : null;
      if (data?.recommendations?.mode === 'firm') {
        // The mount-time fetch may have already applied GENERAL results —
        // let the apply-once effect run again for the researched firm recs.
        success = true;
        recsAppliedRef.current = false;
        setRecs(data.recommendations as OnboardingRecs);
        setRecsUnavailable(false);
      } else if (data?.pending) {
        // A concurrent request (second tab / quick re-mount) is already
        // paying for this research — one delayed re-ask picks up its cache.
        pendingRetry = true;
        organicRecsFiredRef.current = false;
        setTimeout(() => { void fetchOrganicRecommendations(name); }, 15_000);
        return;
      } else if (!recsPresentRef.current) {
        setRecsUnavailable(true);
      }
    } catch { setRecsUnavailable(true); }
    finally {
      clearTimeout(slowTimer);
      if (!pendingRetry) setCompanyResearching(false);
      if (!success && !pendingRetry) {
        // One transient failure must never permanently downgrade the session
        // to generic onboarding — unlatch so retry / resume can re-fire.
        organicRecsFiredRef.current = false;
        setCompanyResearchFailed(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep a plain mirror of Boolean(recs) for the organic fetch's failure
  // branch — the [] -deps callback would otherwise read a stale closure.
  useEffect(() => { recsPresentRef.current = Boolean(recs); }, [recs]);

  const confirmCompany = useCallback(async () => {
    const name = companyLegalName.trim();
    if (!name || !companyAttested) return;
    // Protected names (2026-08-24): the card renders the refusal inline and
    // disables CONFIRM; this guard is the belt if both slip.
    if (isProtectedOrgName(name)) return;
    // Same-owner duplicate names (2026-08-25): two identical switcher rows
    // help nobody — the card refuses inline, this is the belt.
    if (draftMode && isDuplicateWorkspaceName(name)) return;
    setCompanyConfirmed(true);
    setCompanyEditRequested(false);
    setCompanyResearchFailed(false);
    // Re-confirm with a different name (correction path) must re-research.
    organicRecsFiredRef.current = false;
    setFirmName(name);
    firmNamePlaceholderRef.current = false;
    // Attestation record — auditable, non-fatal. New-org drafts carry it on
    // the DRAFT doc (the mint gate verifies it server-side); the organic
    // flow keeps it on the user doc.
    if (firstSignupMode) setDraftTargetKind('company');
    try {
      const u = getAuth().currentUser;
      if (u) {
        if (draftMode) {
          // Draft docs carry the attestation (the mint gate verifies it
          // server-side); first-signup drafts also record the fork choice.
          await setDoc(doc(getFirestore(), 'users', u.uid, 'onboarding', 'pending_org'), {
            legalName: name,
            attestedAuthorized: true,
            attestedAt: serverTimestamp(),
            ...(firstSignupMode ? { targetKind: 'company' } : {}),
          }, { merge: true });
        } else {
          await setDoc(doc(getFirestore(), 'users', u.uid), {
            organic_company: {
              legalName: name,
              attestedAuthorized: true,
              attestedAt: serverTimestamp(),
            },
          }, { merge: true });
        }
      }
    } catch { /* research still personalizes this session */ }
    void fetchOrganicRecommendations(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyLegalName, companyAttested, firmName, fetchOrganicRecommendations, draftMode, firstSignupMode, isDuplicateWorkspaceName]);

  // Resume: legal name stored, research never produced a profile → re-fire.
  useEffect(() => {
    if (!firmProfileChecked || !companyConfirmed || instantFirmProfile) return;
    if (companyLegalName.trim()) void fetchOrganicRecommendations(companyLegalName.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmProfileChecked, companyConfirmed, instantFirmProfile]);

  // Solo / no-legal-entity path (review round 2026-08-18): an honest way
  // through for people without a company — no attestation, no research,
  // and the fabricated tenant placeholder ('<name> Law') never survives
  // into user-facing writes.
  const skipCompanyConfirm = useCallback(async () => {
    setCompanyConfirmed(true);
    setCompanyEditRequested(false);
    // First-signup fork (2026-08-19 split): "just me" targets the PERSONAL
    // uid-keyed workspace — launch activates it instead of minting a co_ org.
    if (firstSignupMode) setDraftTargetKind('personal');
    if (firmNamePlaceholderRef.current) {
      setFirmName('');
      firmNamePlaceholderRef.current = false;
    }
    try {
      const u = getAuth().currentUser;
      if (u) {
        if (firstSignupMode) {
          await setDoc(doc(getFirestore(), 'users', u.uid, 'onboarding', 'pending_org'), {
            targetKind: 'personal',
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }
        await setDoc(doc(getFirestore(), 'users', u.uid), {
          organic_company: { skipped: true, attestedAt: serverTimestamp() },
        }, { merge: true });
      }
    } catch { /* resume just re-offers the choice */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstSignupMode]);

  // Organic-trial retry from the activation screen (review round 2026-08-18):
  // if provisioning failed at signup, the user can start it here — and the
  // refusal reasons get honest copy instead of an enterprise-license wall.
  const [organicRetrying, setOrganicRetrying] = useState(false);
  const [organicRetryNote, setOrganicRetryNote] = useState<string | null>(null);
  const retryOrganicTrial = useCallback(async () => {
    setOrganicRetrying(true);
    setOrganicRetryNote(null);
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const startOrganicTrial = httpsCallable(getFunctions(undefined, 'us-central1'), 'startOrganicTrial');
      const result: any = await startOrganicTrial({});
      const r = result?.data || {};
      if (r.started || r.reason === 'already-provisioned' || r.reason === 'lazy-mode') {
        // 'lazy-mode': the flag flipped while this screen was open —
        // re-bootstrap runs the first-signup triage and enters draft mode.
        await bootstrap();
        try { await refreshSubscription(); } catch { /* bootstrap already refreshed */ }
        return;
      }
      if (r.reason === 'pending-team-invite') {
        setOrganicRetryNote('you have a team invitation waiting — open the invite link from your email to join your team (no trial needed).');
      } else if (r.reason === 'is-workspace-member') {
        setOrganicRetryNote("you're already part of a team workspace — head back to the home page to start working.");
      } else {
        setOrganicRetryNote("couldn't start your trial just now — give it a minute and try again.");
      }
    } catch {
      setOrganicRetryNote("couldn't start your trial just now — give it a minute and try again.");
    } finally {
      setOrganicRetrying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The confirm gate faces ORGANIC accounts only (provenance, never mere
  // profile-absence — non-firm-aware trial CODES must keep the plain
  // company-or-team-name input; review round 2026-08-18).
  const isOrganicTrial = subscription?.trial_tier === 'organic';
  // New-org drafts ALWAYS lead with the legal-name confirm — no code can
  // seed a created org, and an organization must be a real one (task #75).
  // First-signup drafts (2026-08-19 split) lead with it too: the confirm IS
  // the company/just-me fork that decides which workspace launch mints.
  const confirmGateEligible = newOrgMode || firstSignupMode || isOrganicTrial;
  const showCompanyConfirm = confirmGateEligible && firmProfileChecked
    && ((!instantFirmProfile && !companyConfirmed && recs?.mode !== 'firm') || companyEditRequested);
  // The terms popup serves BOTH profile-card branches now (the confirm card
  // and the unified attestation under the plain name input, 2026-08-24), so
  // it closes when the PROFILE PHASE exits rather than when the confirm
  // branch flips — that phase-keyed effect lives just after trialPhase's
  // declaration. The original latent-state concern (review 2026-08-23: the
  // modal popping back open unrequested) is covered there.

  // Pending promo-code belt (2026-08-25 owner report: a /start?code link's
  // redemption depended on RETURNING to /start after account creation — any
  // other post-signup routing dropped the code silently and the account fell
  // to the organic tier). If the flag survived all the way to onboarding,
  // redeem it here: the shared client never throws, and the server
  // classifies correctly against whatever state the account is in (fresh
  // trial start, or an extension over an accidentally started organic
  // trial). Whichever surface consumes the flag first owns the redemption.
  const pendingCodeRedeemedRef = useRef(false);
  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged((user) => {
      if (!user || user.isAnonymous || pendingCodeRedeemedRef.current) return;
      let code: string | null = null;
      try {
        code = sessionStorage.getItem('pending_trial_code');
        if (code) sessionStorage.removeItem('pending_trial_code');
      } catch { /* storage unavailable */ }
      if (!code) return;
      pendingCodeRedeemedRef.current = true;
      const normalized = code.trim().toUpperCase();
      void import('../services/accessCodeClient').then(({ redeemAccessCode }) =>
        redeemAccessCode(normalized),
      ).then((r) => {
        console.log('[onboarding] pending trial code consumed', { success: (r as any)?.success !== false });
        // The redemption response has always carried the org it created —
        // the client used to DISCARD it, leaving every connect this session
        // to ambient (wrong-plane) resolution until the completion-time
        // back-pointer switch (2026-08-27 dead-end RCA).
        const org = (r as any)?.orgAccountId;
        if (typeof org === 'string' && org.startsWith('co_')) setRedemptionOrgAccountId(org);
      }).catch(() => { /* non-fatal — the code itself stays unredeemed and usable at /start */ });
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Session-mode mint self-heal (2026-08-27 dead-end RCA): the redemption's
  // in-request org mint can die on a transient error, its catch is non-fatal
  // by contract, and the ONLY self-heal lived on /desktop — which onboarding
  // users never visit. So a code redeemer could walk the whole flow with the
  // back-pointer still on the personal plane and no sign-in target. Heal it
  // where the user actually is: on entry, if the caller's pending org shows
  // an access-code draft that never minted, re-invoke the same idempotent
  // callable (it resumes from pending_org.mintedAccountId; a healthy account
  // returns in one read). Also adopts an already-minted org id on resume so
  // signInWorkspaceTarget is set without waiting for a fresh mint.
  const sessionMintHealRef = useRef(false);
  useEffect(() => {
    if (draftMode || sessionMintHealRef.current) return;
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged((user) => {
      if (!user || user.isAnonymous || sessionMintHealRef.current) return;
      sessionMintHealRef.current = true;
      void (async () => {
        try {
          const snap = await getDoc(doc(getFirestore(), 'users', user.uid, 'onboarding', 'pending_org'));
          const d = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
          if (!d || d.source !== 'access-code-trial') return;
          const minted = typeof d.mintedAccountId === 'string' ? d.mintedAccountId : '';
          if (minted.startsWith('co_')) {
            setRedemptionOrgAccountId((prev) => prev ?? minted);
            return;
          }
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const heal = httpsCallable(getFunctions(undefined, 'us-central1'), 'ensureTrialOrgWorkspace');
          const res = await heal({});
          const org = (res.data as Record<string, unknown> | undefined)?.accountId;
          if (typeof org === 'string' && org.startsWith('co_')) {
            setRedemptionOrgAccountId((prev) => prev ?? org);
          }
        } catch { /* best-effort — the redemption-time retry and hourly sweep still stand */ }
      })();
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftMode]);

  // Instant identity read + apply (never over user input or a resumed
  // session's saved values; placeholder semantics identical to the recs
  // path).
  useEffect(() => {
    // New-org mode: org #1's firmProfile / attestation / signup prefill are
    // a DIFFERENT organization's identity — never read them for a draft.
    if (newOrgMode) { setFirmProfileChecked(true); return; }
    (async () => {
      try {
        const user = getAuth().currentUser;
        if (!user) return;
        const snap = await getDoc(doc(getFirestore(), 'users', user.uid));
        const fp = (snap.data() as any)?.trial?.firmProfile;
        if (fp && typeof fp === 'object') setInstantFirmProfile(fp as Record<string, string>);
        // Invite contact details (2026-08-24): a code minted with the
        // invitee's own phone pre-fills it here, so the verification text
        // fires at save without retyping. Never overwrites anything typed.
        const contact = (snap.data() as any)?.trial?.contactProfile;
        if (contact && typeof contact.phone === 'string' && contact.phone.trim()) {
          setContactPhone((prev) => prev || contact.phone.trim());
        }
        // Invite person name (owner report 2026-08-27, Whitfield run): the
        // code carries the invitee's OWN name (personName, minted by Prest0
        // — a REAL source, same class as auth displayName), but only the
        // phone was ever consumed and the name field sat empty for every
        // email/password signup. Fill it the same way: only over an empty
        // field, never over anything the user typed.
        if (contact && typeof contact.personName === 'string' && contact.personName.trim()) {
          setFullName((prev) => {
            if (prev.trim()) return prev;
            fullNamePrefilledRef.current = true;
            return contact.personName.trim();
          });
        }
        // Resume path (task #74): a previously confirmed organic company —
        // don't ask again; prefill and let the recs effect re-fire research
        // if the earlier attempt never produced a profile.
        const oc = (snap.data() as any)?.organic_company;
        if (!fp && oc?.skipped === true) {
          // Solo / no-legal-entity path chosen earlier — never re-ask.
          setCompanyConfirmed(true);
        } else if (!fp && oc?.legalName && typeof oc.legalName === 'string' && !isProtectedOrgName(oc.legalName)) {
          setCompanyLegalName(oc.legalName);
          setCompanyConfirmed(true);
        } else if (!fp) {
          // /signup prefill handoff — the company + phone typed there are
          // never retyped here (review round 2026-08-18).
          try {
            const raw = sessionStorage.getItem('prest0_signup_prefill');
            if (raw) {
              const pre = JSON.parse(raw);
              if (pre?.company) setCompanyLegalName((prev) => prev || safePrefillOrgName(String(pre.company)));
              if (pre?.phone) setContactPhone((prev) => prev || String(pre.phone));
            }
          } catch { /* best-effort */ }
        }
      } catch { /* recs path still covers prefill */ }
      finally { setFirmProfileChecked(true); }
    })();
  }, []);
  useEffect(() => {
    if (loading || !instantFirmProfile || instantAppliedRef.current) return;
    instantAppliedRef.current = true;
    // safePrefillOrgName (2026-08-24): an invite profile carrying the
    // platform's own name (or any protected name) never prefills — the field
    // stays the user's to fill with THEIR company.
    const instantPrefill = safePrefillOrgName(instantFirmProfile.firmName);
    if ((!firmName.trim() || firmNamePlaceholderRef.current) && instantPrefill) {
      setFirmName(instantPrefill);
      firmNamePlaceholderRef.current = false;
    }
    const sizeOption = mapTeamSizeToOption(instantFirmProfile.teamSize);
    if (!firmSize && sizeOption) setFirmSize(sizeOption);
    // INSTANT chip pre-selection (2026-08-04 owner feedback: the bubbles sat
    // blank for 10-15s). Keyword-derive up to two work areas from the
    // invite's industry/focus RIGHT NOW and light their capabilities; the
    // model recommendations (prefetched at redemption, usually cache-warm)
    // replace these seconds later unless the user has already touched the
    // chips — user toggles always win.
    if (practiceAreas.length === 0) {
      const haystack = `${instantFirmProfile.industry || ''} ${instantFirmProfile.focus || ''}`;
      const areas: string[] = [];
      for (const [re, area] of INDUSTRY_AREA_KEYWORDS) {
        if (areas.length >= 2) break;
        if (re.test(haystack) && !areas.includes(area)) areas.push(area);
      }
      if (areas.length > 0) {
        areasFromInstantRef.current = true;
        setPracticeAreas(areas);
        setSelectedCapabilities(prev => {
          const capSet = new Set(prev);
          UNIVERSAL_CAPABILITIES.forEach(c => capSet.add(c));
          for (const a of areas) (CAPABILITIES_BY_PRACTICE[a] || []).forEach(c => capSet.add(c));
          return Array.from(capSet);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, instantFirmProfile]);

  // Apply firm recommendations exactly once, after hydration, and never over
  // anything the user (or a resumed session) already chose.
  useEffect(() => {
    if (loading || !recs || recs.mode !== 'firm' || recsAppliedRef.current) return;
    recsAppliedRef.current = true;
    const recsPrefill = safePrefillOrgName(recs.firmName);
    if ((!firmName.trim() || firmNamePlaceholderRef.current) && recsPrefill) {
      setFirmName(recsPrefill);
      firmNamePlaceholderRef.current = false;
    }
    const sizeOption = mapTeamSizeToOption(recs.firmTeamSize);
    if (!firmSize && sizeOption) setFirmSize(sizeOption);
    if ((practiceAreas.length === 0 || areasFromInstantRef.current) && recs.recommendedWorkAreas.length > 0) {
      const areas = recs.recommendedWorkAreas.filter(a => DEFAULT_PRACTICE_OPTIONS.includes(a));
      if (areas.length > 0) {
        // Replacing keyword-derived INSTANT picks: rebuild the selection from
        // scratch (universal + the model areas' caps + the model's specific
        // picks) so stale instant-area capabilities don't linger. User
        // toggles cleared the flag, so a touched selection is never rebuilt.
        const replacingInstant = areasFromInstantRef.current;
        areasFromInstantRef.current = false;
        setPracticeAreas(areas);
        setSelectedCapabilities(prev => {
          const capSet = new Set(replacingInstant ? UNIVERSAL_CAPABILITIES : prev);
          for (const a of areas) (CAPABILITIES_BY_PRACTICE[a] || []).forEach(c => capSet.add(c));
          (recs.recommendedCapabilities || []).forEach(c => capSet.add(c));
          return Array.from(capSet);
        });
      }
    }
  }, [loading, recs, firmName, firmSize, practiceAreas]);

  // Active connections per slug (multi-account, task #52): the
  // add-another-account flow diffs this roster to find WHICH connection just
  // landed, so its scope sheet targets the NEW account and not the primary.
  // Each entry carries the signed-in account's identity (email etc., 2026-08-19
  // owner directive: every view names WHICH accounts are signed in).
  const connectionRostersRef = useRef<Record<string, Array<{ id: string; identity?: string }>>>({});
  /**
   * BROWSER-LANE connections, keyed by MATCH KEY rather than slug
   * (2026-09-03 owner report).
   *
   * Until now this page read `getConnectedAccounts()` alone — Composio/OAuth
   * connections — so a user who signed into Oura through Prest0n's browser was
   * told "No integrations connected yet" on the readiness check, kept being
   * nudged by the connect coach, and kept seeing Oura in the recommended list.
   * The missing identity chip was the visible symptom of that.
   *
   * Keyed by `connectAppOfferKey` (lowercase, strip non-alphanumerics) because
   * the two namespaces do not line up: a catalog row is `ouraring`, its
   * browser-lane connection is `custom-oura_ring`, and the user typed "Oura
   * Ring". That normalizer is already THE match key for "is this the same
   * app?" everywhere else in the connect lane, so this reuses it rather than
   * inventing a second answer.
   *
   * Deliberately NOT resolved against the catalog: `allToolkits` loads lazily
   * for the apps phase, and this refresh runs before that on the common path —
   * a catalog-dependent mapping would be correct only sometimes, which is
   * worse than not depending on it.
   */
  const customRostersRef = useRef<BrowserLaneRoster>({ byKey: {}, slugs: [] });

  const refreshConnectedApps = useCallback(async () => {
    try {
      const accounts = await getConnectedAccounts();
      const active = accounts.filter((a) => (a.status || '').toUpperCase() === 'ACTIVE');
      const rosters: Record<string, Array<{ id: string; identity?: string }>> = {};
      for (const a of active) {
        const slug = (a.toolkit?.slug || '').toLowerCase();
        if (!slug) continue;
        rosters[slug] = a.connections?.length
          ? a.connections.map((c) => ({ id: c.id, identity: c.identity }))
          : [{ id: a.id, identity: a.identity }];
      }
      connectionRostersRef.current = rosters;
      // Browser-lane connections are ADDITIVE and best-effort: a failed custom
      // fetch must never shrink the roster, the same fail-open rule
      // connectedAppsStore already writes down. A user whose Composio roster
      // loaded must not be told they have nothing because a second call
      // 500'd.
      let customs: Awaited<ReturnType<typeof listCustomConnections>> = [];
      try {
        customs = await listCustomConnections();
      } catch {
        customs = [];
      }
      // The rules live in browserLaneRoster.ts so a test can EXECUTE them —
      // which lane a connection belongs to, and which catalog row it answers
      // for, are exactly the decisions that are wrong in ways nobody notices.
      const browserLane = buildBrowserLaneRoster(customs);
      customRostersRef.current = browserLane;
      const activeSlugs = [...Object.keys(rosters), ...browserLane.slugs];
      setConnectedApps(activeSlugs);
      return activeSlugs;
    } catch {
      return [];
    }
  }, []);

  /**
   * Is THIS catalog row's app connected? (2026-09-03)
   *
   * `connectedApps.includes(slug)` only ever matched a Composio slug. A
   * browser-lane connection lives under `custom-<name>`, so a catalog row for
   * the same app never lit up. This is the predicate the ROWS and the
   * recommendation filters ask; the raw list stays the thing that gets
   * counted.
   */
  const isAppConnected = useCallback((slug: string): boolean => {
    return isAppConnectedInEitherLane(connectedApps, customRostersRef.current, slug);
  }, [connectedApps]);

  // Row-chip label for a slug's roster: leads with WHO is signed in.
  // One account → its identity ("miles@acme.com"); several → primary
  // identity + "+N" (mirrors the integrations tiles); identity unknown →
  // the old count fallback.
  const rosterChip = useCallback((slug: string): string => {
    const roster = connectionRostersRef.current[slug] || [];
    const identities = roster.map((c) => c.identity).filter(Boolean) as string[];
    if (identities.length === 0) {
      // Browser-lane fallback (2026-09-03): no Composio roster for this slug,
      // but a browser sign-in may still stand for the same app. Its identity
      // is what the user attested at completion — when they gave one. When
      // they did not, the row ASKS (identityAskFor below) rather than
      // guessing or saying nothing.
      const custom = browserLaneEntryFor(customRostersRef.current, slug);
      if (custom?.identity) return custom.identity;
      return roster.length > 1 ? `${roster.length} accounts` : '';
    }
    const primary = identities[0];
    const extra = roster.length - 1;
    return extra > 0 ? `${primary} +${extra}` : primary;
  }, []);

  /**
   * The "which account?" control for a connected browser-lane app that never
   * recorded one (2026-09-03 owner decision: ask, rather than show nothing).
   * Null for a direct connection (its identity comes from the provider) and
   * for one that already has an identity.
   */
  const identityAskFor = useCallback((slug: string): React.ReactNode => {
    const custom = browserLaneEntryFor(customRostersRef.current, slug);
    if (!custom || custom.identity) return null;
    return (
      <IdentityPrompt
        slug={custom.slug}
        variant="onboarding"
        onSave={async (customSlug, identity) => {
          try {
            const res = await updateCustomConnectionIdentity(customSlug, identity);
            if (res?.ok !== true) return false;
          } catch {
            return false;
          }
          await refreshConnectedApps();
          return true;
        }}
      />
    );
  }, [refreshConnectedApps]);

  const persistSession = useCallback(async (next: OnboardingSession) => {
    const user = getAuth().currentUser;
    if (!user) throw new Error('Not signed in');
    const db = getFirestore();
    const ref = doc(db, 'users', user.uid, 'onboarding', onboardingDocId);
    const normalized: OnboardingSession = {
      ...next,
      state: deriveState(next),
      updatedAt: serverTimestamp(),
    };
    await setDoc(ref, {
      ...normalized,
      createdAt: next.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    setSession(normalized);
    setSuccess('Progress saved.');
    // onboardingDocId is a dep (2026-08-19): first-signup mode flips it to
    // 'pending_org' AFTER mount — a stale closure would write the task-56
    // session doc from draft mode.
  }, [onboardingDocId]);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const user = getAuth().currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      let [sub, configResult, activeApps] = await Promise.all([
        refreshSubscription(),
        resolveEffectiveConfig().catch(() => null),
        refreshConnectedApps(),
      ]);

      // ── First-signup triage (lazy personal/org split, 2026-08-19) ──
      // A visitor with NO session and NO subscription is a brand-new signup
      // (or a gate-redirected return of one). The server decides the mode:
      //   started / already-provisioned → eager/self-healed trial → the
      //     normal session-mode staged flow (re-pull the subscription);
      //   'lazy-mode' → FIRST-SIGNUP DRAFT MODE — same staged flow, but the
      //     pending_org draft is the only record until launch mints the
      //     chosen target (company co_ org, or the personal uid workspace);
      //   'pending-team-invite' / 'is-workspace-member' → honest refusal
      //     routing preserved: home, where PendingInviteGate/resolution owns
      //     the journey.
      let bootFirstSignup = false;
      if (!newOrgMode && !(sub && ['active', 'trialing'].includes(sub.status))) {
        try {
          const db0 = getFirestore();
          const sessionProbe = await getDoc(doc(db0, 'users', user.uid, 'onboarding', 'session'));
          if (!sessionProbe.exists()) {
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const startTrial = httpsCallable(getFunctions(undefined, 'us-central1'), 'startOrganicTrial');
            const r: any = ((await startTrial({})) as any)?.data || {};
            if (r?.started === true || r?.reason === 'already-provisioned') {
              sub = await refreshSubscription();
            } else if (r?.reason === 'lazy-mode') {
              const draftRef0 = doc(db0, 'users', user.uid, 'onboarding', 'pending_org');
              const draftProbe = await getDoc(draftRef0);
              const draftD: any = draftProbe.data() || {};
              // A minted draft is only "done" once launch finished
              // (launchCompleted). An EARLY mint mid-onboarding (2026-08-19
              // eager-VM) must RESUME — bouncing to '/' threw away the
              // user's in-progress setup. Legacy consumed drafts (minted
              // without the earlyMinted stamp) predate the early mint and
              // are always completion-minted → treat as done.
              if (draftD.mintedAccountId && (draftD.launchCompleted || !draftD.earlyMinted)) {
                // Their first workspace already launched once — workspace
                // resolution owns the routing from here (second orgs go
                // through ?neworg=1).
                window.location.assign('/');
                return;
              }
              bootFirstSignup = true;
              setFirstSignupMode(true);
              if (draftD.mintedAccountId) setMintedOrgAccountId(draftD.mintedAccountId);
              const savedKind = draftD.targetKind;
              if (savedKind === 'personal' || savedKind === 'company') setDraftTargetKind(savedKind);
              await setDoc(draftRef0, { firstSignup: true, updatedAt: serverTimestamp() }, { merge: true });
            } else if (r?.reason === 'pending-team-invite' || r?.reason === 'is-workspace-member') {
              window.location.assign('/');
              return;
            }
          }
        } catch { /* fall through — the no-billing branch stays the honest fallback */ }
      }

      const billingComplete = Boolean(sub && ['active', 'trialing'].includes(sub.status));
      const tenantId = sub?.tenantId || configResult?.tenant?.id || null;
      setEffectiveTenantId(tenantId);

      const existingKnowledge = configResult?.tenant?.knowledge || [];
      const restoredUploads = existingKnowledge
        .filter((doc: any) => typeof doc?.content === 'string' && doc.content.includes('Storage: '))
        .map((doc: any) => {
          const urlMatch = doc.content.match(/URL:\s*(https?:\/\/\S+)/);
          return {
            name: doc.title || 'Uploaded document',
            url: urlMatch?.[1] || '',
            docId: doc.id,
          };
        });
      setUploadedFiles(restoredUploads);

      const hasAnyIntegration = activeApps.length > 0;

      const db = getFirestore();

      // New-org entry gate (task #75 review findings 4/14/17): only a user
      // who still OWES their own trial onboarding is refused — and refused
      // HERE, before they spend minutes on a draft the mint would reject.
      // Activated, legacy, and invited-member accounts pass (the server gate
      // mirrors this exactly).
      if (newOrgMode) {
        try {
          const ownSnap = await getDoc(doc(db, 'users', user.uid, 'onboarding', 'session'));
          const ownSession = ownSnap.exists() ? (ownSnap.data() as any) : null;
          if (needsTrialOnboarding(sub as any, ownSession, Date.now())) {
            setNewOrgBlocked(true);
            setLoading(false);
            return;
          }
        } catch { /* fail open — the mint re-verifies server-side */ }
      }

      // The render-scope onboardingDocId can't see the firstSignup flip made
      // above in this same run — thread the mode-correct id locally.
      const bootDocId = newOrgMode || bootFirstSignup ? 'pending_org' : 'session';
      const ref = doc(db, 'users', user.uid, 'onboarding', bootDocId);
      const snap = await getDoc(ref);

      // A CONSUMED draft belongs to the PREVIOUS creation — reset to a fresh
      // shell so "start a new organization" starts a new one instead of
      // resuming org #1's data into the server's idempotent-return branch
      // (review finding 15). Consumed = launch finished (launchCompleted),
      // or a legacy completion-time mint (minted without the earlyMinted
      // stamp). An early-minted draft mid-onboarding RESUMES (2026-08-19
      // eager-VM: the org + VM already exist; resetting would strand them).
      let draftData: OnboardingSession | null = snap.exists() ? (snap.data() as OnboardingSession) : null;
      const draftAny: any = draftData;
      if (newOrgMode && draftAny?.mintedAccountId && (draftAny.launchCompleted || !draftAny.earlyMinted)) {
        await setDoc(ref, { createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
        draftData = null;
        // The finished org's reservation must not follow the user into the
        // NEXT org's setup (review 2026-08-23): a stale latch would point
        // this flow's connects at the previous org's workstation.
        clearReservedDraftOrgId();
        setDraftReservedOrgId(null);
      } else if (newOrgMode && draftAny?.mintedAccountId) {
        setMintedOrgAccountId(draftAny.mintedAccountId);
      }

      let activeSession: OnboardingSession;
      if (draftData) {
        const data = draftData;
        activeSession = {
          ...emptySession((sub?.plan || 'starter') as PlanTier, tenantId, billingComplete),
          ...data,
          tenantId: data?.tenantId || tenantId,
          tier: (data?.tier || sub?.plan || 'starter') as PlanTier,
          steps: {
            ...emptySession((sub?.plan || 'starter') as PlanTier, tenantId, billingComplete).steps,
            ...(data?.steps || {}),
            billingComplete,
            slackConnected: billingComplete && (data?.steps?.slackConnected || hasAnyIntegration),
            configCompleted: billingComplete && Boolean(data?.steps?.configCompleted),
            docsCompleted: billingComplete && Boolean(data?.steps?.docsCompleted),
            testRunCompleted: billingComplete && Boolean(data?.steps?.testRunCompleted),
          },
          state: deriveState({
            ...emptySession((sub?.plan || 'starter') as PlanTier, tenantId, billingComplete),
            ...data,
            steps: {
              ...emptySession((sub?.plan || 'starter') as PlanTier, tenantId, billingComplete).steps,
              ...(data?.steps || {}),
              billingComplete,
              slackConnected: billingComplete && (data?.steps?.slackConnected || hasAnyIntegration),
              configCompleted: billingComplete && Boolean(data?.steps?.configCompleted),
              docsCompleted: billingComplete && Boolean(data?.steps?.docsCompleted),
              testRunCompleted: billingComplete && Boolean(data?.steps?.testRunCompleted),
            },
          }),
        };
      } else {
        activeSession = {
          ...emptySession((sub?.plan || 'starter') as PlanTier, tenantId, billingComplete),
          steps: {
            ...emptySession((sub?.plan || 'starter') as PlanTier, tenantId, billingComplete).steps,
            billingComplete,
            slackConnected: billingComplete && hasAnyIntegration,
          },
        };
        await setDoc(ref, {
          ...activeSession,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }

      setSession(activeSession);
      {
        // NEVER adopt the tenant label as a company-name prefill (owner
        // directive 2026-08-23): tenant labels can be DERIVED from auth
        // fields ("<name> Law" era) rather than user-provided — with no real
        // profile source the field must stay an empty grey placeholder. The
        // legitimate sources (the user's own session save, invite firm
        // profiles, model research, the signup handoff) all still prefill
        // through their own paths.
        const sessionFirmName = safePrefillOrgName(activeSession.profile?.firmName);
        setFirmName(sessionFirmName || '');
        firmNamePlaceholderRef.current = false;
      }
      setBrandTone(activeSession.profile?.brandTone || configResult?.tenant?.profile?.brandTone || 'Professional and direct');
      setDefaultLanguage(activeSession.profile?.defaultLanguage || configResult?.tenant?.profile?.defaultLanguage || 'English');
      setPracticeAreas(activeSession.profile?.practiceAreas || configResult?.tenant?.profile?.practiceAreas || []);
      // Resume support: restore the trial quick-profile's partial answers so a
      // user who left mid-step picks up exactly where they stopped.
      const savedProfile = activeSession.profile as any;
      if (typeof savedProfile?.fullName === 'string' && savedProfile.fullName.trim()) {
        setFullName((prev) => prev || savedProfile.fullName.trim());
        fullNamePrefilledRef.current = true;
      }
      if (savedProfile?.firmSize) setFirmSize(savedProfile.firmSize);
      if (savedProfile?.contactPhone) setContactPhone(savedProfile.contactPhone);
      if (Array.isArray(savedProfile?.selectedCapabilities) && savedProfile.selectedCapabilities.length > 0) {
        setSelectedCapabilities(savedProfile.selectedCapabilities);
      }
      // Draft resume (task #75; first-signup mode 2026-08-19): a confirmed
      // legal name on the draft is never asked again; the mint gate
      // re-verifies server-side anyway.
      if (newOrgMode || bootFirstSignup) {
        const d = activeSession as any;
        if (typeof d?.legalName === 'string' && d.legalName && d?.attestedAuthorized === true) {
          setCompanyLegalName(d.legalName);
          setCompanyConfirmed(true);
        }
      }
      // Unbreakable staged flow: a refresh mid-phase resumes exactly there.
      const savedPhase = (activeSession as any)?.trialPhase;
      if (savedPhase === 'apps' || savedPhase === 'team') {
        setTrialPhase(savedPhase);
        // Everything up to the resumed stage is revisitable (task #56).
        setVisitedPhases(savedPhase === 'team' ? ['profile', 'apps', 'team'] : ['profile', 'apps']);
      } else if (!newOrgMode && savedPhase === 'orientation') {
        // Legacy resume (task #53): the "meet your workspace" phase was
        // removed — a session parked there had already finished setup, so
        // send it straight into the workspace. (Inline write: the
        // persistTrialPhase helper is declared later in the component.)
        try {
          const u = getAuth().currentUser;
          if (u) {
            void setDoc(doc(getFirestore(), 'users', u.uid, 'onboarding', 'session'),
              { trialPhase: 'done', updatedAt: serverTimestamp() }, { merge: true });
          }
        } catch { /* non-blocking */ }
        window.location.href = `${window.location.origin}/desktop`;
        return;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load onboarding');
    } finally {
      setLoading(false);
    }
  }, [refreshConnectedApps, refreshSubscription]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // Poll Firestore for VM provisioning status (trial users).
  // 2026-08-02 fix: provisionTestModeVM writes users/{uid}/infrastructure/vm
  // (UPPERCASE GCE statuses) — the previous users/{uid}/vm/main path never
  // existed, so this banner was permanently dead.
  useEffect(() => {
    const user = getAuth().currentUser;
    if (!user) return;
    const db = getFirestore();
    const vmRef = doc(db, 'users', user.uid, 'infrastructure', 'vm');
    const unsub = onSnapshot(vmRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as any;
      const status: string = data?.status || 'unknown';
      if (['PROVISIONING', 'STAGING'].includes(status)) {
        setVmStatus('PROVISIONING');
        setVmProvisioningStarted(true);
      } else if (status === 'RUNNING') {
        setVmStatus('RUNNING');
        setVmProvisioningStarted(true);
      }
    }, () => { /* non-fatal */ });
    return () => unsub();
  }, []);


  // ── Resume support: debounce-persist the trial quick-profile's partial
  // answers (firm name, work areas, team size, capabilities) into the
  // onboarding session while the step is still incomplete, so closing the
  // tab / signing out never loses progress. Final completion is still only
  // written by saveTrialProfile.
  const partialSaveReady = useRef(false);
  useEffect(() => {
    // Skip the initial hydration pass so we don't echo defaults over a
    // saved profile before bootstrap restores it.
    if (loading) return;
    if (!partialSaveReady.current) { partialSaveReady.current = true; return; }
    const user = getAuth().currentUser;
    if (!user || !session) return;
    const isTrialSession = isTrialSubscription(subscription);
    if (!isTrialSession || session.steps?.configCompleted) return;
    const t = setTimeout(() => {
      const db = getFirestore();
      setDoc(doc(db, 'users', user.uid, 'onboarding', onboardingDocId), {
        profile: { fullName, firmName, practiceAreas, firmSize, contactPhone, selectedCapabilities },
        updatedAt: serverTimestamp(),
      }, { merge: true }).catch(() => { /* best-effort — completion still saves */ });
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullName, firmName, practiceAreas, firmSize, selectedCapabilities]);

  const handleSignOut = async () => {
    try { await getAuth().signOut(); } catch { /* ignore */ }
    window.location.href = `${window.location.origin}/`;
  };

  const togglePracticeArea = (area: string) => {
    setPracticeAreas((prev) => prev.includes(area)
      ? prev.filter((p) => p !== area)
      : [...prev, area]);
  };

  const openIntegrations = () => {
    window.location.href = `${window.location.origin}/integrations`;
  };

  const saveFirmConfig = async () => {
    if (!session) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const tenantId = session.tenantId || effectiveTenantId;
      if (!tenantId) throw new Error('Missing tenant ID - billing/tenant provisioning must complete first');

      await updateTenantProfile(tenantId, {
        firmName,
        practiceAreas,
        brandTone,
        defaultLanguage,
      });
      await updateTenantConfig(tenantId, {
        defaultLanguage,
        chatWelcomeMessage: `Welcome to ${firmName || 'your team'}'s Prest0 workspace.`,
      });

      await persistSession({
        ...session,
        tenantId,
        profile: {
          firmName,
          practiceAreas,
          brandTone,
          defaultLanguage,
        },
        steps: { ...session.steps, configCompleted: true },
        notes: { ...session.notes, lastCompletedStep: 'config_pending', lastError: null },
      });
      setSuccess('Team profile and preferences saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save team setup');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !session) return;
    const tenantId = session.tenantId || effectiveTenantId;
    if (!tenantId) {
      setError('Missing tenant ID - complete billing first');
      return;
    }
    const user = getAuth().currentUser;
    if (!user) return;

    setUploadingDocs(true);
    setError(null);
    setSuccess(null);

    const newUploads: Array<{ name: string; url: string; docId?: string }> = [];

    try {
      for (const file of Array.from(files)) {
        // 1. Upload file to Firebase Storage under tenant path
        const path = `tenants/${tenantId}/knowledge/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, file, {
          contentType: file.type,
          customMetadata: {
            originalName: file.name,
            uploadedBy: user.uid,
            uploadedAt: new Date().toISOString(),
          },
        });
        const downloadUrl = await getDownloadURL(fileRef);

        // 2. Create knowledge doc metadata in Firestore
        const category = file.type.includes('pdf') ? 'Document'
          : file.type.includes('word') || file.type.includes('docx') ? 'Document'
          : file.type.includes('sheet') || file.type.includes('csv') ? 'Reference Data'
          : 'General';

        const result = await addKnowledgeDoc(tenantId, {
          title: file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' '),
          category,
          content: `Uploaded file: ${file.name} (${(file.size / 1024).toFixed(1)} KB)\nStorage: ${path}\nURL: ${downloadUrl}`,
          language: defaultLanguage || 'English',
          isPublic: false,
        });

        newUploads.push({ name: file.name, url: downloadUrl, docId: result?.docId });
      }

      setUploadedFiles((prev) => [...prev, ...newUploads]);
      setSuccess(`${newUploads.length} document${newUploads.length > 1 ? 's' : ''} uploaded and indexed.`);

      // Advance onboarding step
      await persistSession({
        ...session,
        steps: { ...session.steps, docsCompleted: true },
        notes: { ...session.notes, lastCompletedStep: 'docs_pending', lastError: null },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload documents');
    } finally {
      setUploadingDocs(false);
    }
  };

  const markDocsSkip = async () => {
    if (!session) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await persistSession({
        ...session,
        steps: { ...session.steps, docsCompleted: true },
        notes: { ...session.notes, lastCompletedStep: 'docs_pending', lastError: null },
      });
      setSuccess('Document step skipped - you can upload docs later from settings.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to skip docs step');
    } finally {
      setSaving(false);
    }
  };

  const runLiveTest = async () => {
    if (!session) return;
    const tenantId = session.tenantId || effectiveTenantId;
    setTestRunning(true);
    setError(null);
    setSuccess(null);

    try {
      const checks = [
        {
          label: 'Tenant provisioned',
          pass: !!tenantId,
          detail: tenantId ? `Tenant ${tenantId} found` : 'No tenant ID found',
        },
        {
          label: 'Tools connected',
          pass: connectedApps.length > 0 || !!session.steps.slackConnected,
          detail: connectedApps.length > 0
            ? `${connectedApps.length} integration${connectedApps.length > 1 ? 's' : ''} active (${connectedApps.slice(0, 3).join(', ')}${connectedApps.length > 3 ? '…' : ''})`
            : session.steps.slackConnected ? 'Step completed (connect tools anytime from Settings)' : 'No integrations connected yet',
        },
        {
          label: 'Team profile configured',
          pass: Boolean(firmName && practiceAreas.length > 0),
          detail: firmName
            ? `${firmName}${practiceAreas.length > 0 ? ` · ${practiceAreas.length} work area${practiceAreas.length > 1 ? 's' : ''}` : ''}`
            : 'Team profile incomplete',
        },
        {
          label: 'Knowledge document loaded',
          pass: uploadedFiles.length > 0 || !!session.steps.docsCompleted,
          detail: uploadedFiles.length > 0
            ? `${uploadedFiles.length} uploaded file${uploadedFiles.length > 1 ? 's' : ''}`
            : 'No uploaded onboarding docs yet',
        },
      ];

      setTestResults(checks);

      if (checks.every((c) => c.pass)) {
        await persistSession({
          ...session,
          steps: { ...session.steps, testRunCompleted: true },
          notes: { ...session.notes, lastCompletedStep: 'test_run_pending', lastError: null },
        });
        setSuccess('Live test passed - tenant, integrations, profile, and onboarding docs are all in place.');
      } else {
        setError('Live test found setup gaps. Finish the failed checks and run it again.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to run live test');
    } finally {
      setTestRunning(false);
    }
  };

  // ── Staged trial flow (2026-08-04 owner directive) ─────────────────────
  // profile → apps (optional, in-flow OAuth) → team (optional) → workspace.
  // Every phase is announced by the JourneyStepper from the first screen, is
  // rendered as a PAGE in the same layout (never a popup over the previous
  // step), and is persisted on transition so a refresh resumes exactly here.
  // Task #53 (2026-08-12): the old fourth in-flow phase, 'orientation'
  // ("meet your workspace"), is gone — the user just MADE those choices, so
  // recapping them was pure friction. Finishing the last phase (or skipping
  // the optional ones) opens the workspace directly via launchWorkspace.
  type TrialPhase = 'profile' | 'apps' | 'team';
  const [trialPhase, setTrialPhase] = useState<TrialPhase>('profile');
  // Terms popup hygiene (2026-08-24, extends review 2026-08-23): the popup
  // belongs to the profile card (both its branches) — leaving the profile
  // phase must not leave it armed to pop back open on a later return.
  useEffect(() => {
    if (trialPhase !== 'profile') {
      setShowTermsModal(false);
      setTermsCopied(false);
    }
  }, [trialPhase]);
  const persistTrialPhase = useCallback(async (phase: TrialPhase | 'done') => {
    try {
      const user = getAuth().currentUser;
      if (!user) return;
      await setDoc(doc(getFirestore(), 'users', user.uid, 'onboarding', onboardingDocId),
        { trialPhase: phase, updatedAt: serverTimestamp() }, { merge: true });
    } catch { /* non-blocking — the phase still advances locally */ }
    // onboardingDocId dep: see persistSession (first-signup mode flips it).
  }, [onboardingDocId]);
  // Free stage navigation (task #56): back is always allowed; forward jumps
  // only to stages already reached (the save/continue buttons stay the only
  // way to advance into NEW territory, so validation is never skipped).
  const [visitedPhases, setVisitedPhases] = useState<TrialPhase[]>(['profile']);
  const advancePhase = (phase: TrialPhase) => {
    setTrialPhase(phase);
    setVisitedPhases((prev) => (prev.includes(phase) ? prev : [...prev, phase]));
    void persistTrialPhase(phase);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* noop */ }
  };
  // Revisit WITHOUT re-running saves — pure navigation between stages the
  // user has already seen; a refresh resumes on the revisited stage.
  const goToPhase = (phase: TrialPhase) => {
    if (!visitedPhases.includes(phase)) return;
    setTrialPhase(phase);
    void persistTrialPhase(phase);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* noop */ }
  };

  // Early optional asks (2026-08-04 owner directive: connecting apps and
  // inviting the team should feel optional and be ASKED on the setup page,
  // not sprung on the user two steps later). The profile card carries both
  // questions; a "later" answer fast-forwards past that step on save — the
  // step stays in the stepper (marked done) and everything remains reachable
  // from the workspace afterwards. null = unanswered, treated as yes.
  type EarlyAsk = 'yes' | 'later' | null;
  const [wantsApps, setWantsApps] = useState<EarlyAsk>(null);
  const [wantsTeam, setWantsTeam] = useState<EarlyAsk>(null);
  // 'finish' = nothing left to ask — open the workspace (task #53).
  const phaseAfterProfile = (): TrialPhase | 'finish' => {
    if (wantsApps !== 'later') return 'apps';
    if (wantsTeam !== 'later') return 'team';
    return 'finish';
  };

  // In-flow app connections: OAuth opens in a POPUP (the onboarding page never
  // navigates away) and a light poll flips the row to ✓ when the account
  // reports ACTIVE.
  const [connectingSlug, setConnectingSlug] = useState<string | null>(null);
  const connectPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startConnectPoll = useCallback(() => {
    if (connectPollRef.current) return;
    let ticks = 0;
    connectPollRef.current = setInterval(async () => {
      ticks += 1;
      await refreshConnectedApps();
      if (ticks >= 40 && connectPollRef.current) {
        clearInterval(connectPollRef.current);
        connectPollRef.current = null;
      }
    }, 3000);
  }, [refreshConnectedApps]);
  useEffect(() => () => {
    if (connectPollRef.current) clearInterval(connectPollRef.current);
  }, []);

  // ── Storage scope sheet (2026-08-11, PLAN-storage-folder-permissions §7.1;
  //    extended 2026-08-12 owner directive) ──
  // When the connect poll reports ANY toolkit newly ACTIVE, a post-connect
  // granular-controls card slides in ON THE SURFACE THE CONNECT STARTED FROM
  // (the in-flow section or the "more apps" modal — never the other one).
  // Every app gets the access-tier row (full access default / read + write /
  // read-only); STORAGE toolkits (drive/dropbox/box/…) additionally get the
  // folder question — only chosen folders (recommended) or everything.
  // Purely in-flow: no route change, no popup, no link out of onboarding.
  // "decide later" just dismisses — the connection stays on Everything +
  // full access server-side.
  interface ScopeSheetState {
    slug: string;
    /** Which surface initiated the connect — the sheet renders THERE only. */
    origin: 'flow' | 'modal';
    /** Storage apps get folder controls; every app gets the tier row. */
    storage: boolean;
    /** Multi-account (task #52): set = this sheet scopes a specific NEWLY
     *  ADDED connection; browse/save target it instead of the primary. */
    connectionId?: string;
    /** Access tier (2026-08-12): 'full' is the server default — kept
     *  selected + saved means NO access call is made. */
    tier: AccessTier;
    /** Fine-tuned five-class profile (2026-08-19, shared AppAccessSheet).
     *  Set only when the user opened fine-tune and toggled — undefined means
     *  the tier's lossless preset. */
    profile?: AccessProfile;
    /** Share-with-organization (2026-08-19 owner directive: one-click,
     *  default ON). Storage apps arrive server-auto-shared; the continue CTA
     *  applies only the DELTA vs sharedAtOpen. */
    share: boolean;
    sharedAtOpen: boolean;
    mode: 'folders' | 'all';
    /** Top-level folders from browseStorageFolders(slug, 'root'). */
    folders: StorageScopeFolder[];
    /** Checked folders keyed by id — enriched with the drill-down path
     *  (2026-08-20: subfolders are choosable at any depth via the shared
     *  FolderBrowser, so the human path matters for the chips + server). */
    selected: Record<string, StorageScopeFolder>;
    browse: 'loading' | 'ready' | 'error';
    saving: boolean;
    error: string | null;
  }
  const [scopeSheet, setScopeSheet] = useState<ScopeSheetState | null>(null);
  // Per-app chip shown on the connected row after a decision ("2 folders" /
  // "everything" / "read-only") — display-only; the server docs are the truth.
  const [scopeChips, setScopeChips] = useState<Record<string, string>>({});
  // Slugs whose in-flow connect started but whose post-connect question
  // hasn't been asked yet, mapped to the surface that initiated the connect.
  // Only slugs the user connected HERE ever queue — pre-existing connections
  // (grandfathered to Everything/full access) never re-prompt.
  const scopeAskQueueRef = useRef<Map<string, 'flow' | 'modal'>>(new Map());
  // Additive connects awaiting their NEW connection (task #52, all apps
  // since task #55): slug → the connection ids known at launch + the surface
  // the add started from. The drain effect diffs the live roster and reacts
  // to the one id that wasn't there before.
  const pendingAddRef = useRef<Map<string, { knownIds: string[]; origin: 'flow' | 'modal' }>>(new Map());
  // The live OAuth popup handle (2026-08-14 owner report: the sign-in window
  // stayed open after the connection completed, parked on whatever page the
  // provider redirect left it on). The drain effect closes it the moment the
  // poll sees the connection land; the callback landing's own self-close is
  // the second belt for flows this page didn't start.
  const connectPopupRef = useRef<Window | null>(null);
  // Sign-in-window-closed watcher cleanup (2026-08-20): the coach hides the
  // moment the user closes the OAuth window — the poll keeps running.
  const popupClosedWatchRef = useRef<(() => void) | null>(null);
  useEffect(() => () => { popupClosedWatchRef.current?.(); }, []);
  const closeConnectPopup = useCallback(() => {
    popupClosedWatchRef.current?.();
    popupClosedWatchRef.current = null;
    try {
      const w = connectPopupRef.current;
      if (w && !w.closed) w.close();
    } catch { /* popup may already be gone */ }
    connectPopupRef.current = null;
    // Cross-surface obligations (2026-08-19, engine parity with
    // runConnectPopupFlow): the moment a connection lands here, chat
    // grounding drops its stale cache and the assistant's capability flags
    // reconcile — the user can ask about the app on their very next turn.
    void import('../services/threadRecommendations/connectedAppsStore')
      .then((m) => m.invalidateConnectedAppsCache())
      .catch(() => { /* best-effort */ });
    void import('../services/prest0nSession/client')
      .then((m) => m.reconcilePrest0nConnections())
      .catch(() => { /* best-effort — the portal poll reconcile is the backstop */ });
  }, []);

  // Folder browse with retries (2026-08-14 owner report: "couldn't load your
  // folders" during onboarding). The sheet opens SECONDS after OAuth
  // completes, when the brand-new connection can still be settling — one
  // attempt was fragile by design. Two silent retries with backoff, and the
  // error note carries a live "try again" so the user is never dead-ended.
  const SCOPE_BROWSE_RETRY_DELAYS_MS = [1500, 3500];
  const loadScopeFolders = useCallback((slug: string, connectionId?: string) => {
    setScopeSheet((prev) =>
      prev && prev.slug === slug ? { ...prev, browse: 'loading' } : prev);
    const attempt = (i: number): void => {
      browseStorageFolders(slug, 'root', connectionId)
        .then((folders) => setScopeSheet((prev) =>
          prev && prev.slug === slug ? { ...prev, folders, browse: 'ready' } : prev))
        .catch(() => {
          if (i < SCOPE_BROWSE_RETRY_DELAYS_MS.length) {
            window.setTimeout(() => attempt(i + 1), SCOPE_BROWSE_RETRY_DELAYS_MS[i]);
          } else {
            setScopeSheet((prev) =>
              // Browse failure degrades gracefully: both radios stay, the
              // folders option is disabled with a retry note, "everything"
              // becomes the selection.
              prev && prev.slug === slug ? { ...prev, browse: 'error', mode: 'all' } : prev);
          }
        });
    };
    attempt(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openScopeSheet = useCallback((slug: string, opts?: { connectionId?: string; origin?: 'flow' | 'modal' }) => {
    const connectionId = opts?.connectionId;
    const storageApp = isStorageToolkit(slug);
    setScopeSheet({
      slug,
      origin: opts?.origin ?? 'flow',
      storage: storageApp,
      connectionId,
      tier: 'full',
      // Share-with-org default ON (2026-08-19). Storage connects are already
      // auto-shared at mint server-side (integrations.share holders), so the
      // checked box matches reality; non-storage shares on continue.
      share: true,
      sharedAtOpen: storageApp,
      // 'all' matches the server default, so save is NEVER disabled on
      // arrival (task #55 owner report: the old folders-mode default with
      // zero folders picked silently locked the save button).
      mode: 'all',
      folders: [],
      selected: {},
      browse: storageApp ? 'loading' : 'ready',
      saving: false,
      error: null,
    });
    // Non-storage apps have no folders to browse — the sheet is the
    // access-tier question alone.
    if (!storageApp) return;
    loadScopeFolders(slug, connectionId);
  }, [loadScopeFolders]);

  // Poll → sheet handoff: refreshConnectedApps (driven by startConnectPoll)
  // updates connectedApps; the first queued slug that lands opens the sheet
  // on ITS originating surface. One sheet at a time — the next queued app
  // waits for close, and the sheet never blocks further connects.
  useEffect(() => {
    if (scopeSheet) return;
    // Additive connects first: fire when the slug's roster gains an id that
    // wasn't known at launch — that id IS the newly added account. Storage
    // apps get the folder sheet for exactly that account; non-storage apps
    // have nothing per-connection to configure, so the row chip just starts
    // reflecting the signed-in identity/count.
    for (const [slug, pending] of Array.from(pendingAddRef.current.entries())) {
      const fresh = (connectionRostersRef.current[slug] || []).find((c) => !pending.knownIds.includes(c.id));
      if (fresh) {
        pendingAddRef.current.delete(slug);
        closeConnectPopup();
        if (isStorageToolkit(slug) && trialPhase === 'apps') {
          openScopeSheet(slug, { connectionId: fresh.id, origin: pending.origin });
        } else {
          const chip = rosterChip(slug);
          const count = (connectionRostersRef.current[slug] || []).length;
          setScopeChips((prev) => ({ ...prev, [slug]: chip || `${count} accounts` }));
        }
        return;
      }
    }
    if (scopeAskQueueRef.current.size === 0) return;
    const landed = connectedApps.find((s) => scopeAskQueueRef.current.has(s));
    if (!landed) return;
    const origin = scopeAskQueueRef.current.get(landed) ?? 'flow';
    scopeAskQueueRef.current.delete(landed);
    closeConnectPopup();
    // A connection that lands after the user moved past the apps step still
    // closes the popup (above) but never opens an invisible sheet — the
    // server defaults already apply, so the row just chips "full access"
    // (2026-08-19: the stuck-sheet fix's second half).
    if (trialPhase === 'apps') {
      openScopeSheet(landed, { origin });
    } else {
      const chip = rosterChip(landed);
      setScopeChips((prev) => ({ ...prev, [landed]: [chip, 'full access'].filter(Boolean).join(' · ') }));
    }
  }, [connectedApps, scopeSheet, trialPhase, openScopeSheet, closeConnectPopup, rosterChip]);

  // Leaving the apps step closes any open sheet and un-wedges the queue
  // (2026-08-19 fix: `scopeSheet` was plain state, never cleared on phase
  // change — one un-dismissed sheet early-returned the drain effect forever,
  // so no later sheet ever opened and the popup auto-close was unreachable).
  // The connection itself needs nothing: it is already ACTIVE on the server
  // defaults (Everything + full access).
  useEffect(() => {
    if (trialPhase !== 'apps') {
      setScopeSheet(null);
    }
  }, [trialPhase]);

  // Mobile reliability (2026-08-19): background tabs throttle timers, so the
  // 3s poll can sit paused while the user finishes OAuth in the popup/tab.
  // The moment this page is visible again, take one immediate poll tick if
  // any connect is still armed.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (scopeAskQueueRef.current.size > 0 || pendingAddRef.current.size > 0) {
        void refreshConnectedApps();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshConnectedApps]);

  // FolderBrowser hands the folder back enriched with its breadcrumb path —
  // toggling is a plain add/remove keyed by id.
  const toggleScopeFolder = (folder: StorageScopeFolder) => {
    setScopeSheet((prev) => {
      if (!prev) return prev;
      const selected = { ...prev.selected };
      if (selected[folder.id] !== undefined) delete selected[folder.id];
      else selected[folder.id] = folder;
      return { ...prev, selected };
    });
  };

  const saveScopeSheet = async () => {
    if (!scopeSheet || scopeSheet.saving) return;
    const { slug, storage: storageApp, mode, selected, connectionId, tier, profile, share, sharedAtOpen } = scopeSheet;
    const folders = Object.values(selected).map((f) => ({
      id: f.id,
      name: f.name,
      ...(f.path ? { path: f.path } : {}),
    }));
    if (storageApp && mode === 'folders' && folders.length === 0) return; // button is disabled; belt & braces
    setScopeSheet({ ...scopeSheet, saving: true, error: null });
    try {
      if (storageApp) {
        await setStorageScope(
          slug,
          mode === 'folders' ? { mode: 'folders', folders } : { mode: 'all' },
          connectionId,
        );
      }
      // Access (2026-08-12; profiles 2026-08-19): the server default IS full
      // access — only a narrower pick POSTs. A fine-tuned profile that
      // matches no preset saves as a custom profile.
      const customProfile = profile && tierFromProfile(profile) === null ? profile : null;
      if (customProfile) {
        await setAccessProfile(slug, customProfile);
      } else if (tier !== 'full') {
        await setAccessTier(slug, tier);
      }
      // Share-with-organization delta (2026-08-19 owner directive: default
      // ON, one click). Non-fatal: a member without integrations.share gets
      // a server 403 — the connection itself is unaffected. Draft modes
      // target the org being CREATED; if the early mint hasn't landed yet,
      // the share queues on the draft and launchWorkspace replays it.
      if (share !== sharedAtOpen) {
        const shareTargetId = connectionId || connectionRostersRef.current[slug]?.[0]?.id;
        if (shareTargetId) {
          if (draftMode && !mintedOrgAccountId && share) {
            try {
              const u = getAuth().currentUser;
              if (u) {
                await setDoc(doc(getFirestore(), 'users', u.uid, 'onboarding', 'pending_org'), {
                  queuedShares: arrayUnion({ connectionId: shareTargetId, slug }),
                  updatedAt: serverTimestamp(),
                }, { merge: true });
              }
            } catch { /* best-effort belt */ }
          } else {
            try {
              await shareConnection(shareTargetId, share, draftMode ? mintedOrgAccountId ?? undefined : undefined);
            } catch { /* permission-gated server-side; never blocks the funnel */ }
          }
        }
      }
      const accountCount = connectionRostersRef.current[slug]?.length || 0;
      const identityChip = rosterChip(slug);
      const tierChip = tier === 'read-write' ? 'read + write' : tier === 'read-only' ? 'read-only' : '';
      setScopeChips((prev) => ({
        ...prev,
        // An additive save labels the row by WHO is signed in — the folder
        // count belongs to ONE of the accounts, so echoing it would mislead.
        [slug]: storageApp
          ? [
              connectionId && accountCount > 1
                ? (identityChip || `${accountCount} accounts`)
                : mode === 'folders'
                  ? `${folders.length} folder${folders.length === 1 ? '' : 's'}`
                  : 'everything',
              tierChip,
            ].filter(Boolean).join(' · ')
          : [identityChip, tierChip || 'full access'].filter(Boolean).join(' · '),
      }));
      setScopeSheet(null);
    } catch (e) {
      // Keep the sheet open with the error — the user's picks are intact.
      setScopeSheet((prev) => (prev && prev.slug === slug
        ? { ...prev, saving: false, error: e instanceof Error ? e.message : 'could not save — try again' }
        : prev));
    }
  };

  // "decide later" — dismiss only. NO server call and NO blocking state: the
  // connection stays usable on Everything + full access server-side; the row
  // chip says so.
  const dismissScopeSheet = () => {
    if (!scopeSheet) return;
    const accountCount = connectionRostersRef.current[scopeSheet.slug]?.length || 0;
    const identityChip = rosterChip(scopeSheet.slug);
    setScopeChips((prev) => ({
      ...prev,
      [scopeSheet.slug]: !scopeSheet.storage
        ? [identityChip, 'full access'].filter(Boolean).join(' · ')
        : scopeSheet.connectionId && accountCount > 1
          ? (identityChip || `${accountCount} accounts`)
          : 'everything',
    }));
    setScopeSheet(null);
  };

  // Popup-blocker fallback (task #53, owner report: "the one click sign in
  // window never popped up"): when the browser refuses the popup, the minted
  // sign-in URL lands here and renders as a plain link — a direct click on a
  // link is never popup-blocked.
  const [manualConnect, setManualConnect] = useState<{ slug: string; url: string } | null>(null);

  // Honest CTA copy (2026-09-02): a tile whose app has no one-click sign-in
  // must not say "connect" and then open something else. Falls back to
  // "connect" while the catalog is still cold — the click still routes
  // correctly, it just can't promise the lane in advance.
  const connectCtaLabel = (slug: string): string => {
    const lane = peekConnectLane(slug);
    return lane === 'unknown' ? 'connect' : laneCtaLabel(lane);
  };

  const connectAppInFlow = async (slug: string, opts?: { additional?: boolean; origin?: 'flow' | 'modal' }) => {
    setConnectingSlug(slug);
    setManualConnect(null);
    // Shared page error state (profile save, team save, docs, connect all
    // write it) — a new attempt must not sit under a stale line.
    setError(null);
    // Lane check BEFORE any popup (2026-09-02): only ~122 of the catalog's
    // ~1,448 toolkits have Composio-managed auth. For the rest the OAuth
    // mint cannot work, and opening the window first is what produced the
    // reported symptom — a sign-in window that appears, vanishes, and leaves
    // an error banner. DocuSign is in 11 of the 18 industry recommendation
    // lists, so this ran on the tiles we recommend hardest.
    const lane = peekConnectLane(slug);
    if (lane !== 'unknown' && !laneIsDirect(lane)) {
      setConnectingSlug(null);
      // 'none' is a no_auth toolkit: opening a sign-in for an app that has
      // none would be its own dead end. Say so and stop.
      if (lane === 'none') {
        setError(laneExplainer(lane, prettyIntegration(slug)));
        return;
      }
      await routeToBrowserLane({
        appName: prettyIntegration(slug),
        toolkitSlug: slug,
        surface: 'onboarding',
        // Concierge first (2026-08-18 owner directive: every non-direct
        // connection goes through the chat interface from any surface) —
        // the same handshake this step's own "another application…" row
        // uses; it falls back to the in-page sign-in when no chat is up.
        chatFirst: true,
        ...(draftMode && mintedOrgAccountId ? { workspaceAccountId: mintedOrgAccountId } : {}),
      });
      return;
    }
    // Explain the "Composio" consent screen BEFORE the popup can confuse
    // anyone (task #46) — large panel beside where the popup opens.
    setCoachApp(prettyIntegration(slug));
    if (opts?.additional) {
      // Add-another-account (task #52; ALL apps since task #55 — Gmail and
      // Calendar included, owner report 2026-08-14): remember the ids we
      // already know so the drain effect can spot the NEW connection.
      // Storage apps get a folder sheet for exactly that account;
      // non-storage just updates the account-count chip (the access tier
      // is app-wide and already chosen).
      pendingAddRef.current.set(slug, {
        knownIds: (connectionRostersRef.current[slug] || []).map((c) => c.id),
        origin: opts?.origin ?? 'flow',
      });
    } else {
      // EVERY app connected here queues the post-connect granular-controls
      // ask (2026-08-12 owner directive) — storage apps add folder controls,
      // all apps get the access-tier row. The sheet opens once the poll sees
      // the connection land, ON THE SURFACE the connect started from.
      scopeAskQueueRef.current.set(slug, opts?.origin ?? 'flow');
    }
    // RELIABILITY (task #53, mechanics now in the SHARED engine —
    // src/services/composio/connectPopupFlow.ts, 2026-08-18): the popup
    // opens SYNCHRONOUSLY inside the click's call stack (window.open runs
    // before the engine's first await) and is navigated once the OAuth URL
    // arrives; opener severed manually; pinned top-left so the coach panel
    // points at it (task #47).
    // Popup handshake (task #52): the callback page sees this mark and
    // renders a minimal "connected — close this window" landing instead of
    // the full Integrations page (which auto-opened a SECOND folder ask
    // inside the popup — the "asked me twice" report). The engine re-marks
    // identically — this call keeps the handshake visible at the surface.
    markConnectPopupLaunch(slug, 'onboarding');
    // Additive connects also hand the engine the known ids (2026-08-19,
    // parity with IntegrationsPanel): the engine re-marks the add-account
    // handshake, so the manual-link fallback keeps the roster diff too.
    const launch = await launchConnectPopup({
      toolkitSlug: slug,
      surface: 'onboarding',
      ...(opts?.additional
        ? { addAccount: { knownIds: (connectionRostersRef.current[slug] || []).map((c) => c.id) } }
        : {}),
      // Draft modes (2026-08-19): the storage auto-share must land on the
      // org being CREATED — the creator's back-pointer still points at their
      // previous workspace until the completion switch.
      ...(draftMode && mintedOrgAccountId ? { shareTo: mintedOrgAccountId } : {}),
    });
    connectPopupRef.current = launch.popup;
    // The coach's job ends with the sign-in window (2026-08-20): closing it
    // hides the panel right away — the connect poll keeps running, because
    // users routinely close the window right after consenting.
    popupClosedWatchRef.current?.();
    popupClosedWatchRef.current = watchPopupClosed(launch.popup, () => setCoachApp(null));
    if (launch.status === 'unsupported') {
      // The server saw what a cold catalog could not: no one-click sign-in
      // for this app. Not an error — hand it to Prest0n's browser lane.
      setCoachApp(null);
      pendingAddRef.current.delete(slug);
      scopeAskQueueRef.current.delete(slug);
      setConnectingSlug(null);
      if (launch.lane === 'none') {
        setError(laneExplainer('none', prettyIntegration(slug)));
        return;
      }
      await routeToBrowserLane({
        appName: prettyIntegration(slug),
        toolkitSlug: slug,
        surface: 'onboarding',
        chatFirst: true,
        ...(draftMode && mintedOrgAccountId ? { workspaceAccountId: mintedOrgAccountId } : {}),
      });
      return;
    }
    if (launch.status === 'error') {
      setCoachApp(null);
      // A launch that never started leaves nothing to drain — drop the
      // queued asks so a later, unrelated connect does not inherit them
      // (they were written optimistically before the mint).
      pendingAddRef.current.delete(slug);
      scopeAskQueueRef.current.delete(slug);
      const e = launch.error as { code?: string; fields?: Array<{ displayName?: string; name?: string }> } | undefined;
      const app = prettyIntegration(slug);
      if (e?.code === 'missing_fields') {
        // The app CAN connect, but it wants a setup value first (WhatsApp's
        // WABA id is the one in the recommended lists). Onboarding has no
        // setup form — the chat concierge does this properly, and it is the
        // sanctioned path for every non-one-click connection. Its own copy
        // takes over when a chat is mounted; the line below is the fallback.
        const asked = (e.fields || [])
          .map((f) => f.displayName || f.name)
          .filter(Boolean)
          .join(', ');
        const { requestConnectConcierge } = await import('../services/composio/connectCustomFlow');
        if (requestConnectConcierge(app)) {
          setConnectingSlug(null);
          return;
        }
        setError(
          asked
            ? `${app} asks for one setup detail first (${asked}) — you can finish it on the integrations page after setup.`
            : `${app} asks for a setup detail before its sign-in can start — you can finish it on the integrations page after setup.`
        );
      } else {
        // Never the raw server sentence (it named the slug and "Composio").
        // D9: deterministic failure copy, house voice.
        setError(`${app} did not open its sign-in. you can try again, or connect it later on the integrations page.`);
      }
    } else {
      if (launch.status === 'blocked' && launch.redirectUrl) {
        // Popup blocked (even after the engine's retry): offer the link.
        setManualConnect({ slug, url: launch.redirectUrl });
      }
      startConnectPoll();
    }
    setConnectingSlug(null);
  };

  // Chip clouds collapse to the relevant subset; "show all" expands (copy-diet).
  // (Capabilities no longer need one — they render as themed GROUPS, task #53.)
  const [showAllAreas, setShowAllAreas] = useState(false);
  // "more apps" modal (task #43): the full one-click catalog, opened on
  // demand — never auto-opened, never a navigation out of the guided flow.
  const [showAllApps, setShowAllApps] = useState(false);
  // "another application…" opener (2026-08-23): the recommended group's
  // first card — type a name, connect. DIRECT-FIRST (owner directive
  // 2026-08-24): as the user types, the same tiered matcher the
  // integrations omnisearch uses surfaces one-click direct connectors from
  // the live catalog — the default path is always the direct connection;
  // Prest0n's browser sign-in is the explicit fallback for everything else.
  const [showCustomAppPrompt, setShowCustomAppPrompt] = useState(false);
  const [customAppName, setCustomAppName] = useState('');
  // Composio coach (task #46): the loud, right-anchored explainer shown while
  // a connection popup is open. Holds the app label; null = hidden.
  const [coachApp, setCoachApp] = useState<string | null>(null);
  // Full catalog for the "more apps" modal (task #46): the SAME live toolkit
  // list the integrations page renders, loaded lazily the first time the
  // modal opens so the onboarding step stays fast.
  const [allToolkits, setAllToolkits] = useState<ComposioToolkit[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogQuery, setCatalogQuery] = useState('');

  // Grouped, searchable catalog for the modal (task #46). Live toolkits from
  // Composio when loaded (grouped by their own category metadata, exactly
  // like the integrations page); the curated TOOLKIT_CATEGORIES map is the
  // fallback so the modal is never empty if the API is slow or down.
  // slug → live toolkit meta (logo + display name) so catalog rows carry the
  // same icons the integrations page shows. Empty until the live list lands;
  // rows fall back to a gold letter avatar, so nothing waits on the network.
  const catalogMeta = useMemo(() => {
    const m = new Map<string, { logo?: string; name?: string }>();
    for (const tk of allToolkits) {
      m.set(tk.slug.toLowerCase(), { logo: tk.meta?.logo, name: tk.name });
    }
    return m;
  }, [allToolkits]);

  // Direct-connector matches for the "connect another application" popup
  // (2026-08-24): same tiered matcher as the integrations omnisearch, over
  // the live catalog the modal already loaded. Empty while the catalog is
  // still loading/down — the browser lane stays the honest fallback.
  const customAppMatches = useMemo(() => {
    const q = customAppName.trim();
    if (q.length < 2 || allToolkits.length === 0) return [] as ComposioToolkit[];
    return searchToolkits(q, allToolkits, { limit: 4 }).map((r) => r.tk);
  }, [customAppName, allToolkits]);

  const { catalogGroups, catalogMatchTotal, catalogTruncated } = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    const matches = (slug: string, name?: string, description?: string) =>
      !q ||
      slug.toLowerCase().includes(q) ||
      (name || '').toLowerCase().includes(q) ||
      (description || '').toLowerCase().includes(q);

    // Pinned "recommended for you" group (owner feedback 2026-08-11; one
    // source 2026-08-19): the model-ranked catalogRecommendations when the
    // recs have landed, else the deterministic per-industry list from the
    // profile step's own chips. Filtered to apps the catalog actually has,
    // list order preserved (it IS the rank). Hidden while searching — a
    // query means the user already knows what they want.
    const availableSlugs = new Set(
      allToolkits.length > 0
        ? allToolkits.map((tk) => tk.slug.toLowerCase())
        : Object.values(TOOLKIT_CATEGORIES).flat(),
    );
    const haystack = `${instantFirmProfile?.industry || ''} ${instantFirmProfile?.focus || ''} ${practiceAreas.join(' ')}`;
    // TOP 12 (2026-08-23 owner directive) — both sources now carry 12; the
    // slice is the honesty belt against older caches.
    const ranked = (recs?.catalogRecommendations?.length
      ? recs.catalogRecommendations
      : rankedIndustryApps(practiceAreas, haystack)
    ).slice(0, 12);
    // The group survives an empty ranked list (review 2026-08-23): its first
    // card is the owner-directed "another application…" opener, which must
    // not disappear for exactly the users who got no recommendations.
    const recommendedGroup = q
      ? []
      : [{
          category: 'recommended for you',
          slugs: ranked.filter((slug) => availableSlugs.has(slug)),
        }];

    let groups: Array<{ category: string; slugs: string[] }>;
    if (allToolkits.length > 0) {
      // POPULARITY order, same model as the Integrations page (2026-08-23
      // owner directive: never alphabetical): toolkits map into the curated
      // workflow families via familyForToolkit, families render in
      // TOOLKIT_CATEGORIES' hand-ranked order (validated against Okta's
      // Businesses at Work adoption data — email/chat, calendar, documents,
      // signatures, CRM, billing lead), 'more apps' trails, and apps inside
      // each family sort most-used-first via sortByLikelihood.
      const byFamily = new Map<string, ComposioToolkit[]>();
      for (const tk of allToolkits) {
        if (!matches(tk.slug, tk.name, tk.meta?.description)) continue;
        const family = familyForToolkit(tk);
        const list = byFamily.get(family) ?? [];
        list.push(tk);
        byFamily.set(family, list);
      }
      const familyOrder = [...Object.keys(TOOLKIT_CATEGORIES), MORE_APPS_FAMILY];
      groups = [
        ...recommendedGroup,
        ...familyOrder
          .map((family) => ({
            category: family,
            slugs: Array.from(new Set(
              (byFamily.get(family) ?? []).sort(sortByLikelihood).map((tk) => tk.slug.toLowerCase()),
            )),
          }))
          .filter((g) => g.slugs.length > 0),
      ];
    } else {
      groups = [
        ...recommendedGroup,
        ...Object.entries(TOOLKIT_CATEGORIES)
          .map(([category, slugs]) => ({ category, slugs: slugs.filter((slug) => matches(slug)) }))
          .filter((g) => g.slugs.length > 0),
      ];
    }

    // 60-result cap (2026-08-16, governance plan §8 Phase 3): the catalog is
    // ~1,209 toolkits — unbounded rows would swamp the modal, so rendering
    // stops at 60 and a note prompts a narrower search. Groups keep their
    // order; the cap slices across them cumulatively.
    const CATALOG_RESULT_CAP = 60;
    const total = groups.reduce((n, g) => n + g.slugs.length, 0);
    if (total <= CATALOG_RESULT_CAP) {
      return { catalogGroups: groups, catalogMatchTotal: total, catalogTruncated: false };
    }
    const capped: typeof groups = [];
    let used = 0;
    for (const g of groups) {
      if (used >= CATALOG_RESULT_CAP) break;
      const take = g.slugs.slice(0, CATALOG_RESULT_CAP - used);
      used += take.length;
      capped.push({ category: g.category, slugs: take });
    }
    return { catalogGroups: capped, catalogMatchTotal: total, catalogTruncated: true };
  }, [allToolkits, catalogQuery, instantFirmProfile, practiceAreas, recs]);

  // Load the full catalog from the PROFILE step on (2026-08-19), so the icons
  // are warm before any app row renders. REWRITTEN 2026-08-20 (owner report:
  // every onboarding surface lettered in production): the previous shape
  // listed `catalogLoading` as a dependency AND set it inside the effect,
  // with a per-run `cancelled` cleanup — so the setState re-render ran the
  // cleanup before the fetch resolved, the fetched catalog was DISCARDED,
  // and `catalogLoading` wedged at true, blocking every future attempt. Only
  // a warm cache (e.g. a prior Integrations visit) resolved fast enough to
  // escape, which is exactly why testers saw logos and fresh signups saw
  // letters. Now: an in-flight REF guard (never re-renders, never re-runs the
  // effect), an unmount-scoped ref instead of per-run cancellation (a late
  // result is still applied — the component is the same), and a patient
  // retry ladder that re-arms on the next phase change after exhaustion.
  const catalogFetchInFlightRef = useRef(false);
  const catalogUnmountedRef = useRef(false);
  // The body RESET matters (review 2026-08-20): StrictMode's dev-only
  // mount → cleanup → remount keeps the same refs, so a cleanup-only effect
  // would leave the flag stuck true and dev sessions would discard every
  // fetched catalog — the exact wedge this rewrite removes.
  useEffect(() => {
    catalogUnmountedRef.current = false;
    return () => { catalogUnmountedRef.current = true; };
  }, []);
  useEffect(() => {
    if ((trialPhase !== 'profile' && trialPhase !== 'apps' && !showAllApps) || allToolkits.length > 0) return;
    if (catalogFetchInFlightRef.current) return;
    catalogFetchInFlightRef.current = true;
    setCatalogLoading(true);
    const CATALOG_RETRY_DELAYS_MS = [5000, 15000];
    const attempt = (i: number): void => {
      listToolkits()
        .then((tks) => {
          if (catalogUnmountedRef.current) return;
          if (tks.length > 0) {
            setAllToolkits(tks);
            setCatalogLoading(false);
          } else if (i < CATALOG_RETRY_DELAYS_MS.length) {
            window.setTimeout(() => attempt(i + 1), CATALOG_RETRY_DELAYS_MS[i]);
          } else {
            // Curated fallback still renders; the guard re-arms so the next
            // phase change (or opening the modal) tries again.
            catalogFetchInFlightRef.current = false;
            setCatalogLoading(false);
          }
        })
        .catch(() => {
          if (catalogUnmountedRef.current) return;
          if (i < CATALOG_RETRY_DELAYS_MS.length) {
            window.setTimeout(() => attempt(i + 1), CATALOG_RETRY_DELAYS_MS[i]);
          } else {
            catalogFetchInFlightRef.current = false;
            setCatalogLoading(false);
          }
        });
    };
    attempt(0);
  }, [trialPhase, showAllApps, allToolkits.length]);

  // Coach auto-hide (task #46): once the app actually connects, the panel has
  // done its job — drop it without the user needing to dismiss.
  useEffect(() => {
    if (!coachApp) return;
    const slug = connectedApps.find((s) => prettyIntegration(s) === coachApp);
    if (slug) setCoachApp(null);
  }, [connectedApps, coachApp]);


  // 'link' (task #42): the invitation was created but the email could not be
  // delivered (e.g. email service unconfigured) — the row offers a copyable
  // invite link instead of pretending the email went out.
  interface InviteRow { email: string; name: string; roleId?: string; status: 'idle' | 'sending' | 'sent' | 'link' | 'error'; error?: string; acceptUrl?: string }
  const [inviteRows, setInviteRows] = useState<InviteRow[]>([
    { email: '', name: '', status: 'idle' },
    { email: '', name: '', status: 'idle' },
    { email: '', name: '', status: 'idle' },
  ]);
  const [inviteSending, setInviteSending] = useState(false);
  // Role choice during onboarding invites (owner directive 2026-08-12,
  // parity with the Team & Roles invite modal): each row carries a role,
  // defaulting to the standard staff role so the common case stays
  // type-an-email-and-send. Roles may not be seeded yet on a brand-new
  // account — the picker simply hides and the server default applies.
  // Owner directive 2026-08-12: the workspace must be ready the moment
  // onboarding ends. Kick the (idempotent, best-effort) VM pre-provision on
  // onboarding mount so the 30–90s first boot overlaps the minutes spent on
  // the profile/apps/team steps instead of the owner's first message.
  // New-org mode NEVER warms a VM — a created org has no VM until hired
  // (task #75), and the uid VM belongs to the user's existing workspace.
  // Owner directive 2026-08-18: also RE-fire on the setup-profile step —
  // the mount-time warm 403s when the trial doc isn't stamped yet (the
  // latch no longer burns on failure), and landing on the profile step is
  // the earliest point the trial is reliably live. By the aha-moment the
  // workstation — and computer use — is that much closer to ready.
  // Draft modes warm the ORGANIZATION'S VM (owner directive 2026-08-23,
  // replacing "draft modes never warm"): the org's accountId is reserved
  // server-side the moment the user arrives — no legal name needed — and the
  // idempotent provision starts against it, so the workstation (and the
  // in-onboarding browser sign-in that runs on it) is ready by the time the
  // user asks. The later legal-name mint reuses the SAME id, so this VM is
  // the organization's VM, minutes early. A "just me" fork simply abandons
  // the reservation; the idle sweep is the cost bound.
  // D1 (2026-08-24 owner directive): the ambient uid-plane warms that used to
  // fire here (mount + profile step) are retired — personal workspaces run
  // serverless and never mint machines. Only the draft-ORG warm below remains:
  // workstations bind to organizations.
  useEffect(() => {
    if (!draftMode) return;
    // Gated on `loading` too (review 2026-08-23): on a cold ?neworg=1 load
    // this effect runs before Firebase auth hydrates, the reserve no-ops on a
    // null currentUser, and — with [draftMode] already true — it would never
    // retry that page-load, silently skipping the warm entirely. The
    // bootstrap flipping `loading` false is the auth-ready signal.
    if (loading) return;
    void reserveAndWarmDraftOrgVm().then((orgId) => {
      if (orgId) setDraftReservedOrgId(orgId);
    });
  }, [draftMode, loading]);

  const [inviteRoleOptions, setInviteRoleOptions] = useState<Array<{ roleId: string; name: string; permCount: number; description: string }>>([]);
  const [defaultInviteRoleId, setDefaultInviteRoleId] = useState<string | undefined>(undefined);
  // Raw role docs for the inline role editor (task #55) — the SAME
  // RoleEditorModal the members & roles section renders, mounted in-flow.
  const [inviteRolesRaw, setInviteRolesRaw] = useState<any[]>([]);
  const [roleEditorTarget, setRoleEditorTarget] = useState<'new' | any | null>(null);
  // "+ create a custom role…" from a row's select (2026-08-19): remember the
  // row so the freshly minted role is assigned to it on save.
  const pendingCustomRoleRowRef = useRef<number | null>(null);
  // "see what each role can do ▸" expander (2026-08-19 owner directive:
  // permission detail must be EASY but optional — no decision fatigue).
  const [roleDetailOpen, setRoleDetailOpen] = useState(false);
  const [roleDetailRoleId, setRoleDetailRoleId] = useState<string | null>(null);
  const reloadInviteRoles = useCallback(() => {
    listRoles()
      .then((data) => {
        const active = ((data.roles as any[]) ?? []).filter((r) => r.status === 'active');
        setInviteRolesRaw(active);
        const options = active
          // Owner is not an invitable role (2026-08-19): a workspace has ONE
          // owner, and offering it in a five-slot invite dropdown was a
          // one-click privilege hand-off. Admin stays — that's a real choice.
          .filter((r) => r.templateKey !== 'owner')
          .map((r) => ({
            roleId: (r.roleId ?? r.id) as string,
            name: (r.name ?? r.roleId ?? r.id) as string,
            permCount: (r.effectivePermissions?.length ?? r.grants?.length ?? 0) as number,
            // Same description Team & Roles shows (system roles stay synced
            // to the templates server-side; custom roles may leave it blank).
            description: (typeof r.description === 'string' ? r.description : '') as string,
          }));
        setInviteRoleOptions(options);
        const byKey = (key: string) =>
          active.find((r) => r.templateKey === key || r.roleId === key || r.slug === key);
        const rec = byKey('associate') ?? byKey('member') ?? byKey('paralegal');
        // Custom workspaces without any standard-member role still need a
        // concrete default — a controlled <select> with value '' rendered
        // blank and the row silently fell back to the server default
        // (review 2026-08-13). First active role wins in that case.
        const recId = (rec?.roleId ?? rec?.id ?? options[0]?.roleId) as string | undefined;
        setDefaultInviteRoleId(recId);
        if (recId) {
          setInviteRows((rows) => rows.map((row) => (row.roleId ? row : { ...row, roleId: recId })));
        }
      })
      .catch(() => { /* picker hides; server default applies */ });
  }, []);
  useEffect(() => {
    if (trialPhase !== 'team') return;
    reloadInviteRoles();
  }, [trialPhase, reloadInviteRoles]);
  // Which row's "copy invite link" just fired (transient "copied ✓" feedback).
  const [copiedInviteIdx, setCopiedInviteIdx] = useState<number | null>(null);
  const inviteEmailValid = (e: string) => /\S+@\S+\.\S+/.test(e.trim());

  const updateInviteRow = (i: number, patch: Partial<InviteRow>) => {
    setInviteRows(rows => rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  };

  const sendTeamInvites = async () => {
    const pending = inviteRows
      .map((r, i) => ({ ...r, i }))
      // 'link' rows are already-created invitations (email undeliverable) —
      // re-sending would mint a duplicate invite and burn a seat.
      .filter(r => r.status !== 'sent' && r.status !== 'link' && r.email.trim());
    if (pending.length === 0) { void launchWorkspace(); return; }
    // Draft modes QUEUE the invites — the organization doesn't exist yet;
    // completion mints it and sends these with the new accountId (task #75;
    // first-signup company drafts share the exact same contract).
    if (draftMode) {
      try {
        const u = getAuth().currentUser;
        if (u) {
          await setDoc(doc(getFirestore(), 'users', u.uid, 'onboarding', 'pending_org'), {
            queuedInvites: pending
              .filter((r) => inviteEmailValid(r.email))
              .map((r) => ({ email: r.email.trim(), name: r.name.trim() || null, roleId: r.roleId || null })),
          }, { merge: true });
        }
      } catch { /* the team stage stays reachable post-mint from Team & Roles */ }
      void launchWorkspace();
      return;
    }
    setInviteSending(true);
    await Promise.allSettled(pending.map(async (row) => {
      if (!inviteEmailValid(row.email)) {
        updateInviteRow(row.i, { status: 'error', error: 'invalid email' });
        return;
      }
      updateInviteRow(row.i, { status: 'sending', error: undefined });
      try {
        const res = await createInvitation({
          email: row.email.trim(),
          displayName: row.name.trim() || undefined,
          // Explicit role parity with the Team & Roles invite modal
          // (2026-08-12). Absent → the server resolves its standard default.
          ...(row.roleId ? { roleIds: [row.roleId] } : {}),
        });
        // Truthful outcome (task #42): only claim "sent" when the email
        // actually left. Otherwise surface the invite link so the owner can
        // deliver it themselves — the invitation itself is live either way.
        if (res.emailSent === false && res.acceptUrl) {
          updateInviteRow(row.i, { status: 'link', acceptUrl: res.acceptUrl });
        } else {
          // Email went out — keep the link anyway (task #44): the owner can
          // always hand the invite link directly, sent email or not.
          updateInviteRow(row.i, { status: 'sent', acceptUrl: res.acceptUrl });
        }
      } catch (e: any) {
        updateInviteRow(row.i, { status: 'error', error: String(e?.message || 'failed to send').slice(0, 80) });
      }
    }));
    setInviteSending(false);
  };

  // "let's go →" pending state (2026-08-09 owner clarity pass): the button
  // previously did nothing visible while persistTrialPhase awaited + the
  // hard reload started — the final submission of onboarding must react
  // instantly like every other one.
  const [launching, setLaunching] = useState(false);
  // A failed sealed transition (new-org launch below) releases the overlay —
  // re-enable the button so the creator can retry the switch (the org itself
  // was already minted; retrying just re-runs the verified switch).
  useEffect(() => subscribeWorkspaceTransition((s) => {
    if (!s) setLaunching(false);
  }), []);
  const launchWorkspace = async () => {
    setLaunching(true);
    // ── First-signup "just me" (2026-08-19 split): the target is the
    // PERSONAL uid-keyed workspace. Activate it (structure + uniform trial,
    // no VM in-callable), replay the draft's answers through the exact
    // session-mode writes, mark the session done, then warm the VM and land
    // on /desktop — the boot tile owns the wait, chat works immediately.
    if (firstSignupMode && draftTargetKind === 'personal') {
      try {
        const { activatePersonalWorkspace: activate } = await import('../services/rbacService');
        await activate();
        const u = getAuth().currentUser;
        if (u) {
          await setDoc(doc(getFirestore(), 'users', u.uid, 'onboarding', 'session'), {
            profile: {
              firmName,
              practiceAreas,
              firmSize,
              contactPhone,
              selectedCapabilities,
              onboardedAt: serverTimestamp(),
            },
            steps: { billingComplete: true, slackConnected: false, configCompleted: true, docsCompleted: true, testRunCompleted: true },
            state: 'complete',
            trialPhase: 'done',
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          }, { merge: true });
        }
        await writeUidWorkspaceOnboardingArtifacts();
        try { clearWorkspaceOptionsCache(); } catch { /* revalidates on load */ }
        // D1: no VM warm — the personal workspace is serverless-only.
        window.location.href = `${window.location.origin}/desktop`;
      } catch (e) {
        setLaunching(false);
        setError(e instanceof Error ? e.message : 'could not set up your workspace — try again');
      }
      return;
    }
    if (newOrgMode || (firstSignupMode && draftTargetKind === 'company')) {
      // ── Completion mint (task #75): the organization comes into existence
      // HERE and only here — server-verified (creator's own onboarding
      // complete + attested draft), owner = creator, first-created-org
      // founding credit decided server-side. Then: send the queued invites
      // with the REAL accountId, switch into the new workspace, and land on
      // the subscription page (hire-required) or the workspace (founding).
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const createOrg = httpsCallable(getFunctions(undefined, 'us-central1'), 'createOrganizationWorkspace');
        const result: any = await createOrg({});
        const accountId = result?.data?.accountId;
        const posture = result?.data?.posture;
        if (!accountId) throw new Error('mint returned no organization');
        // Queued team invites → real invitations on the new org (owner
        // permissions exist via the founder bootstrap). Non-fatal per row.
        try {
          const u = getAuth().currentUser;
          if (u) {
            const draftSnap = await getDoc(doc(getFirestore(), 'users', u.uid, 'onboarding', 'pending_org'));
            const queued: any[] = Array.isArray((draftSnap.data() as any)?.queuedInvites)
              ? (draftSnap.data() as any).queuedInvites : [];
            await Promise.allSettled(queued.map((q) => createInvitation({
              accountId,
              email: String(q?.email || ''),
              displayName: q?.name || undefined,
              ...(q?.roleId ? { roleIds: [q.roleId] } : {}),
            } as any)));
            // Queued connection shares (2026-08-19): connects that landed
            // before the early mint replay onto the REAL org now.
            const queuedShares: any[] = Array.isArray((draftSnap.data() as any)?.queuedShares)
              ? (draftSnap.data() as any).queuedShares : [];
            await Promise.allSettled(queuedShares.map((q) =>
              q?.connectionId ? shareConnection(String(q.connectionId), true, accountId) : Promise.resolve(null)));
          }
        } catch { /* invites stay sendable from Team & Roles */ }
        // Fresh switcher so the sealed controller's roster read (and the
        // reloaded page) see the new org. The verified switch itself now
        // runs INSIDE beginWorkspaceTransition below (2026-08-20: this
        // launch used to bare-switch + navigate with no overlay — the one
        // org change with no announcement; a failed switch silently landed
        // on /desktop in the wrong workspace).
        try { clearWorkspaceOptionsCache(); } catch { /* revalidates on load */ }
        // EVERY posture gets its VM (owner decision 2026-08-19 — one
        // organization means at least one VM; reverses the no-VM-until-hired
        // half of hire-first). Usually a no-op: the early mint at profile
        // save already kicked it and this re-kick hits the idempotent
        // already-exists path. Fire-and-forget either way.
        try {
          const token = await getAuthToken();
          if (token) {
            void fetch(fnUrl('provisionUserVM'), {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ accountId }),
            }).catch(() => undefined);
          }
        } catch { /* the /desktop boot poller + wake path are the safety net */ }
        // Consumed-draft marker (2026-08-19): the resume/reset gates key on
        // launchCompleted — an early-minted draft WITHOUT it resumes; with
        // it, a fresh ?neworg=1 starts the next org.
        try {
          const u2 = getAuth().currentUser;
          if (u2) {
            await setDoc(doc(getFirestore(), 'users', u2.uid, 'onboarding', 'pending_org'), {
              launchCompleted: true,
              updatedAt: serverTimestamp(),
            }, { merge: true });
          }
        } catch { /* non-fatal */ }
        // hire_required keeps its hire-first landing (/subscription — the
        // desktop now works when they visit it, with the hire CTA alongside);
        // founding + trial land on /desktop, whose boot tile owns any
        // remaining wait (2026-08-19: founding used to route to '/' because
        // it had no VM — it has one now). SEALED (2026-08-20): the same
        // transition overlay every other switch shows — verified switch +
        // arrival markers + hard navigation, all inside the controller.
        beginWorkspaceTransition({
          targetAccountId: accountId,
          targetLabel: (firmName || '').trim() || undefined,
          uid: getAuth().currentUser?.uid || null,
          navigation: 'desktop',
          navigationPath: posture === 'hire_required'
            ? `/subscription?account=${encodeURIComponent(accountId)}`
            : undefined,
        });
      } catch (e) {
        setLaunching(false);
        setError(e instanceof Error ? e.message : 'could not create the organization — try again');
      }
      return;
    }
    try {
      // Mark the staged flow finished so a later /onboarding visit starts at
      // the profile summary instead of resuming a completed phase.
      await persistTrialPhase('done');
    } catch {
      // Non-fatal — /desktop resumes gracefully; never strand the button.
    }
    // LAND IN THE ORGANIZATION (owner report 2026-08-25, live run): a new
    // account that founded an organization must finish onboarding INSIDE
    // that organization — not on the personal workspace the session
    // happened to start on. The code lane mints the org at redemption, so
    // by here the membership back-pointer already names it; switching
    // through the same sealed transition every other switch uses (verified
    // switch + arrival markers + hard navigation) makes the arrival honest.
    // No org (organic "just me") → the personal workspace stays the home.
    try {
      const u = getAuth().currentUser;
      if (u) {
        const mem = await getDoc(doc(getFirestore(), 'users', u.uid, 'account_membership', 'main'));
        const pointed = (mem.data() as Record<string, unknown> | undefined)?.accountId;
        if (typeof pointed === 'string' && pointed.startsWith('co_')) {
          beginWorkspaceTransition({
            targetAccountId: pointed,
            targetLabel: (firmName || '').trim() || undefined,
            uid: u.uid,
            navigation: 'desktop',
          });
          return;
        }
      }
    } catch { /* fall through to the plain landing below */ }
    // Instant workspace (2026-08-02, owner directive): chat is served from
    // the cloud and works the moment the profile is saved — nothing needs to
    // block here. /desktop itself renders an honest "your workstation is
    // being built" tile (with a live boot poller) until the VM passes its
    // health probes, so every entry path shares one boot experience instead
    // of a 5-minute lock modal guarding only this button.
    window.location.href = `${window.location.origin}/desktop`;
  };

  // App-wide trial rule (2026-08-14 incident): client-free / activated
  // accounts carry residual trial flags (e.g. Fernando: plan 'trial',
  // is_client_free true) but must NEVER be walked through the trial staged
  // flow. This page used to test the raw flags directly — the one surface
  // that ignored is_client_free — so landing here (redirect carry-over,
  // bookmark, account switch) read as "being taken down the onboarding
  // process". isTrialSubscription is the single source of truth.
  const isTrial = isTrialSubscription(subscription);

  /**
   * The uid-keyed workspace's onboarding artifacts — profile write-through,
   * accounts/{uid}/onboarding/profile (the ONE onboarding profile doc; since
   * P0-B1 of the memory-system program, 2026-09-07, it also carries the
   * summary / note / starterPrompts that used to live in a companion
   * memory/onboarding_profile doc — the memory collection now holds only
   * server-written memory rows), and the personalized welcome + starters
   * on config/runtime. Extracted (2026-08-19) so the
   * first-signup "just me" launch can REPLAY the draft's answers onto the
   * freshly activated personal workspace through the exact same writes the
   * session-mode flow performs. Draft modes never call this until launch.
   */
  const writeUidWorkspaceOnboardingArtifacts = async () => {
    const user = getAuth().currentUser;
    if (!user) return;
    const db = getFirestore();
    const onboardingProfile = {
      firmName,
      practiceAreas,
      firmSize,
      contactPhone,
      selectedCapabilities,
      creditAmountCents: subscription?.trial_credit_cents || 0,
      onboardedAt: serverTimestamp(),
    };
    // Phase 0 (2026-08-15): the answers chosen here must SHOW UP on the
    // account profile page — write them through (merge-if-empty; a user
    // edit on /profile always wins over auto-fill).
    // Names come from the Firebase identity (display name) — the same
    // source the auth context parses. First token / remainder split.
    // The typed full name (identity source of record, 2026-08-25) beats the
    // auth displayName split — email/password signups have no displayName
    // and the legacy fabricated class must be overwritten, not merged around.
    const nameParts = (fullName.trim() || user.displayName || '').trim().split(/\s+/).filter(Boolean);
    void writeThroughProfile({
      firstName: nameParts[0],
      lastName: nameParts.slice(1).join(' '),
      phone: contactPhone,
      orgName: firmName,
      teamSizeLabel: firmSize,
    });
    // Perfect initial conditions (2026-08-04 owner directive): the FIRST
    // thing the user sees in the workspace chat — message AND one-click
    // starter chips — is built from their invite's firm profile + these
    // onboarding answers. useTenantChatConfig reads both fields off
    // accounts/{uid}/config/runtime with top precedence. Computed first —
    // the starters ride onto BOTH docs below.
    const creditCents = subscription?.trial_credit_cents || 0;
    // Same stale-connect filter as refreshInitialConditions: never suggest
    // connecting an app that is already connected at write time.
    const starterPrompts = deriveStarterPrompts(recs, selectedCapabilities)
      .filter((p) => !isStaleConnectSuggestion(p, connectedApps));
    const welcomeText = buildPersonalizedWelcome(firmName, practiceAreas, starterPrompts, creditCents);

    // accounts/{uid}/onboarding/profile — the ONE onboarding profile doc: the
    // VM, the server-side branding + invite resolvers and the welcome hook
    // all read it. P0-B1 (2026-09-07, PLAN-prest0n-memory-system-20260905
    // §12): the legacy companion doc memory/onboarding_profile is retired —
    // the persistent-context summary, the greeting note and the starter
    // prompts it carried now ride HERE, so accounts/{id}/memory holds only
    // server-written memory rows (write-locked by P0-A2; read-locked by
    // P0-B3). Merge keeps needsWelcome intact for the welcome hook.
    const practiceStr = practiceAreas.length > 0 ? practiceAreas.join(', ') : 'General';
    const capsStr = selectedCapabilities.length > 0 ? selectedCapabilities.join(', ') : 'General';
    const accountRef = doc(db, 'accounts', user.uid, 'onboarding', 'profile');
    await setDoc(accountRef, {
      ...onboardingProfile,
      onboardedAt: serverTimestamp(),
      needsWelcome: true,
      summary: `This account belongs to ${firmName || 'an unnamed team'}. Work areas: ${practiceStr}. Team size: ${firmSize || 'not specified'}. Capabilities selected: ${capsStr}. Trial credit: $${(creditCents / 100).toFixed(0)}.`,
      starterPrompts,
      note: 'Always greet the user by their company or team name and reference their work areas and selected capabilities when relevant. Their preferred first tasks are in starterPrompts.',
    }, { merge: true });

    const runtimeRef = doc(db, 'accounts', user.uid, 'config', 'runtime');
    await setDoc(runtimeRef, {
      chatWelcomeMessage: welcomeText,
      ...(starterPrompts.length > 0 ? { welcomeStarters: starterPrompts } : {}),
      welcomeGeneratedAt: serverTimestamp(),
      // Distinguish the onboarding refinement from the redemption-time
      // 'invite-code-seed' (server writes the seed the moment a
      // firm-profile code is redeemed; this overwrite is the richer copy).
      welcomeSource: 'onboarding',
    }, { merge: true });
  };

  const saveTrialProfile = async () => {
    if (!firmName) return;
    // Unified confirm (2026-08-24): no organization onboarding saves without
    // the representation + terms attestation; protected names never save.
    if (!companyAttested || isProtectedOrgName(firmName)) return;
    // Owner directives 2026-08-25: the real full name is required, and a
    // draft may never take a name the user's existing workspaces hold.
    if (!fullName.trim()) return;
    if (draftMode && isDuplicateWorkspaceName(firmName)) return;
    setTrialSaving(true);
    setError(null);
    try {
      const tenantId = session?.tenantId || effectiveTenantId;
      if (tenantId && !draftMode) {
        await updateTenantProfile(tenantId, { firmName, practiceAreas });
      }
      const user = getAuth().currentUser;
      if (user) {
        const db = getFirestore();
        // Identity persistence (2026-08-25): the typed full name becomes the
        // account's displayName (real source, replacing the email-fabricated
        // class) and lands on users/{uid} for every server-side surface —
        // including "[first name]'s Workspace" personal-row labeling.
        const typedFullName = fullName.trim().replace(/\s+/g, ' ');
        if (typedFullName && typedFullName !== (user.displayName || '').trim()) {
          try { await updateProfile(user, { displayName: typedFullName }); } catch { /* non-fatal */ }
        }
        try {
          await setDoc(doc(db, 'users', user.uid), {
            full_name: typedFullName,
            first_name: typedFullName.split(' ')[0] || '',
          }, { merge: true });
        } catch { /* non-fatal — auth displayName is the primary source */ }
        const onboardingProfile = {
          fullName: typedFullName,
          firmName,
          practiceAreas,
          firmSize,
          contactPhone,
          selectedCapabilities,
          creditAmountCents: subscription?.trial_credit_cents || 0,
          onboardedAt: serverTimestamp(),
          // Unified confirm record (2026-08-24): the save gate requires the
          // representation + terms checkbox on every path, so stamp the
          // attestation the same way the organic confirm card does.
          attestedAuthorized: true,
          attestedAt: serverTimestamp(),
          tosAgreedAt: serverTimestamp(),
        };
        // Draft modes skip EVERY org #1 / personal write — the draft doc is
        // the only record until the launch mints/activates the target
        // (task #75; first-signup split 2026-08-19).
        if (!draftMode) {
          // Phase 1: a new account starting a new organization founds it here
          // — same answers fill BOTH personal profile and the org record (the
          // overlap the owner called out). Idempotent; fire-and-forget.
          // (Legacy session mode only — draft launches never touch org_{uid}.)
          // The user's edited name is AUTHORITATIVE (owner report 2026-08-24:
          // the invite-seeded label survived a profile-setup rename until a
          // manual workspace rename). ensureOrganization founds/returns the
          // native org, then the governed rename propagates the chosen name
          // through every identity mirror — organizations doc, profile/main,
          // accounts root, users root, subscription account_label, and the
          // live workspace_index fan-out. Live trials always take the light
          // (immediate) rename policy; 'unchanged' is the common no-op.
          // TRIAL-CODE LANE FIX (owner report 2026-08-25, live run): this
          // used to rename user.uid unconditionally — the PERSONAL workspace
          // — so a code redeemer's typed company name landed on their
          // personal row while the organization the code actually founded
          // kept the email-derived placeholder ("milestothe"). The company
          // name belongs to the COMPANY workspace: when the account already
          // holds a created organization (the code lane mints it at
          // redemption, before the user has typed a name), rename THAT and
          // never found a native org over the top of it.
          void (async () => {
            let orgAccountId: string | null = null;
            try {
              const mem = await getDoc(doc(db, 'users', user.uid, 'account_membership', 'main'));
              const pointed = (mem.data() as Record<string, unknown> | undefined)?.accountId;
              if (typeof pointed === 'string' && pointed.startsWith('co_')) orgAccountId = pointed;
            } catch { /* resolved again below */ }
            // CODE-LANE ORDERING (2026-08-27, Whitfield live incident): when
            // the redemption-time mint died (observed: request-deadline kill),
            // the back-pointer holds no co_ yet at profile save. The old
            // fallback then renamed the PERSONAL plane — and when the org
            // finally minted, the duplicate wall saw the company name "taken"
            // and knocked the org down to a placeholder. A redeemed trial on
            // the account means the org is PENDING, never absent: run the
            // idempotent server mint NOW and rename what it returns. The
            // native lane below stays for genuinely org-less organic
            // accounts only.
            if (!orgAccountId) {
              try {
                const userSnap = await getDoc(doc(db, 'users', user.uid));
                const hasCodeTrial = Boolean((userSnap.data() as any)?.trial);
                if (hasCodeTrial) {
                  const token = await user.getIdToken();
                  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
                  const resp = await fetch(`https://us-central1-${projectId}.cloudfunctions.net/ensureTrialOrgWorkspace`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ data: {} }),
                  });
                  const body = await resp.json().catch(() => null);
                  const r = body?.result ?? body;
                  if (resp.ok && typeof r?.accountId === 'string' && r.accountId.startsWith('co_')) {
                    orgAccountId = r.accountId;
                  }
                }
              } catch { /* org stays pending — the /desktop self-heal retries */ }
            }
            if (orgAccountId) {
              await renameWorkspaceOrganization(orgAccountId, firmName.trim());
              return;
            }
            await ensureOrganization({
              name: firmName,
              teamSizeLabel: firmSize,
              createdFrom: 'trial-onboarding',
            });
            await renameWorkspaceOrganization(user.uid, firmName.trim());
          })().catch(() => undefined);
        }
        // Save to user's onboarding session (or the draft in draft modes)
        const ref = doc(db, 'users', user.uid, 'onboarding', onboardingDocId);
        await setDoc(ref, {
          profile: onboardingProfile,
          steps: { billingComplete: true, slackConnected: false, configCompleted: true, docsCompleted: true, testRunCompleted: true },
          state: 'complete',
          updatedAt: serverTimestamp(),
        }, { merge: true });
        if (!draftMode) {
          await writeUidWorkspaceOnboardingArtifacts();
        }
        // Early mint + eager VM at the PROFILE step (2026-08-19): a company
        // draft has its legalName + attestation by the time save is
        // clickable, so the org can exist NOW and its VM can spend the
        // apps/team stretch booting from the golden image. Personal ("just
        // me") first-signup targets deliberately stay launch-time: an early
        // personal activation would stamp uid trial state and poison the
        // company-trial eligibility check if the user goes back and confirms
        // a company after all.
        if (draftMode && (newOrgMode || draftTargetKind === 'company')) {
          void mintDraftOrgEarly();
        }
        // Phone verification text, RIGHT AWAY (2026-08-19 owner report: "I
        // put my phone number in and didn't receive a verification text").
        // Same idiom as Profile.tsx's saved-change hook: a user-submitted
        // number + the disclosure under the field = express consent; the
        // server (decideSmsVerification) skips already-confirmed numbers
        // and NEVER texts an opted-out one. Fire-and-forget by contract.
        if (contactPhone.trim()) {
          void import('../services/smsVerificationClient')
            .then((m) => m.requestSmsVerification(contactPhone.trim()))
            .catch(() => { /* never blocks the funnel; the banner re-offers */ });
        }
      }
      // Next phase honors the up-next choices made on this card: "later"
      // answers fast-forward past the optional steps (still reachable from
      // the workspace afterwards).
      const next = phaseAfterProfile();
      if (next === 'finish') {
        // Both optional steps deferred — setup is done; open the workspace.
        await launchWorkspace();
        return;
      }
      advancePhase(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save profile');
    } finally {
      setTrialSaving(false);
    }
  };

  /**
   * SETUP-AWARE INITIAL CONDITIONS (2026-08-04 owner directive: the first
   * suggested queries must reflect what the account ACTUALLY has connected
   * and who joined — not just the firm profile). Called when the user leaves
   * the apps and team phases: re-derives the starter chips with prompts for
   * each connected app (and a team routine when invites went out), rewrites
   * the welcome bubble to match, and records the live setup facts in the
   * memory doc so every future greeting/suggestion pass knows them too.
   * Non-fatal by contract — the flow always advances.
   */
  const refreshInitialConditions = useCallback(async (extra?: { invitedCount?: number }) => {
    try {
      // Draft modes must NEVER touch the creator's uid-keyed account docs:
      // these writes would replace org #1's welcome/starters/memory with the
      // NEW org's identity (review finding 1; first-signup drafts have no
      // launched workspace yet). The mint/activation seeds the real docs.
      if (draftMode) return;
      const user = getAuth().currentUser;
      if (!user) return;
      const db = getFirestore();
      const creditCents = subscription?.trial_credit_cents || 0;
      // Drop "Connect my X" starters for apps ALREADY connected — the rec
      // model writes them before the apps phase, and re-suggesting a live
      // connection reads as Preston not knowing his own setup.
      const base = deriveStarterPrompts(recs, selectedCapabilities)
        .filter((p) => !isStaleConnectSuggestion(p, connectedApps));
      const appPrompts = APP_STARTERS
        .filter(([slug]) => isAppConnected(slug))
        .map(([, prompt]) => prompt);
      const teamPrompts = (extra?.invitedCount || 0) > 0
        ? ['Set up a shared weekly status routine for the team']
        : [];
      // Connected-app prompts lead (they exercise the user's real setup),
      // then the team routine, then the firm-profile starters; dedupe, cap 6.
      const starters = Array.from(new Set([...appPrompts, ...teamPrompts, ...base])).slice(0, 6);
      const welcomeText = buildPersonalizedWelcome(firmName, practiceAreas, starters, creditCents);
      await setDoc(doc(db, 'accounts', user.uid, 'config', 'runtime'), {
        chatWelcomeMessage: welcomeText,
        ...(starters.length > 0 ? { welcomeStarters: starters } : {}),
        welcomeGeneratedAt: serverTimestamp(),
        welcomeSource: 'onboarding',
      }, { merge: true });
      // The setup facts (connected apps, invited teammates, refined
      // starters) ride the same onboarding/profile doc — P0-B1 (2026-09-07)
      // retired the memory/onboarding_profile companion; merge never touches
      // needsWelcome.
      await setDoc(doc(db, 'accounts', user.uid, 'onboarding', 'profile'), {
        connectedApps,
        ...(typeof extra?.invitedCount === 'number' ? { invitedTeammates: extra.invitedCount } : {}),
        starterPrompts: starters,
        setupNote: `Connected apps: ${connectedApps.length > 0 ? connectedApps.join(', ') : 'none yet'}.`
          + ((extra?.invitedCount || 0) > 0 ? ` Teammates invited during setup: ${extra?.invitedCount}.` : '')
          + ' Prefer suggestions that use what is actually connected.',
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch { /* non-fatal — the flow always advances */ }
  }, [draftMode, subscription, recs, selectedCapabilities, connectedApps, firmName, practiceAreas]);

  const trialCreditDollars = subscription?.trial_credit_cents
    ? (subscription.trial_credit_cents / 100).toFixed(2)
    : null;

  const trialExpiresDate = (() => {
    if (!subscription?.trial_expires_at) return null;
    const raw = subscription.trial_expires_at as any;
    const exp: Date = raw?.toDate?.() ?? (typeof raw?._seconds === 'number' ? new Date(raw._seconds * 1000) : new Date(raw));
    return isNaN(exp.getTime()) ? null : exp;
  })();

  const trialDaysRemaining = trialExpiresDate
    ? Math.max(0, Math.ceil((trialExpiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const trialEndsOn = trialExpiresDate
    ? trialExpiresDate.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : null;
  const hasActiveBilling = Boolean(
    subscription && (
      ['active', 'trialing'].includes(subscription.status) ||
      isTrial
    )
  );

  const progress = useMemo(() => {
    if (!session) return 0;
    const values = Object.values(session.steps);
    const complete = values.filter(Boolean).length;
    return Math.round((complete / values.length) * 100);
  }, [session]);

  if (loading) {
    // Branded bootstrap (2026-08-09): wordmark + live spinner + clear copy —
    // the previous text-only page didn't read as "something is happening."
    return (
      <BrandedTransition
        variant="page"
        title="loading your onboarding…"
        subtitle="pulling your subscription, workspace, and saved progress."
      />
    );
  }

  // A creator who still owes their own trial onboarding cannot start a new
  // org — route them to their setup instead of a draft the mint would refuse
  // at the very last click (task #75 review findings 4/14/17).
  if (newOrgMode && newOrgBlocked) {
    return (
      <Page>
        <TopBar>
          <BrandWordmark href="/" />
          <TopBarSignOut type="button" onClick={handleSignOut}>sign out</TopBarSignOut>
        </TopBar>
        <Wrap>
          <Card>
            <CardTitle>finish your own setup first</CardTitle>
            <SectionText>
              complete your onboarding, then start the new organization from the
              workspace switcher — it&apos;s this exact same flow, run once for the
              new org.
            </SectionText>
            <ButtonRow>
              <PrimaryButton onClick={() => { window.location.assign('/onboarding'); }}>
                continue my setup →
              </PrimaryButton>
            </ButtonRow>
          </Card>
        </Wrap>
      </Page>
    );
  }

  // ── Trial users: staged flow — profile → apps → team → workspace ──
  // One layout for every phase (stepper rail + action card), no popups, no
  // off-page navigation; each phase is announced, optional ones say so.
  if ((isTrial && hasActiveBilling) || draftMode) {
    const allInvitesSent = inviteRows.every(r => !r.email.trim() || r.status === 'sent');
    const anyInviteSent = inviteRows.some(r => r.status === 'sent');
    // Model recs win; otherwise the profile step's own industry chips rank
    // the rows (2026-08-19) — the flat gmail/gcal/gdrive trio is only the
    // no-signal floor inside rankedIndustryApps.
    const recommendedSlugs = (recs?.recommendedIntegrations?.length
      ? recs.recommendedIntegrations
      : rankedIndustryApps(
          practiceAreas,
          `${instantFirmProfile?.industry || ''} ${instantFirmProfile?.focus || ''} ${practiceAreas.join(' ')}`,
        )).slice(0, 5);
    // A browser-lane connection counts here too (2026-09-03): a user who
    // signed into Oura through Prest0n's browser must stop being recommended it.
    const connectedRecommended = recommendedSlugs.filter(slug => isAppConnected(slug));
    // Scope sheet save gate (2026-08-11 §7.1): folders mode needs ≥1 pick.
    // Non-storage sheets (access tier only, 2026-08-12) can always continue.
    const scopeSaveDisabled = !scopeSheet
      || scopeSheet.saving
      || (scopeSheet.storage && scopeSheet.mode === 'folders' && Object.keys(scopeSheet.selected).length === 0);

    // ── post-connect sheet (render; 2026-08-11 §7.1 folders; 2026-08-12
    //    tiers; 2026-08-19 shared AppAccessSheet + share row + ONE CTA) ──
    // ONE sheet definition, TWO anchors: it renders in-flow when the connect
    // started from the recommended tiles and INSIDE the "more apps" modal
    // when the connect started there — never both. Every app gets the shared
    // access sheet (presets + fine-tune); storage toolkits additionally get
    // the folder question; every app gets the share-with-org toggle (default
    // on). An in-flow card, never an overlay or a route change. Leaving the
    // step just dismisses — the connection is already fully active on the
    // server defaults (everything + full access).
    // Which signed-in account is this sheet about? Additive sheets target the
    // fresh connection; first-connect sheets the primary (2026-08-19 owner
    // directive: every access-controls view names the app AND the account).
    const sheetRoster = connectionRostersRef.current[scopeSheet?.slug || ''] || [];
    const sheetIdentity = scopeSheet?.connectionId
      ? sheetRoster.find((c) => c.id === scopeSheet.connectionId)?.identity
      : sheetRoster[0]?.identity;
    const sheetLogo = scopeSheet ? catalogMeta.get(scopeSheet.slug)?.logo : undefined;

    const renderScopeSheetCard = () => scopeSheet && (
      <ScopeSheetCard data-testid="storage-scope-sheet">
        {/* App icon + headline — the same logo the rows and the integrations
            page use, so the user always sees WHICH app they're configuring. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
          <IntegrationIcon
            logo={sheetLogo}
            name={prettyIntegration(scopeSheet.slug)}
            size={28}
            style={{ marginTop: '0.05rem' }}
          />
          <span style={{ minWidth: 0 }}>
            {scopeSheet.storage ? (
              <span style={{ display: 'block', fontSize: '0.9rem', fontWeight: 700, color: 'var(--brand-text)', textTransform: 'lowercase' }}>
                what should <KeepCase>Prest0n</KeepCase> use in your {scopeSheet.connectionId ? 'newly added ' : ''}{prettyIntegration(scopeSheet.slug)}?
              </span>
            ) : (
              <span style={{ display: 'block', fontSize: '0.9rem', fontWeight: 700, color: 'var(--brand-text)', textTransform: 'lowercase' }}>
                connected — <KeepCase>Prest0n</KeepCase> has full access to {prettyIntegration(scopeSheet.slug)}.
              </span>
            )}
            {sheetIdentity && (
              <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 600, color: '#857F73', overflowWrap: 'anywhere' }}>
                {sheetIdentity}
              </span>
            )}
          </span>
        </div>
        <div style={{ fontSize: '0.76rem', color: '#857F73', textTransform: 'lowercase', lineHeight: 1.5 }}>
          full access by default — change it now or anytime in integrations.
        </div>
        {scopeSheet.connectionId && (
          <div style={{ fontSize: '0.76rem', color: '#857F73', textTransform: 'lowercase', lineHeight: 1.5 }}>
            these folders come from the account you just added — your other {prettyIntegration(scopeSheet.slug)} keeps its own settings.
          </div>
        )}

        {/* TWO questions, visibly separate (task #55 owner report: the tier
            radios and the folder radios rendered as ONE flat five-option
            list). 2026-08-19: question 1 is now the SHARED AppAccessSheet
            (governance plan §6, one component on every surface) — presets +
            optional fine-tune, edited locally and applied by the single
            continue CTA below. */}
        <div style={{
          fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.06em',
          color: 'var(--brand-gold-hover)', marginTop: '0.15rem',
        }}>
          {scopeSheet.storage ? '1 · what can Prest0n do here?' : 'what can Prest0n do here?'}
        </div>
        <div style={{
          border: '1px solid #ECEAE5', borderRadius: '0.75rem',
          padding: '0.45rem 0.5rem', background: '#fff',
        }}>
          <AppAccessSheet
            slug={scopeSheet.slug}
            displayName={prettyIntegration(scopeSheet.slug)}
            access={{
              tier: scopeSheet.tier,
              profile: scopeSheet.profile ?? profileFromTier(scopeSheet.tier),
              ...(scopeSheet.profile && tierFromProfile(scopeSheet.profile) === null ? { custom: true } : {}),
            }}
            classCounts={null}
            classSummaries={CAPABILITY_CLASS_SUMMARIES}
            canManage
            saving={scopeSheet.saving}
            onSelectTier={(tier) => setScopeSheet((prev) => (prev ? { ...prev, tier, profile: undefined } : prev))}
            onSaveProfile={(profile) => setScopeSheet((prev) => {
              if (!prev) return prev;
              const preset = tierFromProfile(profile);
              return preset ? { ...prev, tier: preset, profile: undefined } : { ...prev, profile };
            })}
          />
        </div>

        {scopeSheet.storage && (
          <>
            <div style={{
              fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.06em',
              color: 'var(--brand-gold-hover)', marginTop: '0.2rem',
            }}>
              2 · which files can Prest0n see?
            </div>
            <div style={{
              display: 'grid', gap: '0.55rem', border: '1px solid #ECEAE5',
              borderRadius: '0.75rem', padding: '0.65rem 0.8rem', background: '#fff',
            }}>
            <label style={{ display: 'flex', gap: '0.55rem', alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="radio"
                name="storage-scope-mode"
                checked={scopeSheet.mode === 'all'}
                onChange={() => setScopeSheet({ ...scopeSheet, mode: 'all' })}
                style={{ marginTop: '0.15rem' }}
              />
              <span style={{ fontSize: '0.85rem', color: 'var(--brand-text)', textTransform: 'lowercase', lineHeight: 1.45 }}>
                <strong>everything in my {prettyIntegration(scopeSheet.slug)}</strong>
                <span style={{ display: 'block', color: '#857F73', fontSize: '0.78rem', lineHeight: 1.5 }}>(default)</span>
              </span>
            </label>
            <label style={{ display: 'flex', gap: '0.55rem', alignItems: 'flex-start', cursor: scopeSheet.browse === 'error' ? 'not-allowed' : 'pointer' }}>
              <input
                type="radio"
                name="storage-scope-mode"
                checked={scopeSheet.mode === 'folders'}
                disabled={scopeSheet.browse === 'error'}
                onChange={() => setScopeSheet({ ...scopeSheet, mode: 'folders' })}
                style={{ marginTop: '0.15rem' }}
              />
              <span style={{ fontSize: '0.85rem', color: scopeSheet.browse === 'error' ? '#9A948A' : 'var(--brand-text)', textTransform: 'lowercase', lineHeight: 1.45 }}>
                <strong>only folders i choose</strong>{' '}
                <span style={{ color: '#857F73' }}>— for tighter control</span>
                {scopeSheet.browse === 'error' && (
                  <span style={{ display: 'block', color: '#9A948A', fontSize: '0.78rem', lineHeight: 1.5 }}>
                    couldn't load your folders just now —{' '}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        loadScopeFolders(scopeSheet.slug, scopeSheet.connectionId);
                      }}
                      style={{
                        background: 'none', border: 'none', padding: 0, fontFamily: 'inherit',
                        fontSize: '0.78rem', fontWeight: 700, color: 'var(--brand-gold-hover)',
                        cursor: 'pointer', textDecoration: 'underline', textTransform: 'lowercase',
                      }}
                    >
                      try again
                    </button>
                    {' '}— or pick folders anytime in integrations.
                  </span>
                )}
              </span>
            </label>

            {scopeSheet.mode === 'folders' && scopeSheet.browse === 'loading' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#857F73', fontSize: '0.82rem', paddingLeft: '1.6rem' }}>
                <Spinner $size={16} /> loading your folders…
              </div>
            )}
            {scopeSheet.mode === 'folders' && scopeSheet.browse === 'ready' && (
              <div style={{ display: 'grid', gap: '0.4rem', paddingLeft: '1.6rem' }}>
                {/* The SAME drill-down browser the integrations picker uses
                    (FolderBrowser, 2026-08-20 owner directive): open any
                    folder and choose subfolders at any depth — breadcrumbs,
                    per-level filter, "shared with you" section, selection
                    chips. The root listing is seeded from the sheet's own
                    retry-belted load so nothing is fetched twice. */}
                <FolderBrowser
                  appSlug={scopeSheet.slug}
                  appDisplayName={prettyIntegration(scopeSheet.slug)}
                  connectionId={scopeSheet.connectionId}
                  selected={scopeSheet.selected}
                  onToggle={toggleScopeFolder}
                  initialRootListing={scopeSheet.folders}
                />
                <div style={{ color: '#857F73', fontSize: '0.76rem', lineHeight: 1.5, textTransform: 'lowercase' }}>
                  each folder includes everything inside it, now and later; refine anytime in integrations.
                </div>
              </div>
            )}

            </div>
          </>
        )}

        {/* Share with the organization — ONE tap, default on (2026-08-19
            owner directive). Storage apps arrive already shared (server
            auto-share at mint); the continue CTA applies only the delta. */}
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.55rem',
          border: '1px solid #ECEAE5', borderRadius: '0.75rem',
          padding: '0.6rem 0.8rem', background: '#fff', cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={scopeSheet.share}
            onChange={() => setScopeSheet((prev) => (prev ? { ...prev, share: !prev.share } : prev))}
            style={{ accentColor: 'var(--brand-gold)', marginTop: '0.15rem' }}
            data-testid="scope-sheet-share-org"
          />
          <span style={{ fontSize: '0.82rem', color: 'var(--brand-text)', textTransform: 'lowercase', lineHeight: 1.45 }}>
            <strong>share with your organization</strong>
            <span style={{ display: 'block', color: '#857F73', fontSize: '0.76rem', lineHeight: 1.5 }}>
              teammates work through this connection with what their role allows — data &amp; access are
              customized per role in team &amp; roles, and members get limited or special access. adjust
              anytime in integrations.
            </span>
          </span>
        </label>

        {scopeSheet.error && (
          <div style={{ color: '#B42318', fontSize: '0.8rem' }}>{scopeSheet.error}</div>
        )}

        {/* ONE decision, one button (2026-08-19): "done — continue" applies
            only the deltas (narrower access, folder picks, share toggle).
            The old defer link is gone — the connection is already fully
            active on the server defaults, so there was never a second
            decision to defer; leaving the step or closing the modal still
            just dismisses. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => { void saveScopeSheet(); }}
            disabled={scopeSaveDisabled}
            style={{
              background: 'var(--brand-gold)', color: '#fff', border: 'none', borderRadius: '999px',
              padding: '0.45rem 1.1rem', fontFamily: 'inherit', fontSize: '0.8rem',
              fontWeight: 600, cursor: 'pointer', textTransform: 'lowercase',
              opacity: scopeSaveDisabled ? 0.55 : 1,
            }}
          >
            {scopeSheet.saving ? 'saving…' : 'done — continue'}
          </button>
          <span style={{ fontSize: '0.74rem', color: '#9A948A', textTransform: 'lowercase' }}>
            {scopeSaveDisabled && !scopeSheet.saving
              ? 'pick at least one folder to save — or switch back to everything.'
              : 'you can change any of this anytime in integrations.'}
          </span>
        </div>
      </ScopeSheetCard>
    );
    // ── end storage scope sheet ──

    const RAIL: Record<typeof trialPhase, { title: string; sub?: string }> = {
      profile: { title: 'quick profile setup', sub: 'saved as you go — pick up where you left off anytime.' },
      apps: { title: 'connect your apps', sub: 'optional — everything happens right here.' },
      team: { title: 'invite your team', sub: 'optional — your workspace is built for teams.' },
    };

    // Dynamic step count (task #53): deferring an optional step REMOVES it
    // from the journey strip — "later" means fewer steps, visibly, not a
    // step that lingers as an obligation.
    const journeySteps = NEW_ACCOUNT_JOURNEY.filter((s) =>
      !(s.key === 'apps' && wantsApps === 'later') &&
      !(s.key === 'team' && wantsTeam === 'later'));
    // The strip is the navigator (task #56): visited in-onboarding stages
    // are one click away; code/account live on other pages and stay inert.
    const navigableStepKeys = journeySteps
      .map((s) => s.key)
      .filter((k) => k !== trialPhase && (visitedPhases as string[]).includes(k));

    const visibleAreas = showAllAreas
      ? DEFAULT_PRACTICE_OPTIONS
      : DEFAULT_PRACTICE_OPTIONS.filter((a, i) => practiceAreas.includes(a) || i < 8);
    const hiddenAreaCount = DEFAULT_PRACTICE_OPTIONS.length - visibleAreas.length;

    return (
      <Page>
        <SeoHead
          title="workspace setup — prest0"
          description="Finish setting up your prest0 workspace."
          noIndex={true}
        />
        <TopBar>
          <BrandWordmark href="/" />
          <TopBarSignOut type="button" onClick={handleSignOut}>sign out</TopBarSignOut>
        </TopBar>
        <Wrap>
          <TrialGrid>
            <TrialRail>
              <JourneyStepper
                steps={journeySteps}
                currentKey={trialPhase}
                railOnDesktop
                navigableKeys={navigableStepKeys}
                onStepClick={(k) => goToPhase(k as TrialPhase)}
              />
              <Hero>
                <Eyebrow>prest0 evaluation</Eyebrow>
                <RailTitle>{RAIL[trialPhase].title}</RailTitle>
                {RAIL[trialPhase].sub && <Subtitle>{RAIL[trialPhase].sub}</Subtitle>}
                {/* New-org drafts never show org #1's trial numbers — the
                    expectations for the NEW organization go here instead. */}
                {newOrgMode ? (
                  <div style={{ marginTop: '0.85rem', fontSize: '0.85rem', color: '#5A5346', lineHeight: 1.55 }}>
                    setting up a new organization. your first created organization comes with a
                    $200 founding credit — applied to your wallet with your first payment (or
                    right away if you already have an active plan). after that, each new
                    organization hires its Prest0n right after setup — everything you set up
                    here is saved either way.
                  </div>
                ) : trialCreditDollars ? (
                  <StatusPill>
                    <span>trial credit: ${trialCreditDollars}</span>
                    {trialEndsOn && (
                      <>
                        <span>·</span>
                        <span>good through {trialEndsOn}{trialDaysRemaining !== null ? ` (${trialDaysRemaining}d left)` : ''}</span>
                      </>
                    )}
                  </StatusPill>
                ) : null}
                {trialPhase === 'profile' && recs?.mode === 'firm' && (
                  <div style={{ marginTop: '0.85rem', fontSize: '0.85rem', color: '#4B7B5D', lineHeight: 1.55, textTransform: 'lowercase' }}>
                    ✨ {recs.welcomeNote || `set up for ${recs.firmName || 'your team'} — adjust anything.`}
                  </div>
                )}
              </Hero>
            </TrialRail>

            {trialPhase === 'profile' && (
              <Card>
                <CardTitle>
                  {/* safePrefillOrgName (2026-08-24): a protected invite name
                      never headlines the page as "set up for Prest0". */}
                  {safePrefillOrgName((recs?.mode === 'firm' && recs.firmName) || instantFirmProfile?.firmName)
                    ? `set up for ${safePrefillOrgName((recs?.mode === 'firm' && recs.firmName) || instantFirmProfile?.firmName)} — confirm the details`
                    : 'about your work (~30 seconds)'}
                </CardTitle>
                {recs?.mode === 'firm' ? (
                  <div style={{ fontSize: '0.8rem', color: '#4B7B5D', margin: '-0.5rem 0 1rem' }}>
                    {companyConfirmed
                      ? `✨ set up for ${safePrefillOrgName(recs.firmName) || companyLegalName || 'your company'} — adjust anything.`
                      : 'pre-filled from your invite — adjust anything.'}
                  </div>
                ) : companyResearching ? (
                  <div style={{
                    fontSize: '0.8rem', color: '#4B7B5D', margin: '-0.5rem 0 1rem',
                    display: 'flex', alignItems: 'center', gap: '0.45rem',
                  }}>
                    <Spinner $size={14} /> researching {companyLegalName.trim() || 'your company'} — personalizing your setup. keep going, this fills in by itself.
                  </div>
                ) : companyResearchFailed && companyConfirmed ? (
                  <div style={{ fontSize: '0.8rem', color: '#9A948A', margin: '-0.5rem 0 1rem' }}>
                    couldn't research {companyLegalName.trim() || 'your company'} just now — pick what fits below, or{' '}
                    <button
                      type="button"
                      onClick={() => {
                        const n = companyLegalName.trim();
                        if (n) void fetchOrganicRecommendations(n);
                      }}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        font: 'inherit', color: '#4B7B5D', textDecoration: 'underline',
                      }}
                    >
                      retry
                    </button>.
                  </div>
                ) : instantFirmProfile ? (
                  <div style={{ fontSize: '0.8rem', color: '#4B7B5D', margin: '-0.5rem 0 1rem' }}>
                    ✨ personalizing for {instantFirmProfile.firmName || 'your team'} — recommendations land in a moment.
                  </div>
                ) : (recsFetchedRef.current && !recs && !recsUnavailable) ? (
                  <div style={{
                    fontSize: '0.8rem', color: '#9A948A', margin: '-0.5rem 0 1rem',
                    display: 'flex', alignItems: 'center', gap: '0.45rem',
                  }}>
                    <Spinner $size={14} /> personalizing your setup…
                  </div>
                ) : (recsUnavailable && !recs) ? (
                  <div style={{ fontSize: '0.8rem', color: '#9A948A', margin: '-0.5rem 0 1rem' }}>
                    pick what fits below — you can change any of it later.
                  </div>
                ) : null}

                <FormGrid>
                  {/* Your name (owner directive 2026-08-25): the unified
                      onboarding is the identity source of record — the
                      standard signup captures no real name (the email path
                      fabricates one from the address). Required; prefills
                      only from a REAL source (auth displayName). The first
                      name also names the personal workspace. */}
                  <FullWidth>
                    <Label>
                      your name
                      <Input
                        type="text"
                        autoComplete="name"
                        placeholder="first and last name"
                        value={fullName}
                        onChange={(e) => { fullNamePrefilledRef.current = true; setFullName(e.target.value); }}
                        data-testid="full-name-input"
                      />
                    </Label>
                  </FullWidth>
                  {/* Organic company confirm (task #74): a codeless signup
                      leads with the legal name + authority attestation; the
                      CONFIRM fires the web research that personalizes the
                      rest of this page — no waiting, no separate step.
                      Organic accounts only; code flows keep the plain input. */}
                  {isOrganicTrial && !firmProfileChecked ? (
                    <FullWidth>
                      {/* No input until the users-doc read resolves — keystrokes
                          must never land in the wrong field (review 2026-08-18). */}
                      <div style={{
                        fontSize: '0.8rem', color: '#9A948A', display: 'flex',
                        alignItems: 'center', gap: '0.45rem', minHeight: '3.2rem',
                      }}>
                        <Spinner $size={14} /> one moment…
                      </div>
                    </FullWidth>
                  ) : showCompanyConfirm ? (
                    /* Company-confirm card (2026-08-19 tidy-up, owner report:
                       "elements and text boxes are not placed professionally").
                       One bordered card owns the whole decision: name →
                       attestation → CTA with its disabled-reason inline →
                       the just-me fork as a REAL button (it decides which
                       workspace gets minted — it deserves visual weight). */
                    <FullWidth>
                      <div style={{
                        border: '1px solid #ECEAE5', borderRadius: '0.9rem',
                        background: '#FBFAF7', padding: '0.9rem 1rem',
                        display: 'grid', gap: '0.65rem',
                      }}>
                        <Label>
                          your company's legal name
                          <Input
                            type="text"
                            placeholder="Acme Incorporated"
                            value={companyLegalName}
                            onChange={(e) => setCompanyLegalName(e.target.value)}
                            autoFocus
                          />
                        </Label>
                        <label style={{
                          display: 'flex', alignItems: 'flex-start', gap: '0.55rem',
                          fontSize: '0.8rem', color: '#5A5346', cursor: 'pointer', lineHeight: 1.5,
                          border: '1px solid #ECEAE5', borderRadius: '0.7rem',
                          background: '#fff', padding: '0.55rem 0.7rem',
                        }}>
                          <input
                            type="checkbox"
                            checked={companyAttested}
                            onChange={(e) => setCompanyAttested(e.target.checked)}
                            style={{ marginTop: '2px', accentColor: 'var(--brand-gold)', flexShrink: 0 }}
                          />
                          <span>
                            i confirm this is my company's legal name, i'm authorized to set
                            up Prest0n for it, and i agree to the{' '}
                            {/* In-flow ToS (2026-08-23 owner directive): the link opens a
                                popup with the full, selectable terms text — it NEVER
                                navigates out of onboarding. */}
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}
                              style={{
                                background: 'none', border: 'none', padding: 0, fontFamily: 'inherit',
                                fontSize: 'inherit', fontWeight: 700, color: 'var(--brand-gold-hover)',
                                cursor: 'pointer', textDecoration: 'underline', textTransform: 'lowercase',
                              }}
                            >
                              terms of service
                            </button>.
                          </span>
                        </label>
                        <div>
                          <PrimaryButton
                            type="button"
                            onClick={() => { void confirmCompany(); }}
                            disabled={!companyLegalName.trim() || !companyAttested || isProtectedOrgName(companyLegalName)
                              || (draftMode && isDuplicateWorkspaceName(companyLegalName))}
                            style={{ width: '100%', maxWidth: '340px', padding: '0.6rem 1.2rem', whiteSpace: 'normal' }}
                          >
                            confirm — personalize my setup
                          </PrimaryButton>
                          {isProtectedOrgName(companyLegalName) ? (
                            <div style={{ fontSize: '0.74rem', color: '#B0533B', marginTop: '0.4rem', textTransform: 'lowercase' }}>
                              {PROTECTED_ORG_NAME_MESSAGE}
                            </div>
                          ) : draftMode && isDuplicateWorkspaceName(companyLegalName) ? (
                            <div style={{ fontSize: '0.74rem', color: '#B0533B', marginTop: '0.4rem', textTransform: 'lowercase' }}>
                              you already have a workspace named {companyLegalName.trim()} — give this one its own name.
                            </div>
                          ) : (!companyLegalName.trim() || !companyAttested) && (
                            <div style={{ fontSize: '0.74rem', color: '#9A948A', marginTop: '0.4rem', textTransform: 'lowercase' }}>
                              {!companyLegalName.trim()
                                ? 'enter your company’s legal name to continue'
                                : 'check the confirmation box to continue'}
                            </div>
                          )}
                        </div>
                        {/* An organization must be a real one — the solo skip
                            exists only for the personal organic flow. */}
                        {!newOrgMode && (
                          <button
                            type="button"
                            onClick={() => { void skipCompanyConfirm(); }}
                            style={{
                              justifySelf: 'start', background: '#fff', cursor: 'pointer',
                              border: '1px dashed #DDD8CF', borderRadius: '999px',
                              padding: '0.4rem 0.9rem', fontFamily: 'inherit',
                              fontSize: '0.78rem', fontWeight: 600, color: '#857F73',
                              textTransform: 'lowercase',
                            }}
                          >
                            just me — no company to set up →
                          </button>
                        )}
                      </div>
                    </FullWidth>
                  ) : (
                    /* Full width in BOTH branches (2026-08-19 tidy-up): when
                       this was a half-width cell the grid changed shape with
                       the confirm state and the phone field orphaned into a
                       lone column. */
                    <FullWidth>
                      <Label>
                        company or team name
                        <Input
                          type="text"
                          placeholder="Acme Inc."
                          value={firmName}
                          onChange={(e) => {
                            firmNamePlaceholderRef.current = false; // user took ownership
                            setFirmName(e.target.value);
                          }}
                        />
                        {/* Protected names (owner directive 2026-08-24): the
                            platform's own organization can't be founded here —
                            refuse inline, before the save gate has to. */}
                        {isProtectedOrgName(firmName) && (
                          <span style={{ fontSize: '0.74rem', fontWeight: 400, color: '#B0533B', lineHeight: 1.5, textTransform: 'lowercase' }}>
                            {PROTECTED_ORG_NAME_MESSAGE}
                          </span>
                        )}
                        {confirmGateEligible && companyConfirmed && !companyEditRequested && (
                          <button
                            type="button"
                            onClick={() => {
                              setCompanyEditRequested(true);
                              setCompanyAttested(false);
                              setCompanyResearchFailed(false);
                            }}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              fontSize: '0.75rem', color: '#9A948A', textDecoration: 'underline',
                              padding: 0, marginTop: '0.35rem', textAlign: 'left',
                            }}
                          >
                            wrong company? re-run the setup research
                          </button>
                        )}
                      </Label>
                      {/* Unified confirm (owner directive 2026-08-24): EVERY
                          organization onboarding carries the same
                          authority attestation + terms agreement — the
                          invite/code path used to skip it entirely while the
                          created-org and organic paths required it. Same
                          state (companyAttested) and the same in-flow terms
                          popup as the organic confirm card; only the
                          personalization intake differs between paths. */}
                      <label style={{
                          display: 'flex', alignItems: 'flex-start', gap: '0.55rem',
                          fontSize: '0.8rem', color: '#5A5346', cursor: 'pointer', lineHeight: 1.5,
                          border: '1px solid #ECEAE5', borderRadius: '0.7rem',
                          background: '#fff', padding: '0.55rem 0.7rem', marginTop: '0.55rem',
                        }}>
                          <input
                            type="checkbox"
                            checked={companyAttested}
                            onChange={(e) => setCompanyAttested(e.target.checked)}
                            data-testid="represent-attestation"
                            style={{ marginTop: '2px', accentColor: 'var(--brand-gold)', flexShrink: 0 }}
                          />
                          <span>
                            i confirm i can represent {firmName.trim() || 'this company or team'} and
                            set up Prest0n for it, and i agree to the{' '}
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}
                              style={{
                                background: 'none', border: 'none', padding: 0, fontFamily: 'inherit',
                                fontSize: 'inherit', fontWeight: 700, color: 'var(--brand-gold-hover)',
                                cursor: 'pointer', textDecoration: 'underline', textTransform: 'lowercase',
                              }}
                            >
                              terms of service
                            </button>.
                          </span>
                      </label>
                    </FullWidth>
                  )}

                  {/* Terms of Service — in-flow popup (2026-08-23): the
                      full legal text, selectable and one-tap copyable,
                      rendered from the SAME TermsContent the
                      /terms-of-service page uses. Closing returns the
                      user exactly where they were. */}
                  <Modal
                    isOpen={showTermsModal}
                    onClose={() => { setShowTermsModal(false); setTermsCopied(false); }}
                    title="terms of service"
                    maxWidth="min(760px, 94vw)"
                    footer={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => {
                            const text = termsBodyRef.current?.innerText || '';
                            void navigator.clipboard?.writeText(text).then(
                              () => setTermsCopied(true),
                              () => { /* selection stays available */ });
                          }}
                          style={{
                            background: '#fff', border: '1px solid #DDD8CF', borderRadius: '999px',
                            padding: '0.45rem 1rem', fontFamily: 'inherit', fontSize: '0.8rem',
                            fontWeight: 600, color: '#5A5346', cursor: 'pointer', textTransform: 'lowercase',
                          }}
                        >
                          {termsCopied ? '✓ copied' : 'copy full text'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowTermsModal(false); setTermsCopied(false); }}
                          style={{
                            background: 'var(--brand-gold)', color: '#fff', border: 'none', borderRadius: '999px',
                            padding: '0.45rem 1.1rem', fontFamily: 'inherit', fontSize: '0.8rem',
                            fontWeight: 600, cursor: 'pointer', textTransform: 'lowercase',
                          }}
                        >
                          done — back to setup
                        </button>
                      </div>
                    }
                  >
                    <div ref={termsBodyRef} style={{ userSelect: 'text' }}>
                      <TermsContentLazy />
                    </div>
                  </Modal>

                  <div>
                    <Label as="div">team size</Label>
                    <PracticeAreaGrid style={{ marginTop: '0.5rem' }}>
                      {TEAM_SIZE_OPTIONS.map((size) => (
                        <Chip
                          key={size}
                          type="button"
                          $selected={firmSize === size}
                          onClick={() => setFirmSize(size)}
                        >
                          {size}
                        </Chip>
                      ))}
                    </PracticeAreaGrid>
                  </div>

                  <Label>
                    your phone <span style={{ color: '#98917F', fontWeight: 400 }}>optional</span>
                    {/* Phase 0 (2026-08-15 owner report): the contact phone was
                        collected nowhere in the trial flow — the only "phone"
                        anywhere was the provisioned Prest0n number. This one
                        write-throughs to the account profile page.
                        2026-08-19: saving with a number now sends the
                        verification text right away (requestSmsVerification —
                        the same user-submitted-number idiom Profile.tsx uses;
                        the approved double-opt-in body, reply YES to confirm).
                        The disclosure below is the express-consent surface —
                        A2P campaign QE2c was rejected for texting a passively
                        collected number, so the copy must state the text. */}
                    <Input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="how we reach you — not your Prest0n number"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                    />
                    <span style={{ fontSize: '0.72rem', fontWeight: 400, color: '#98917F', lineHeight: 1.5, textTransform: 'lowercase' }}>
                      adding your number sends one verification text — reply yes to confirm.
                      msg &amp; data rates may apply ·{' '}
                      <a href="/sms-terms" target="_blank" rel="noreferrer" style={{ color: '#857F73' }}>sms terms</a>
                    </span>
                  </Label>

                  <FullWidth>
                    <Label as="div">what kind of work do you do?</Label>
                    <PracticeAreaGrid style={{ marginTop: '0.5rem' }}>
                      {visibleAreas.map((area) => (
                        <Chip
                          key={area}
                          type="button"
                          $selected={practiceAreas.includes(area)}
                          onClick={() => togglePracticeAreaWithCaps(area)}
                        >
                          {area}
                        </Chip>
                      ))}
                      {hiddenAreaCount > 0 && (
                        <Chip type="button" $selected={false} onClick={() => setShowAllAreas(true)}>
                          + {hiddenAreaCount} more
                        </Chip>
                      )}
                      {showAllAreas && (
                        <Chip type="button" $selected={false} onClick={() => setShowAllAreas(false)}>
                          show fewer
                        </Chip>
                      )}
                    </PracticeAreaGrid>
                  </FullWidth>

                  <FullWidth ref={capabilitiesRef}>
                    <Label as="div">what would you like Prest0n to help with?</Label>
                    <div style={{ fontSize: '0.8rem', color: '#857F73', marginBottom: '0.5rem', textTransform: 'lowercase' }}>
                      {recs?.mode === 'firm'
                        ? 'pre-selected for your work — one tap toggles a whole theme.'
                        : practiceAreas.length > 0
                          ? 'tailored to your work areas — one tap toggles a whole theme.'
                          : 'pick your work areas above for tailored themes.'}
                    </div>
                    {/* Task #53 (decision-fatigue report): themed bubbles
                        instead of 10–18 individual chips. The examples inside
                        each bubble carry the idea-generation intent; the
                        stored model stays the individual capability list. */}
                    <div style={{
                      display: 'grid', gap: '0.55rem', marginTop: '0.25rem',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
                    }}>
                      {capabilityGroups.map(({ key, label, members }) => {
                        const selected = isGroupSelected(members);
                        const examples = members.slice(0, 3).join(' · ');
                        return (
                          <GroupBubble
                            key={key}
                            type="button"
                            $selected={selected}
                            aria-pressed={selected}
                            onClick={() => toggleCapabilityGroup(members)}
                          >
                            <GroupCheck $selected={selected} aria-hidden="true">
                              {selected ? '✓' : ''}
                            </GroupCheck>
                            <span style={{ minWidth: 0 }}>
                              <GroupLabel $selected={selected}>{label}</GroupLabel>
                              <GroupExamples>
                                {examples}
                                {members.length > 3 ? ` · +${members.length - 3} more` : ''}
                              </GroupExamples>
                            </span>
                          </GroupBubble>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#857F73', marginTop: '0.6rem' }}>
                      just a starting point — Prest0n handles all of this and keeps learning; ask for anything, anytime.
                    </div>
                  </FullWidth>
                </FormGrid>

                {/* Up-next choices (2026-08-04): both optional steps are asked
                    HERE, so nothing later in the flow is a surprise. "later"
                    fast-forwards past the step on save. */}
                <div style={{
                  border: '1px solid #ECEAE5', borderRadius: '0.9rem',
                  padding: '0.85rem 1rem', margin: '1.1rem 0 0', background: '#FBFAF7',
                  display: 'grid', gap: '0.7rem',
                }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--brand-text)', textTransform: 'lowercase' }}>
                    up next — your call, both optional
                  </div>
                  {/* Wrap pass (2026-08-09): let the chip pair drop below the
                      label as a unit at 1024–1100px instead of the label
                      breaking mid-parenthetical next to squeezed chips. */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.84rem', color: '#555', textTransform: 'lowercase', flex: '1 1 240px', minWidth: 0 }}>
                      connect your apps (recommended — gmail, drive, calendar)
                    </span>
                    <span style={{ display: 'inline-flex', gap: '0.4rem', whiteSpace: 'nowrap' }}>
                      <Chip type="button" $selected={wantsApps !== 'later'} onClick={() => setWantsApps('yes')}>yes, right after this</Chip>
                      <Chip type="button" $selected={wantsApps === 'later'} onClick={() => setWantsApps('later')}>later</Chip>
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.84rem', color: '#555', textTransform: 'lowercase', flex: '1 1 240px', minWidth: 0 }}>
                      {/* Created-org invites earn credit only AFTER the org hires
                          (72h window) — promising setup credit here would be
                          money the flow never pays (review finding 19). */}
                      {(newOrgMode || (firstSignupMode && draftTargetKind === 'company'))
                        ? 'invite teammates (optional — they join the moment the org exists)'
                        : 'invite teammates (optional — adds up to $1,600 setup credit)'}
                    </span>
                    <span style={{ display: 'inline-flex', gap: '0.4rem', whiteSpace: 'nowrap' }}>
                      <Chip type="button" $selected={wantsTeam !== 'later'} onClick={() => setWantsTeam('yes')}>yes, show me</Chip>
                      <Chip type="button" $selected={wantsTeam === 'later'} onClick={() => setWantsTeam('later')}>just me for now</Chip>
                    </span>
                  </div>
                </div>

                {error && <ErrorText>{error}</ErrorText>}

                <ButtonRow>
                  <PrimaryButton
                    onClick={saveTrialProfile}
                    // While the organic confirm block is up, CONFIRM (or the
                    // solo skip) is the only way forward — save must never
                    // walk past the legal-name gate on a fabricated tenant
                    // placeholder name (review round 2026-08-18).
                    disabled={trialSaving || !firmName.trim() || !fullName.trim() || showCompanyConfirm
                      || isProtectedOrgName(firmName) || !companyAttested
                      || (draftMode && isDuplicateWorkspaceName(firmName))}
                  >
                    {trialSaving ? 'saving…' : 'save & continue →'}
                  </PrimaryButton>
                  {/* A silently dimmed CTA reads as broken (2026-08-19 owner
                      report) — say WHY it's waiting, ~200px from the cause. */}
                  {!trialSaving && (showCompanyConfirm || !firmName.trim() || !fullName.trim() || isProtectedOrgName(firmName) || !companyAttested || (draftMode && isDuplicateWorkspaceName(firmName))) && (
                    <span style={{ fontSize: '0.74rem', color: '#9A948A', textTransform: 'lowercase', alignSelf: 'center' }}>
                      {showCompanyConfirm
                        ? 'confirm your company above (or choose "just me") to continue'
                        : !fullName.trim()
                          ? 'add your name to continue'
                          : !firmName.trim()
                            ? 'add your company or team name to continue'
                            : isProtectedOrgName(firmName)
                              ? 'use your own company or team’s name to continue'
                              : (draftMode && isDuplicateWorkspaceName(firmName))
                                ? 'you already have a workspace with this name — give this one its own'
                                : 'check the confirmation box above to continue'}
                    </span>
                  )}
                </ButtonRow>
                {/* When both optional steps are deferred, this save button IS
                    the launch CTA — surface the workstation truth here.
                    Early-minted drafts (2026-08-19) watch THEIR org's VM. */}
                <WorkstationReadinessHint
                  active={(!draftMode && trialPhase === 'profile' && wantsApps === 'later' && wantsTeam === 'later')
                    || (draftMode && Boolean(mintedOrgAccountId))}
                  account={draftMode ? mintedOrgAccountId : undefined}
                />
              </Card>
            )}

            {trialPhase === 'apps' && (
              <Card>
                {/* In-flow back navigation (task #56) — never out of onboarding. */}
                <button
                  type="button"
                  onClick={() => goToPhase('profile')}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    fontSize: '0.78rem', fontWeight: 600, color: '#857F73', textTransform: 'lowercase',
                    padding: 0, marginBottom: '0.6rem', display: 'block',
                  }}
                >
                  ← back to profile setup
                </button>
                <CardTitle>
                  {recs?.mode === 'firm' && recs.firmName
                    ? `picked for ${recs.firmName} — each takes ~30 seconds, right here`
                    : 'connect what you use — each takes ~30 seconds, right here'}
                </CardTitle>
                <div style={{ fontSize: '0.82rem', color: '#857F73', lineHeight: 1.55, margin: '-0.35rem 0 0.9rem' }}>
                  optional, but recommended — Prest0n can only work inside your email, files, and calendar once you connect them. after each connect you choose exactly what Prest0n can do — and roles control who on your team can use it. skip anytime.
                </div>
                <div style={{ display: 'grid', gap: '0.55rem', margin: '0.25rem 0 1.25rem' }}>
                  {recommendedSlugs.map((slug) => {
                    const connected = isAppConnected(slug);
                    return (
                      <div key={slug} style={{
                        // flexWrap (2026-08-14 owner report): the chips must
                        // NEVER crowd or overlay the app name — when the row
                        // is tight, the chip block wraps to its own line and
                        // the name keeps the full width.
                        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                        justifyContent: 'space-between', gap: '0.45rem',
                        // Connected rows read as SELECTED at a glance (task
                        // #53): green border + tint + a filled ✓ pill — the
                        // same "done" language as the journey strip, so the
                        // next move is always the rows without one.
                        border: `1px solid ${connected ? '#B5E0BF' : '#ECEAE5'}`,
                        borderRadius: '0.9rem', padding: '0.7rem 0.9rem',
                        background: connected ? '#F3FBF4' : '#fff',
                      }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem', minWidth: 0 }}>
                          {/* Real app logo, same source as the integrations
                              page (2026-08-14 owner report) — gold letter
                              avatar only until the catalog lands. */}
                          <IntegrationIcon logo={catalogMeta.get(slug)?.logo} name={prettyIntegration(slug)} size={24} />
                          <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--brand-text)', textTransform: 'lowercase' }}>
                            {prettyIntegration(slug)}
                          </span>
                        </span>
                        {connected ? (
                          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1B7A3D', display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.15rem' }}>
                            <span style={{
                              background: '#E7F6EA', border: '1px solid #B5E0BF',
                              borderRadius: '999px', padding: '0.18rem 0.6rem',
                            }}>
                              ✓ connected
                            </span>
                            {/* Scope chip (2026-08-11 §7.1): the sheet's outcome, e.g. "· 2 folders".
                                Falls back to the signed-in identity so pre-existing
                                connections still say WHICH account is connected. */}
                            {(scopeChips[slug] || rosterChip(slug)) && (
                              <span style={{ fontWeight: 600, color: '#857F73' }}>{` · ${scopeChips[slug] || rosterChip(slug)}`}</span>
                            )}
                            {/* No identity to show, and no provider callback
                                that could supply one — so ask (2026-09-03). */}
                            {!scopeChips[slug] && !rosterChip(slug) && identityAskFor(slug)}
                            {/* Add another account — EVERY connected app,
                                every time (task #55 owner directive: Gmail
                                and Calendar too, and always available after
                                the 2nd/3rd account). Storage gets a folder
                                sheet for the new account; other apps just
                                gain the extra account. */}
                            <button
                              type="button"
                              onClick={() => connectAppInFlow(slug, { additional: true })}
                              disabled={connectingSlug === slug}
                              title={`connect another ${prettyIntegration(slug)} account`}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                                fontSize: '0.74rem', fontWeight: 600, color: '#857F73',
                                textDecoration: 'underline', textTransform: 'lowercase',
                                padding: 0, marginLeft: '0.5rem',
                              }}
                            >
                              {connectingSlug === slug ? 'opening…' : '+ add another account'}
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => connectAppInFlow(slug)}
                            disabled={connectingSlug === slug}
                            style={{
                              // Gold, not black (task #53): the action color
                              // of this flow, never an intimidating dark pill.
                              background: 'var(--brand-gold)', color: '#fff', border: 'none', borderRadius: '999px',
                              padding: '0.45rem 1rem', fontFamily: 'inherit', fontSize: '0.8rem',
                              fontWeight: 600, cursor: 'pointer', textTransform: 'lowercase',
                            }}
                          >
                            {connectingSlug === slug ? 'opening…' : connectCtaLabel(slug)}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Post-connect granular controls — FLOW anchor (2026-08-12):
                    the sheet renders here only when the connect started from
                    these recommended tiles; modal-originated connects render
                    it inside the "more apps" modal instead — the next step
                    always appears where the user just acted. */}
                {scopeSheet?.origin === 'flow' && renderScopeSheetCard()}

                <div style={{ fontSize: '0.78rem', color: '#857F73', lineHeight: 1.55, marginBottom: '0.9rem' }}>
                  a sign-in window opens; this page waits and ticks each one off.
                  the screen will say &ldquo;continue to Composio&rdquo; — that's expected:
                  Composio is Prest0n's secure connection partner, and your password
                  stays with the provider. you can always add apps later from the
                  integrations page.
                </div>
                <div style={{ margin: '0 0 1.1rem' }}>
                  <button
                    type="button"
                    onClick={() => setShowAllApps(true)}
                    style={{
                      width: '100%', background: '#fff', border: '1px solid var(--brand-line)',
                      borderRadius: '0.9rem', padding: '0.65rem 0.9rem', fontFamily: 'inherit',
                      fontSize: '0.85rem', fontWeight: 600, color: 'var(--brand-text)', cursor: 'pointer',
                      textTransform: 'lowercase',
                    }}
                  >
                    {/* Copy tracks the catalog expansion (2026-08-16): the
                        modal spans the ENTIRE catalog, not just one-click. */}
                    {allToolkits.length > 100
                      ? `more apps — browse all ${allToolkits.length.toLocaleString()} →`
                      : 'more apps — browse the full catalog →'}
                  </button>
                </div>

                {/* Popup-blocker fallback (task #53): a direct link click is
                    never blocked; same window name keeps one sign-in window. */}
                {manualConnect && (
                  <div style={{
                    border: '1px solid #E7DCC3', background: 'rgba(198, 167, 94, 0.07)',
                    borderRadius: '0.9rem', padding: '0.75rem 0.95rem', margin: '0 0 1rem',
                    fontSize: '0.82rem', color: '#6b5d3f', lineHeight: 1.55, textTransform: 'lowercase',
                  }}>
                    your browser blocked the sign-in window.{' '}
                    <a
                      href={manualConnect.url}
                      target="prest0n-connect"
                      rel="noreferrer"
                      onClick={() => { setManualConnect(null); startConnectPoll(); }}
                      style={{ color: 'var(--brand-gold-hover)', fontWeight: 700 }}
                    >
                      open the {prettyIntegration(manualConnect.slug)} sign-in →
                    </a>
                  </div>
                )}

                {error && <ErrorText>{error}</ErrorText>}

                <ButtonRow>
                  <PrimaryButton
                    onClick={() => {
                      void refreshInitialConditions();
                      if (wantsTeam === 'later') { void launchWorkspace(); } else { advancePhase('team'); }
                    }}
                    disabled={launching}
                  >
                    {launching
                      ? 'opening your workspace…'
                      : connectedRecommended.length > 0
                        ? `${connectedRecommended.length} connected — continue →`
                        : 'continue →'}
                  </PrimaryButton>
                </ButtonRow>
                <div style={{ textAlign: 'center', marginTop: '0.6rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      void refreshInitialConditions();
                      if (wantsTeam === 'later') { void launchWorkspace(); } else { advancePhase('team'); }
                    }}
                    disabled={launching}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: '0.78rem', color: '#9A948A', textDecoration: 'underline', textTransform: 'lowercase',
                    }}
                  >
                    skip — connect anytime later
                  </button>
                </div>
                {/* This card launches directly when team is deferred. */}
                <WorkstationReadinessHint
                  active={(!draftMode && trialPhase === 'apps' && wantsTeam === 'later')
                    || (draftMode && Boolean(mintedOrgAccountId))}
                  account={draftMode ? mintedOrgAccountId : undefined}
                />

                {coachApp && (
                  <ComposioConnectCoach
                    appLabel={coachApp}
                    onDismiss={() => setCoachApp(null)}
                  />
                )}

                {/* ── "more apps" modal (task #43) ─────────────────────────
                    The full live catalog, grouped exactly like the
                    integrations page, INSIDE the guided flow: connecting
                    uses the same popup + poll as the curated rows, closing
                    returns to this step untouched, and there is no
                    navigation out of onboarding.
                    NOT a one-click catalog (corrected 2026-09-02): ~8% of the
                    ~1,448 toolkits carry Composio-managed auth. Every row
                    routes by its lane — one-click where that works, Prest0n's
                    browser sign-in for the rest. */}
                <Modal
                  isOpen={showAllApps}
                  onClose={() => {
                    // A modal-origin sheet must not outlive its surface: treat
                    // closing the modal as "decide later" (one click, no call)
                    // so the queue drains and nothing renders on the main page.
                    if (scopeSheet && scopeSheet.origin === 'modal') dismissScopeSheet();
                    setShowAllApps(false);
                  }}
                  title="connect your apps"
                  maxWidth="min(1080px, 96vw)"
                  /* ONE scroll region (owner feedback 2026-08-11): noScroll
                     turns off the modal shell's own scrollbar; intro + search
                     stay fixed and only the app list below scrolls. Nested
                     page+list scrollbars read as broken. */
                  noScroll
                  footer={
                    <>
                      <div style={{
                        fontSize: '0.8rem', color: '#857F73', lineHeight: 1.55,
                        textAlign: 'left', flex: 1,
                      }}>
                        not seeing a tool? most apps here sign in one click; the rest
                        Prest0n signs into on its own computer — either way it works
                        with any system that's online and reachable in a browser. and
                        you can always add apps later from the integrations page.
                      </div>
                      {/* Unmistakable way OUT (task #56, owner report: users
                          get stuck in this popup) — a labeled gold button,
                          not just the corner ×. While the no-match connect
                          offer is showing (2026-08-23), THAT is the next
                          step, so this exit demotes to an outline. */}
                      <button
                        type="button"
                        onClick={() => setShowAllApps(false)}
                        style={{
                          ...(catalogGroups.length === 0 && !catalogLoading && catalogQuery.trim()
                            ? { background: '#fff', color: '#857F73', border: '1px solid #DDD8CF' }
                            : { background: 'var(--brand-gold)', color: '#fff', border: 'none' }),
                          borderRadius: '999px',
                          padding: '0.55rem 1.2rem', fontFamily: 'inherit', fontSize: '0.85rem',
                          fontWeight: 600, cursor: 'pointer', textTransform: 'lowercase',
                          flexShrink: 0, whiteSpace: 'nowrap',
                        }}
                      >
                        done — back to setup →
                      </button>
                    </>
                  }
                >
                  {/* Fixed header zone: intro + search never scroll away. */}
                  <div style={{ padding: '1.25rem 1.5rem 0', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.86rem', color: '#857F73', lineHeight: 1.55, margin: '0 0 0.9rem', textTransform: 'lowercase' }}>
                      connect any of these now — a sign-in window opens and this page
                      ticks it off. your setup stays right where you left it.
                    </div>
                    {/* Popup-blocker fallback (task #53) — mirrored here so a
                        blocked connect started FROM this modal is never
                        announced behind it. */}
                    {manualConnect && (
                      <div style={{
                        border: '1px solid #E7DCC3', background: 'rgba(198, 167, 94, 0.07)',
                        borderRadius: '0.75rem', padding: '0.6rem 0.85rem', margin: '0 0 0.9rem',
                        fontSize: '0.8rem', color: '#6b5d3f', lineHeight: 1.5, textTransform: 'lowercase',
                      }}>
                        your browser blocked the sign-in window.{' '}
                        <a
                          href={manualConnect.url}
                          target="prest0n-connect"
                          rel="noreferrer"
                          onClick={() => { setManualConnect(null); startConnectPoll(); }}
                          style={{ color: 'var(--brand-gold-hover)', fontWeight: 700 }}
                        >
                          open the {prettyIntegration(manualConnect.slug)} sign-in →
                        </a>
                      </div>
                    )}
                    <input
                      type="text"
                      value={catalogQuery}
                      onChange={(e) => setCatalogQuery(e.target.value)}
                      onKeyDown={(e) => {
                        // Enter = the "connect with Prest0n's browser" offer,
                        // exactly when that offer is on screen (a true
                        // zero-match search) — owner ask 2026-08-22. Same
                        // lane as the CTA button below.
                        if (e.key !== 'Enter' || catalogLoading || catalogGroups.length !== 0) return;
                        const name = catalogQuery.trim();
                        if (!name) return;
                        void import('../services/composio/connectCustomFlow').then(
                          ({ requestConnectConcierge, beginCustomConnectFlow }) => {
                            if (!requestConnectConcierge(name)) {
                              const orgVm = signInWorkspaceTarget;
                              void beginCustomConnectFlow({ appName: name, ...(orgVm ? { workspaceAccountId: orgVm } : {}) });
                            }
                          },
                        );
                      }}
                      placeholder={`search ${allToolkits.length > 0 ? `${allToolkits.length}+ ` : ''}integrations…`}
                      style={{
                        width: '100%', boxSizing: 'border-box', border: '1px solid var(--brand-line)',
                        borderRadius: '0.7rem', padding: '0.65rem 0.9rem', fontFamily: 'inherit',
                        fontSize: '0.9rem', marginBottom: '1rem', outline: 'none',
                      }}
                    />
                  </div>
                  {/* THE one scroll region — the whole catalog scrolls here. */}
                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 1.5rem 0.75rem' }}>
                    {/* Post-connect granular controls — MODAL anchor
                        (2026-08-12): a connect started from this catalog gets
                        its sheet right here, above the app list, so saving or
                        dismissing drops the user back on the list to keep
                        connecting. Flow-originated sheets never render here. */}
                    {scopeSheet?.origin === 'modal' && renderScopeSheetCard()}
                    {catalogGroups.map(({ category, slugs }) => (
                      <div key={category} style={{ marginBottom: '1.3rem' }}>
                        <div style={{
                          fontSize: '0.74rem', fontWeight: 700,
                          color: category === 'recommended for you' ? 'var(--brand-gold-hover)' : '#9A948A',
                          textTransform: 'lowercase', letterSpacing: '0.05em', marginBottom: '0.5rem',
                        }}>
                          {category === 'recommended for you' ? '★ recommended for you' : category.toLowerCase()}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.5rem' }}>
                          {/* FIRST recommended entry (2026-08-23 owner
                              directive): an open "another application…" card
                              — type any app's name and Prest0n's browser
                              connects it, same lane as the search fallback. */}
                          {category === 'recommended for you' && (
                            <button
                              type="button"
                              data-testid="onboarding-custom-app-card"
                              onClick={() => { setCustomAppName(''); setShowCustomAppPrompt(true); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '0.55rem',
                                border: '1px dashed var(--brand-gold)', borderRadius: '0.75rem',
                                padding: '0.55rem 0.75rem', background: 'rgba(198, 167, 94, 0.06)',
                                cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', minWidth: 0,
                              }}
                            >
                              <span aria-hidden="true" style={{
                                width: 22, height: 22, borderRadius: 5, flexShrink: 0,
                                background: 'rgba(198, 167, 94, 0.18)', color: '#7a5f1f',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.9rem', fontWeight: 700,
                              }}>+</span>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--brand-text)', textTransform: 'lowercase' }}>
                                  another application…
                                </span>
                                <span style={{ display: 'block', fontSize: '0.72rem', color: '#857F73', textTransform: 'lowercase' }}>
                                  any system with a web login
                                </span>
                              </span>
                            </button>
                          )}
                          {slugs.map((slug) => {
                            const connected = isAppConnected(slug);
                            return (
                              <div key={slug} style={{
                                // flexWrap (2026-08-14 owner report: the
                                // connected chip squeezed "google drive" down
                                // to "goo…"): the chip block wraps below the
                                // name instead of crowding it.
                                display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                                justifyContent: 'space-between', gap: '0.4rem',
                                // Same connected language as the curated rows
                                // (task #53): green border + tint + ✓ pill.
                                border: `1px solid ${connected ? '#B5E0BF' : '#ECEAE5'}`,
                                borderRadius: '0.75rem', padding: '0.55rem 0.75rem',
                                background: connected ? '#F3FBF4' : '#fff', minWidth: 0,
                              }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', minWidth: 0, marginRight: '0.5rem', flex: '1 1 auto' }}>
                                  <IntegrationIcon logo={catalogMeta.get(slug)?.logo} name={prettyIntegration(slug)} size={22} />
                                  <span style={{
                                    fontSize: '0.85rem', fontWeight: 600, color: 'var(--brand-text)',
                                    textTransform: 'lowercase', overflow: 'hidden', textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap', minWidth: '6.5rem',
                                  }}>
                                    {prettyIntegration(slug)}
                                  </span>
                                </span>
                                {connected ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0, marginLeft: 'auto' }}>
                                    <span style={{
                                      fontSize: '0.76rem', fontWeight: 700, color: '#1B7A3D',
                                      background: '#E7F6EA', border: '1px solid #B5E0BF',
                                      borderRadius: '999px', padding: '0.15rem 0.55rem',
                                    }}>
                                      {/* Scope chip (2026-08-11 §7.1) — same outcome as the curated
                                          row, with the signed-in identity as the fallback. */}
                                      ✓ connected{(scopeChips[slug] || rosterChip(slug)) && (
                                        <span style={{ fontWeight: 600, color: '#4B7B5D' }}>{` · ${scopeChips[slug] || rosterChip(slug)}`}</span>
                                      )}
                                    </span>
                                    {!scopeChips[slug] && !rosterChip(slug) && identityAskFor(slug)}
                                    {/* Add another account — every connected app (task #55). */}
                                    <button
                                      type="button"
                                      onClick={() => connectAppInFlow(slug, { additional: true, origin: 'modal' })}
                                      disabled={connectingSlug === slug}
                                      title={`connect another ${prettyIntegration(slug)} account`}
                                      style={{
                                        background: 'none', border: '1px dashed var(--brand-gold)', borderRadius: '999px',
                                        padding: '0.12rem 0.5rem', fontFamily: 'inherit', fontSize: '0.72rem',
                                        fontWeight: 600, color: '#6b5d3f', cursor: 'pointer', textTransform: 'lowercase',
                                      }}
                                    >
                                      {connectingSlug === slug ? '…' : '+ add'}
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    /* origin: 'modal' (2026-08-12) — the
                                       post-connect sheet renders INSIDE this
                                       modal, where the user just acted. */
                                    onClick={() => connectAppInFlow(slug, { origin: 'modal' })}
                                    disabled={connectingSlug === slug}
                                    style={{
                                      background: 'var(--brand-gold)', color: '#fff', border: 'none', borderRadius: '999px',
                                      padding: '0.3rem 0.85rem', fontFamily: 'inherit', fontSize: '0.76rem',
                                      fontWeight: 600, cursor: 'pointer', textTransform: 'lowercase', flexShrink: 0,
                                    }}
                                  >
                                    {connectingSlug === slug ? 'opening…' : connectCtaLabel(slug)}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {/* Cap note (2026-08-16): the list stops at 60 rendered
                        rows — say so, and say what to do about it. */}
                    {catalogTruncated && (
                      <div style={{ color: '#857F73', fontSize: '0.8rem', padding: '0 0 1rem', textTransform: 'lowercase' }}>
                        showing 60 of {catalogMatchTotal} — refine your search
                      </div>
                    )}
                    {catalogGroups.length === 0 && !catalogLoading && (
                      /* No-match state (2026-08-23 owner directive): the
                         connect action IS the next step here, so it renders
                         as the most prominent button on screen — a solid
                         gold pill in a bordered offer card — while the
                         footer's "done — back to setup" demotes to an
                         outline (see modal footer). */
                      <div style={{
                        border: '1px solid #E7DCC3', borderRadius: '0.9rem',
                        background: 'rgba(198, 167, 94, 0.07)', padding: '0.9rem 1rem',
                        margin: '0.5rem 0 1rem', display: 'grid', gap: '0.55rem',
                        justifyItems: 'start',
                      }}>
                        <div style={{ color: '#5A5346', fontSize: '0.86rem', lineHeight: 1.5 }}>
                          no matches for &ldquo;{catalogQuery}&rdquo; — Prest0n&rsquo;s own browser can
                          sign in to any system with a web login.
                        </div>
                        {/* Same lane as the Integrations custom row (2026-08-19):
                            chat concierge first; without a mounted chat the
                            sign-in surface opens directly — never a dead end. */}
                        <button
                          type="button"
                          data-testid="onboarding-connect-custom"
                          onClick={() => {
                            const name = catalogQuery.trim();
                            if (!name) return;
                            void import('../services/composio/connectCustomFlow').then(
                              ({ requestConnectConcierge, beginCustomConnectFlow }) => {
                                if (!requestConnectConcierge(name)) {
                                  const orgVm = signInWorkspaceTarget;
                                  void beginCustomConnectFlow({ appName: name, ...(orgVm ? { workspaceAccountId: orgVm } : {}) });
                                }
                              },
                            );
                          }}
                          style={{
                            background: 'var(--brand-gold)', color: '#fff', border: 'none',
                            borderRadius: '999px', padding: '0.55rem 1.2rem',
                            fontFamily: 'inherit', fontSize: '0.88rem', fontWeight: 700,
                            cursor: 'pointer', textTransform: 'lowercase',
                          }}
                        >
                          connect &ldquo;{catalogQuery.trim()}&rdquo; with Prest0n&rsquo;s browser →
                        </button>
                      </div>
                    )}
                  </div>
                </Modal>
                {/* Name-entry popup for the "another application…" card
                    (2026-08-23): stacked above the catalog modal; proceed
                    hands the name to the same concierge/browser lane. */}
                <Modal
                  isOpen={showCustomAppPrompt}
                  onClose={() => setShowCustomAppPrompt(false)}
                  title="connect another application"
                  description="type the app's name — one-click connect when we support it directly; anything else, Prest0n's own browser signs in with a web login."
                  maxWidth="min(440px, 94vw)"
                  zIndex={20000100}
                  /* The form below carries its own actions; without this the
                     shell renders a default Cancel/CONFIRM pair whose Confirm
                     just closes — a primary button that silently discards the
                     typed name (review 2026-08-23). */
                  footer={null}
                >
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const name = customAppName.trim();
                      if (!name) return;
                      // DIRECT-FIRST (2026-08-24): enter takes the top
                      // catalog match's one-click connect when one exists;
                      // the browser lane fires only from its own explicit
                      // button (or here, when nothing matched).
                      if (customAppMatches.length > 0) {
                        setShowCustomAppPrompt(false);
                        void connectAppInFlow(customAppMatches[0].slug, { origin: 'modal' });
                        return;
                      }
                      setShowCustomAppPrompt(false);
                      void import('../services/composio/connectCustomFlow').then(
                        ({ requestConnectConcierge, beginCustomConnectFlow }) => {
                          if (!requestConnectConcierge(name)) {
                            const orgVm = signInWorkspaceTarget;
                            void beginCustomConnectFlow({ appName: name, ...(orgVm ? { workspaceAccountId: orgVm } : {}) });
                          }
                        },
                      );
                    }}
                    style={{ display: 'grid', gap: '0.7rem' }}
                  >
                    <Input
                      type="text"
                      placeholder="e.g. Fitbit, RingCentral, Clio…"
                      value={customAppName}
                      onChange={(e) => setCustomAppName(e.target.value)}
                      autoFocus
                      maxLength={80}
                      data-testid="custom-app-name-input"
                    />
                    {/* Direct one-click connectors first (2026-08-24): the
                        catalog matches for what the user typed, each with the
                        same connect the catalog rows use. Browser sign-in
                        demotes to the explicit fallback below. */}
                    {customAppMatches.length > 0 && (
                      <div style={{ display: 'grid', gap: '0.45rem' }} data-testid="custom-app-direct-matches">
                        {customAppMatches.map((tk) => {
                          const slug = tk.slug;
                          const connected = isAppConnected(slug);
                          return (
                            <div key={slug} style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              gap: '0.5rem', border: `1px solid ${connected ? '#B5E0BF' : '#ECEAE5'}`,
                              borderRadius: '0.75rem', padding: '0.5rem 0.7rem',
                              background: connected ? '#F3FBF4' : '#fff', minWidth: 0,
                            }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', minWidth: 0 }}>
                                <IntegrationIcon logo={tk.meta?.logo} name={tk.name || prettyIntegration(slug)} size={22} />
                                <span style={{
                                  fontSize: '0.85rem', fontWeight: 600, color: 'var(--brand-text)', textTransform: 'lowercase',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                  {tk.name || prettyIntegration(slug)}
                                </span>
                              </span>
                              {connected ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
                                  <span style={{
                                    fontSize: '0.74rem', fontWeight: 700, color: '#1B7A3D',
                                    background: '#E7F6EA', border: '1px solid #B5E0BF',
                                    borderRadius: '999px', padding: '0.12rem 0.5rem',
                                  }}>
                                    ✓ connected
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => { setShowCustomAppPrompt(false); void connectAppInFlow(slug, { additional: true, origin: 'modal' }); }}
                                    title={`connect another ${tk.name || prettyIntegration(slug)} account`}
                                    style={{
                                      background: 'none', border: '1px dashed var(--brand-gold)', borderRadius: '999px',
                                      padding: '0.12rem 0.5rem', fontFamily: 'inherit', fontSize: '0.72rem',
                                      fontWeight: 600, color: '#6b5d3f', cursor: 'pointer', textTransform: 'lowercase',
                                    }}
                                  >
                                    + add
                                  </button>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => { setShowCustomAppPrompt(false); void connectAppInFlow(slug, { origin: 'modal' }); }}
                                  style={{
                                    background: 'var(--brand-gold)', color: '#fff', border: 'none', borderRadius: '999px',
                                    padding: '0.3rem 0.85rem', fontFamily: 'inherit', fontSize: '0.76rem',
                                    fontWeight: 600, cursor: 'pointer', textTransform: 'lowercase', flexShrink: 0,
                                  }}
                                >
                                  {connectCtaLabel(slug)}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {customAppMatches.length === 0 ? (
                        <button
                          type="submit"
                          disabled={!customAppName.trim()}
                          data-testid="custom-app-proceed"
                          style={{
                            background: 'var(--brand-gold)', color: '#fff', border: 'none', borderRadius: '999px',
                            padding: '0.55rem 1.2rem', fontFamily: 'inherit', fontSize: '0.85rem',
                            fontWeight: 700, cursor: 'pointer', textTransform: 'lowercase',
                            opacity: customAppName.trim() ? 1 : 0.55,
                          }}
                        >
                          proceed →
                        </button>
                      ) : (
                        <button
                          type="button"
                          data-testid="custom-app-browser-fallback"
                          onClick={() => {
                            const name = customAppName.trim();
                            if (!name) return;
                            setShowCustomAppPrompt(false);
                            void import('../services/composio/connectCustomFlow').then(
                              ({ requestConnectConcierge, beginCustomConnectFlow }) => {
                                if (!requestConnectConcierge(name)) {
                                  const orgVm = signInWorkspaceTarget;
                                  void beginCustomConnectFlow({ appName: name, ...(orgVm ? { workspaceAccountId: orgVm } : {}) });
                                }
                              },
                            );
                          }}
                          style={{
                            background: 'none', border: '1px dashed var(--brand-gold)', borderRadius: '999px',
                            padding: '0.45rem 1rem', fontFamily: 'inherit', fontSize: '0.8rem',
                            fontWeight: 600, color: '#6b5d3f', cursor: 'pointer', textTransform: 'lowercase',
                          }}
                        >
                          none of these — sign in via Prest0n's browser →
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowCustomAppPrompt(false)}
                        style={{
                          background: 'none', border: 'none', color: '#857F73', cursor: 'pointer',
                          fontFamily: 'inherit', fontSize: '0.8rem', textDecoration: 'underline', textTransform: 'lowercase',
                        }}
                      >
                        never mind
                      </button>
                    </div>
                  </form>
                </Modal>
              </Card>
            )}

            {trialPhase === 'team' && (
              <Card>
                {/* In-flow back navigation (task #56) — never out of onboarding. */}
                <button
                  type="button"
                  onClick={() => goToPhase(wantsApps !== 'later' && visitedPhases.includes('apps') ? 'apps' : 'profile')}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    fontSize: '0.78rem', fontWeight: 600, color: '#857F73', textTransform: 'lowercase',
                    padding: 0, marginBottom: '0.6rem', display: 'block',
                  }}
                >
                  {wantsApps !== 'later' && visitedPhases.includes('apps') ? '← back to connect your apps' : '← back to profile setup'}
                </button>
                <CardTitle>try Prest0n with your team</CardTitle>
                <div style={{ fontSize: '0.88rem', color: '#666', lineHeight: 1.6, margin: '-0.25rem 0 0.9rem' }}>
                  optional, but recommended — invite up to 5 teammates now for a shared workspace with roles and per-person limits. more anytime from team &amp; roles. skip and it's just you.
                </div>
                <div style={{
                  fontSize: '0.78rem', color: '#857F73', lineHeight: 1.55,
                  border: '1px solid #ECEAE5', borderRadius: '0.75rem',
                  padding: '0.6rem 0.8rem', marginBottom: '1.1rem', background: '#FBFAF7',
                }}>
                  {newOrgMode ? (
                    <>
                      {/* Created-org bonus truth (review finding 19): the credit
                          window for a created org opens 72h AFTER it hires —
                          invites sent during setup are queued and delivered
                          when the org exists, but they don't earn setup
                          credit. The trial explainer below would promise
                          money this flow never pays. */}
                      these invites go out the moment the organization is created.
                      invite credit for a new organization works differently: it applies
                      to invites sent within 72h after the organization hires its
                      Prest0n — setup invites don&rsquo;t earn credit here.
                    </>
                  ) : (
                    <>
                      setup bonus: each teammate who activates within 72h adds wallet credit —{' '}
                      {/* Wrap pass (2026-08-09): the formula breaks only at the
                          '·' separators, never inside a "$300 × first 2" term.
                          Task #55: the page caps at 5 invites, so only the first
                          two credit tiers are reachable HERE — quoting the $1,600
                          9-invite ceiling would overpromise. */}
                      <span style={{ whiteSpace: 'nowrap' }}>$300 × first 2</span> ·{' '}
                      <span style={{ whiteSpace: 'nowrap' }}>$200 × next 3</span>{' '}
                      <span style={{ whiteSpace: 'nowrap' }}>(up to $1,200)</span>.
                      invites sent after setup don't earn credit.
                      {/* Task #69 (2026-08-15): the expectation is set HERE, in
                          full — credit exists only once the teammate accepts and
                          first enters the workspace, and teammates share this
                          workspace's credit rather than minting their own trial. */}
                      {' '}credit lands the moment a teammate accepts and steps into the
                      workspace — not before. teammates work from this workspace&rsquo;s shared
                      credit; they never get a separate trial of their own.
                    </>
                  )}
                </div>
                {/* Two-line rows (2026-08-19; replaces the 225px role cell
                    that still clipped "member (recommended) · 23 perms" —
                    perm counts moved to the expandable detail panel below,
                    where they can actually be READ). Line 1: who; line 2:
                    their role, full width, never truncated. */}
                <div style={{ display: 'grid', gap: '0.55rem', marginBottom: '1rem' }}>
                  {/* Name column widened 130px → equal share (2026-08-20
                      owner report: "name (optional)" clipped and longer
                      names didn't fit). Email keeps a slight edge. */}
                  {inviteRows.map((row, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) 92px', gap: '0.5rem', alignItems: 'center', border: '1px solid #F0EDE7', borderRadius: '0.75rem', padding: '0.55rem 0.6rem' }}>
                      <Input
                        type="email"
                        placeholder={`teammate${i + 1}@company.com`}
                        value={row.email}
                        disabled={row.status === 'sent' || row.status === 'link' || inviteSending}
                        onChange={(e) => updateInviteRow(i, { email: e.target.value, status: 'idle', error: undefined })}
                      />
                      <Input
                        type="text"
                        placeholder="name (optional)"
                        value={row.name}
                        disabled={row.status === 'sent' || row.status === 'link' || inviteSending}
                        onChange={(e) => updateInviteRow(i, { name: e.target.value })}
                      />
                      <span style={{
                        fontSize: '0.72rem', fontWeight: 600, textAlign: 'center', textTransform: 'lowercase',
                        color: row.status === 'sent' ? '#1B7A3D' : row.status === 'link' ? '#8a6d1f' : row.status === 'error' ? '#B3261E' : '#9A948A',
                      }}>
                        {row.status === 'sent' ? '✓ sent' : row.status === 'link' ? 'link ready' : row.status === 'sending' ? 'sending…' : row.status === 'error' ? 'failed' : `slot ${i + 1} of 5`}
                      </span>
                      {/* Role line — full width, never shares a squeezed cell
                          (2026-08-19). "+ create a custom role…" mints one in
                          place via the SAME editor team & roles uses and
                          assigns it to this row. */}
                      {inviteRoleOptions.length > 0 && (<>
                        <span style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0 }}>
                          <span style={{ fontSize: '0.74rem', fontWeight: 600, color: '#857F73', textTransform: 'lowercase', flexShrink: 0 }}>role</span>
                          <select
                            value={row.roleId ?? defaultInviteRoleId ?? ''}
                            disabled={row.status === 'sent' || row.status === 'link' || inviteSending}
                            onChange={(e) => {
                              if (e.target.value === '__create_custom_role__') {
                                pendingCustomRoleRowRef.current = i;
                                setRoleEditorTarget('new');
                                return; // the select keeps its previous value
                              }
                              updateInviteRow(i, { roleId: e.target.value });
                            }}
                            aria-label={`role for teammate ${i + 1}`}
                            title="the role decides what this teammate can see and do — expand 'see what each role can do' below for the full permission list"
                            style={{
                              fontFamily: 'inherit', fontSize: '0.8rem', color: '#333',
                              border: '1px solid #DDD8CF', borderRadius: '0.6rem',
                              padding: '0.45rem 0.5rem', background: '#fff', minWidth: 0, flex: 1,
                              textTransform: 'lowercase', cursor: 'pointer',
                            }}
                          >
                            {inviteRoleOptions.map((opt) => (
                              <option key={opt.roleId} value={opt.roleId}>
                                {opt.name}
                                {opt.roleId === defaultInviteRoleId ? ' (recommended)' : ''}
                              </option>
                            ))}
                            <option value="__create_custom_role__">+ create a custom role…</option>
                          </select>
                          {/* Edit THIS role's permissions in place (task #55)
                              — the members & roles editor, zero navigation. */}
                          <button
                            type="button"
                            onClick={() => {
                              const targetId = row.roleId ?? defaultInviteRoleId;
                              const target = inviteRolesRaw.find((r) => (r.roleId ?? r.id) === targetId);
                              setRoleEditorTarget(target ?? 'new');
                            }}
                            disabled={inviteSending}
                            title="customize this role's permissions — same editor as team & roles"
                            aria-label={`customize role for teammate ${i + 1}`}
                            style={{
                              background: 'none', border: '1px solid #DDD8CF', borderRadius: '0.5rem',
                              padding: '0.3rem 0.45rem', fontFamily: 'inherit', fontSize: '0.78rem',
                              color: '#857F73', cursor: 'pointer', flexShrink: 0,
                            }}
                          >
                            ✎
                          </button>
                        </span>
                        {/* What the chosen role MEANS, right where it was
                            chosen (2026-08-20 owner directive): the same
                            description Team & Roles shows plus the honest
                            implication-expanded permission count. Custom
                            roles without a description get the count alone —
                            never a dangling separator. */}
                        {(() => {
                          const chosen = inviteRoleOptions.find(
                            (o) => o.roleId === (row.roleId ?? defaultInviteRoleId));
                          if (!chosen) return null;
                          const countLabel = `${chosen.permCount} permission${chosen.permCount === 1 ? '' : 's'}`;
                          return (
                            <div style={{
                              gridColumn: '1 / -1', fontSize: '0.74rem', color: '#857F73',
                              lineHeight: 1.5, margin: '-0.25rem 0 0.05rem', textTransform: 'lowercase',
                            }}>
                              {chosen.description ? `${chosen.description} · ${countLabel}` : countLabel}
                            </div>
                          );
                        })()}
                      </>)}
                      {/* Wrap pass (2026-08-09): error strings run to 80
                          chars — they get their own full-width row below
                          instead of shredding inside the 92px status cell. */}
                      {row.status === 'error' && row.error && (
                        <div style={{
                          gridColumn: '1 / -1', fontSize: '0.74rem', color: '#B3261E',
                          lineHeight: 1.45, overflowWrap: 'anywhere', wordBreak: 'break-word',
                          margin: '-0.15rem 0 0.1rem',
                        }}>
                          {row.error}
                        </div>
                      )}
                      {/* Copy-link row (task #42/#44): always available. For
                          'link' rows it's the delivery path (email couldn't
                          be sent); for 'sent' rows it's a convenience — the
                          owner can still hand the link over directly. */}
                      {(row.status === 'link' || row.status === 'sent') && row.acceptUrl && (
                        <div style={{
                          gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '0.6rem',
                          flexWrap: 'wrap', fontSize: '0.74rem',
                          color: row.status === 'link' ? '#8a6d1f' : '#857F73',
                          lineHeight: 1.45, margin: '-0.15rem 0 0.1rem',
                        }}>
                          <span>
                            {row.status === 'link'
                              ? 'invite created — email delivery is unavailable, so copy their invite link and send it to them directly:'
                              : 'invite emailed — you can also copy their invite link and send it yourself:'}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              try { navigator.clipboard.writeText(row.acceptUrl!); } catch { /* noop */ }
                              setCopiedInviteIdx(i);
                              setTimeout(() => setCopiedInviteIdx((c) => (c === i ? null : c)), 2500);
                            }}
                            style={{
                              border: '1px solid var(--brand-gold)', background: copiedInviteIdx === i ? 'rgba(198,167,94,0.12)' : '#fff',
                              color: '#7a5f1f', borderRadius: 999, padding: '0.2rem 0.7rem',
                              fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', textTransform: 'lowercase',
                            }}
                          >
                            {copiedInviteIdx === i ? 'copied ✓' : 'copy invite link'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {/* "see what each role can do" (2026-08-19 owner directive):
                    the full permission detail — grouped exactly like team &
                    roles via the SHARED RoleDetailPanel — one optional tap
                    away, never forced on anyone. */}
                {inviteRoleOptions.length > 0 && (
                  <div style={{ marginBottom: '1.1rem' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setRoleDetailOpen((open) => !open);
                        if (!roleDetailRoleId && defaultInviteRoleId) setRoleDetailRoleId(defaultInviteRoleId);
                      }}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                        fontSize: '0.78rem', fontWeight: 600, color: '#6b5d3f',
                        textTransform: 'lowercase', padding: 0,
                      }}
                    >
                      {roleDetailOpen ? '▾' : '▸'} see what each role can do
                    </button>
                    {roleDetailOpen && (
                      <div style={{ marginTop: '0.5rem', display: 'grid', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {inviteRoleOptions.map((opt) => (
                            <button
                              key={opt.roleId}
                              type="button"
                              onClick={() => setRoleDetailRoleId(opt.roleId)}
                              style={{
                                fontFamily: 'inherit', fontSize: '0.76rem', fontWeight: 600,
                                textTransform: 'lowercase', cursor: 'pointer', borderRadius: '999px',
                                padding: '0.25rem 0.7rem',
                                border: roleDetailRoleId === opt.roleId ? '1px solid var(--brand-gold)' : '1px solid #ECEAE5',
                                background: roleDetailRoleId === opt.roleId ? 'rgba(198, 167, 94, 0.14)' : '#fff',
                                color: 'var(--brand-text)',
                              }}
                            >
                              {opt.name} · {opt.permCount} permission{opt.permCount === 1 ? '' : 's'}
                            </button>
                          ))}
                        </div>
                        {(() => {
                          const detail = inviteRolesRaw.find((r) => (r.roleId ?? r.id) === (roleDetailRoleId ?? defaultInviteRoleId));
                          return detail ? <RoleDetailPanel role={detail} /> : null;
                        })()}
                      </div>
                    )}
                  </div>
                )}
                {/* Five slots max on this page (task #55 owner directive) —
                    three shown by default; the full seat capacity stays
                    reachable from team & roles after setup. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap', marginBottom: '1.1rem' }}>
                  {inviteRows.length < 5 && (
                    <button
                      type="button"
                      onClick={() => setInviteRows(rows => [...rows, { email: '', name: '', roleId: defaultInviteRoleId, status: 'idle' }])}
                      disabled={inviteSending}
                      style={{
                        background: 'none', border: '1px dashed #DDD8CF', borderRadius: '0.75rem',
                        padding: '0.5rem 0.9rem', fontFamily: 'inherit', fontSize: '0.8rem',
                        color: '#857F73', cursor: 'pointer', textTransform: 'lowercase',
                      }}
                    >
                      + add another ({inviteRows.length} of 5)
                    </button>
                  )}
                  {/* Customize roles WITHOUT leaving the flow (task #55): the
                      exact same editor the members & roles section uses,
                      mounted right here as a modal — never a navigation. */}
                  {inviteRoleOptions.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setRoleEditorTarget('new')}
                      disabled={inviteSending}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                        fontSize: '0.78rem', fontWeight: 600, color: '#857F73',
                        textDecoration: 'underline', textTransform: 'lowercase', padding: 0,
                      }}
                    >
                      customize roles &amp; permissions
                    </button>
                  )}
                </div>
                {roleEditorTarget !== null && (
                  <React.Suspense fallback={null}>
                    <RoleEditorModalLazy
                      role={roleEditorTarget === 'new' ? null : roleEditorTarget}
                      allRoles={inviteRolesRaw}
                      onClose={() => { pendingCustomRoleRowRef.current = null; setRoleEditorTarget(null); }}
                      onSaved={(newRoleId?: string) => {
                        setRoleEditorTarget(null);
                        reloadInviteRoles();
                        // "+ create a custom role…" (2026-08-19): the fresh
                        // role lands straight on the row that asked for it.
                        const rowIdx = pendingCustomRoleRowRef.current;
                        pendingCustomRoleRowRef.current = null;
                        if (typeof rowIdx === 'number' && newRoleId) {
                          updateInviteRow(rowIdx, { roleId: newRoleId });
                        }
                      }}
                    />
                  </React.Suspense>
                )}
                <ButtonRow>
                  <PrimaryButton onClick={sendTeamInvites} disabled={inviteSending}>
                    {inviteSending ? 'sending…' : anyInviteSent && allInvitesSent ? 'sent — continue →' : 'send invites →'}
                  </PrimaryButton>
                </ButtonRow>
                {anyInviteSent && allInvitesSent && !inviteSending && (
                  <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
                    <button
                      type="button"
                      onClick={() => { void refreshInitialConditions({ invitedCount: inviteRows.filter(r => r.status === 'sent').length }); void launchWorkspace(); }}
                      disabled={launching}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                        fontSize: '0.85rem', fontWeight: 600, color: 'var(--brand-text)', textDecoration: 'underline', textTransform: 'lowercase',
                      }}
                    >
                      {launching ? 'opening your workspace…' : 'open my workspace →'}
                    </button>
                  </div>
                )}
                <div style={{ textAlign: 'center', marginTop: '0.6rem' }}>
                  <button
                    type="button"
                    onClick={() => { void refreshInitialConditions({ invitedCount: inviteRows.filter(r => r.status === 'sent').length }); void launchWorkspace(); }}
                    disabled={inviteSending || launching}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: '0.78rem', color: '#9A948A', textDecoration: 'underline', textTransform: 'lowercase',
                    }}
                  >
                    skip — add teammates anytime from team & roles
                  </button>
                </div>
                {/* The team card hosts the final launch CTAs — one quiet line
                    of workstation truth right where the user leaves. */}
                <WorkstationReadinessHint
                  active={(!draftMode && trialPhase === 'team') || (draftMode && Boolean(mintedOrgAccountId))}
                  account={draftMode ? mintedOrgAccountId : undefined}
                />
                {/* The team stage hosts the completion buttons — a mint or
                    launch failure must be VISIBLE here, not only on stages
                    the user already left (review finding 18). */}
                {error && <ErrorText>{error}</ErrorText>}
              </Card>
            )}

          </TrialGrid>
        </Wrap>
      </Page>
    );
  }

  if (!hasActiveBilling) {
    return (
      <Page>
        <TopBar>
          <BrandWordmark href="/" />
          <TopBarSignOut type="button" onClick={handleSignOut}>sign out</TopBarSignOut>
        </TopBar>
        <Wrap>
          <Hero>
            <Eyebrow>Prest0 onboarding</Eyebrow>
            <Title>{currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} plan setup</Title>
            <Subtitle>
              {stateDescription('billing_pending', currentTier)}
            </Subtitle>
            <StatusPill>
              <span>0% complete</span>
              <span>•</span>
              <span>{subscription?.status || 'subscription pending'}</span>
            </StatusPill>
          </Hero>

          <Card>
            <CardTitle>welcome to prest0</CardTitle>
            {subscription == null ? (
              <>
                {/* Organic signup whose trial provisioning failed or hasn't
                    landed yet (review round 2026-08-18): a brand-new signup
                    was promised onboarding — offer the trial start directly,
                    never only an enterprise-license wall. */}
                <SectionText>
                  {organicRetryNote
                    || 'one more step — start your free trial and the guided setup opens right up. have an access code instead? redeem it on the start page.'}
                </SectionText>
                <ButtonRow>
                  <PrimaryButton onClick={() => { void retryOrganicTrial(); }} disabled={organicRetrying}>
                    {organicRetrying ? 'starting…' : 'start my free trial'}
                  </PrimaryButton>
                  <SecondaryButton onClick={() => { window.location.href = `${window.location.origin}/start`; }}>
                    redeem an access code
                  </SecondaryButton>
                  <SecondaryButton onClick={bootstrap} disabled={loading}>
                    refresh status
                  </SecondaryButton>
                </ButtonRow>
              </>
            ) : (
              <>
                <SectionText>
                  Your agent's workspace is ready. To unlock the guided setup and begin configuring your AI employee, please activate your enterprise license or redeem a trial access code.
                </SectionText>
                <ButtonRow>
                  <PrimaryButton onClick={() => { window.location.href = `${window.location.origin}/pricing`; }}>
                    View licensing options
                  </PrimaryButton>
                  <SecondaryButton onClick={bootstrap} disabled={loading}>
                    Refresh status
                  </SecondaryButton>
                </ButtonRow>
              </>
            )}
          </Card>
        </Wrap>
      </Page>
    );
  }

  // ── Legacy STEP_ORDER funnel RETIRED (2026-08-19, uniformity directive) ──
  // Active non-trial plans used to land in a third, divergent 4-step page
  // ("Connect your tools / Team profile / Upload documents / Verify").
  // Every journey now runs the ONE staged flow above; an already-activated
  // account that reaches /onboarding just gets its workspace back.
  return (
    <Page>
      <SeoHead
        title="Get Started | prest0"
        description="Create your prest0 account and start using enterprise legal AI tools today."
        noIndex={true}
      />
      <TopBar>
        <BrandWordmark href="/" />
        <TopBarSignOut type="button" onClick={handleSignOut}>sign out</TopBarSignOut>
      </TopBar>
      <Wrap>
        <Card>
          <CardTitle>your workspace is ready</CardTitle>
          <SectionText>
            setup is complete for this workspace — everything (apps, team, roles,
            documents) is managed from inside it now.
          </SectionText>
          <ButtonRow>
            <PrimaryButton onClick={() => { window.location.href = `${window.location.origin}/desktop`; }}>
              open my workspace →
            </PrimaryButton>
            <SecondaryButton onClick={() => { window.location.href = `${window.location.origin}/integrations`; }}>
              manage integrations
            </SecondaryButton>
          </ButtonRow>
        </Card>
      </Wrap>
    </Page>
  );
};

export default Onboarding;
